// src/EnhancedSearch.jsx
// Kubi AI-Powered Unified Search - v6
// Fixed: Light/dark mode, Rollups/Positions, Bulk actions, Rich profile
import React, { useState, useEffect, useContext, createContext, useRef, useMemo } from 'react';
import { useBilling } from './App';
import {
  Search, MapPin, Mail, Phone, Linkedin, FileText, Download, ExternalLink, Plus, Send, X,
  Clock, Building, Eye, Loader2, CheckCircle, AlertCircle, TrendingUp,
  Sparkles, Wand2, Lightbulb, Brain, Database, Globe, Star, Coins, RefreshCw,
  Paperclip, File, Image, ArrowLeft, Target, Zap, Users, SortAsc, SortDesc,
  Briefcase, Award, CheckSquare, Square, DollarSign, Calendar, Tag, Layers,
  BookOpen, Shield, ChevronDown, ChevronUp, Copy, MoreHorizontal, Upload, ThumbsDown, Lock,
  Settings, Link2, Navigation, ShieldCheck, ShieldAlert, Smartphone
} from 'lucide-react';

// =============================================================================
// STYLES
// =============================================================================
const styles = `
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
@keyframes slideInUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes scanLine { 0% { width: 0%; opacity: 1; } 80% { width: 100%; opacity: 1; } 100% { width: 100%; opacity: 0; } }
@keyframes revealBadge { 0% { transform: scale(0.6) translateY(4px); opacity: 0; } 60% { transform: scale(1.08); opacity: 1; } 100% { transform: scale(1) translateY(0); opacity: 1; } }
@keyframes pulse2 { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
.spin { animation: spin 1s linear infinite; }
.slide-in-right { animation: slideInRight 0.3s ease-out; }
.slide-in-up { animation: slideInUp 0.3s ease-out; }
.fade-in { animation: fadeIn 0.2s ease-out; }
.scan-line { animation: scanLine 0.9s ease-in-out forwards; }
.reveal-badge { animation: revealBadge 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards; }
.val-pulse { animation: pulse2 1.2s ease-in-out infinite; }
`;
if (typeof document !== 'undefined' && !document.getElementById('kubi-search-styles-v6')) {
  const el = document.createElement('style'); 
  el.id = 'kubi-search-styles-v6'; 
  el.textContent = styles; 
  document.head.appendChild(el);
}

// =============================================================================
// CONFIG & HELPERS
// =============================================================================
const API_URL = process.env.REACT_APP_API_URL || 'https://kubi-backend-production.up.railway.app';
const buildApiUrl = (path) => {
  const base = API_URL.replace(/\/+$/, '');
  return base.endsWith('/api') && path.startsWith('/api/') ? base + path.substring(4) : base + path;
};

// Theme detection - read from localStorage since we may not have access to App's ThemeContext
const useTheme = () => {
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('kubi_darkMode');
    return stored !== 'false'; // Default to dark if not explicitly set to 'false'
  });

  // Listen for storage changes (when theme is toggled in settings)
  useEffect(() => {
    const handleStorageChange = () => {
      setDarkMode(localStorage.getItem('kubi_darkMode') !== 'false');
    };
    window.addEventListener('storage', handleStorageChange);
    // Also check periodically for same-tab changes
    const interval = setInterval(handleStorageChange, 500);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  return { darkMode };
};

// Title case helper for PDL data (often comes lowercase)
const titleCase = (str) => {
  if (!str || typeof str !== 'string') return str;
  // Skip if already mostly capitalized
  if (str.charAt(0) === str.charAt(0).toUpperCase() && !str.match(/^[a-z]/)) return str;
  const lowercase = ['a','an','the','and','but','or','for','nor','on','at','to','by','in','of','with','vs'];
  return str.toLowerCase().split(' ').map((word, i) => {
    if (i === 0 || !lowercase.includes(word)) return word.charAt(0).toUpperCase() + word.slice(1);
    return word;
  }).join(' ');
};

// Format large numbers: 53000 → "53k", 2100 → "2.1k", 500 → "500"
const formatCount = (n) => {
  if (!n || n < 1000) return String(n || 0);
  if (n >= 1000000) return (n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1).replace(/\.0$/, '') + 'M';
  return (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1).replace(/\.0$/, '') + 'k';
};

// Get auth token - check multiple possible storage keys
const getToken = () => {
  // Try common token storage keys
  const token = localStorage.getItem('token')
    || localStorage.getItem('authToken')
    || localStorage.getItem('jwt')
    || localStorage.getItem('kubi_token')
    || sessionStorage.getItem('token');

  if (!token) {
    console.warn('[Auth] No token found in storage. Checked: token, authToken, jwt, kubi_token');
  }
  return token;
};

// PDL-based suggestions for filter categories (based on PDL taxonomy)
const PDL_SUGGESTIONS = {
  industry: ['software', 'saas', 'technology', 'information technology', 'financial services', 'healthcare', 'manufacturing', 'consulting', 'telecommunications', 'real estate', 'retail', 'education', 'media', 'insurance', 'energy', 'construction', 'automotive', 'hospitality', 'logistics', 'pharmaceuticals', 'aerospace', 'legal', 'government', 'nonprofit', 'agriculture', 'mining & metals'],
  skills: ['javascript', 'python', 'react', 'node.js', 'sql', 'aws', 'java', 'typescript', 'docker', 'kubernetes', 'machine learning', 'data analysis', 'project management', 'agile', 'salesforce', 'excel', 'crm', 'marketing', 'seo', 'graphic design', 'ui/ux', 'git', 'linux', 'c++', 'go', 'ruby', 'php', 'rest api', 'ci/cd', 'terraform'],
  skills_optional: ['javascript', 'python', 'react', 'node.js', 'sql', 'aws', 'java', 'typescript', 'docker', 'kubernetes', 'machine learning', 'data analysis', 'project management', 'agile', 'salesforce', 'excel', 'crm', 'lead generation', 'cold calling', 'negotiation', 'business development', 'account management'],
  seniority_level: ['entry', 'junior', 'senior', 'lead', 'manager', 'director', 'vp', 'c-level', 'executive', 'partner', 'owner'],
  management_level: ['entry', 'junior', 'senior', 'lead', 'manager', 'director', 'vp', 'c-level', 'executive', 'partner', 'owner'],
  job_title: ['software engineer', 'sales manager', 'account executive', 'product manager', 'data analyst', 'marketing manager', 'project manager', 'business analyst', 'operations manager', 'financial analyst', 'recruiter', 'hr manager', 'customer success manager', 'solutions architect', 'devops engineer', 'full stack developer', 'sales director', 'vp of sales', 'cto', 'cfo'],
  location: ['california', 'texas', 'new york', 'florida', 'illinois', 'ohio', 'georgia', 'north carolina', 'michigan', 'pennsylvania', 'colorado', 'washington', 'arizona', 'massachusetts', 'virginia', 'tennessee', 'minnesota', 'indiana', 'oregon', 'remote'],
  certifications: ['pmp', 'aws certified', 'cpa', 'cissp', 'scrum master', 'six sigma', 'cfa', 'google analytics', 'salesforce certified', 'comptia a+', 'itil', 'prince2', 'shrm', 'phr', 'ccna'],
  education: ['bachelors', 'masters', 'mba', 'phd', 'associates', 'jd', 'md'],
  languages: ['spanish', 'french', 'mandarin', 'german', 'portuguese', 'japanese', 'korean', 'arabic', 'hindi', 'italian', 'russian']
};

// Generate CSV download from enriched kNine data
const downloadEnrichedCSV = (candidate) => {
  const data = candidate.enrichedData || candidate;
  const fields = {
    'Full Name': data.fullName || '',
    'Job Title': data.title || data.currentOccupation || '',
    'Company': data.company || data.currentEmployer || '',
    'Email': data.email || '',
    'Work Email': data.workEmail || '',
    'Phone': data.phone || data.mobilePhone || '',
    'Work Phone': data.workPhone || '',
    'LinkedIn': data.linkedinUrl || '',
    'Location': data.location || [data.city, data.state, data.country].filter(Boolean).join(', ') || '',
    'Industry': data.industry || '',
    'Seniority': Array.isArray(data.titleLevels) ? data.titleLevels.join(', ') : (data.seniorityLevel || ''),
    'Skills': Array.isArray(data.skills) ? data.skills.join('; ') : '',
    'Languages': Array.isArray(data.languages) ? data.languages.map(l => typeof l === 'string' ? l : l.name).join('; ') : '',
    'Education': Array.isArray(data.education) ? data.education.map(e => [e.degree, e.major, e.school].filter(Boolean).join(' - ')).join('; ') : '',
    'Certifications': Array.isArray(data.certifications) ? data.certifications.map(c => typeof c === 'string' ? c : c.name).join('; ') : '',
    'Experience': Array.isArray(data.experience) ? data.experience.map(e => `${e.title || ''} at ${e.company || ''} (${e.startDate || ''}${e.endDate ? ' - ' + e.endDate : ' - Present'})`).join('; ') : '',
    'Summary': data.summary || data.headline || '',
    'GitHub': data.githubUrl || '',
    'Website': data.website || ''
  };
  const headers = Object.keys(fields);
  const values = Object.values(fields).map(v => `"${String(v).replace(/"/g, '""')}"`);
  const csv = headers.join(',') + '\n' + values.join(',');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${(data.fullName || 'Candidate').replace(/\s+/g, '_')}_kNine.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// =============================================================================
// THEME COLORS - Clean light/dark mode
// =============================================================================
const useColors = () => {
  const { darkMode } = useTheme();
  
  if (darkMode) {
    return {
      // Dark mode
      bg: '#0f172a',
      bgCard: '#1e293b',
      bgHover: '#334155',
      bgInput: '#1e293b',
      bgMuted: '#334155',
      bgAccent: 'rgba(30, 136, 229, 0.15)',
      bgSuccess: 'rgba(16, 185, 129, 0.15)',
      bgWarning: 'rgba(245, 158, 11, 0.15)',
      bgDanger: 'rgba(239, 68, 68, 0.15)',
      
      text: '#f8fafc',
      textSecondary: '#cbd5e1',
      textMuted: '#94a3b8',
      
      border: '#334155',
      borderLight: '#475569',
      
      shadow: '0 4px 12px rgba(0,0,0,0.3)',
      shadowLg: '0 8px 24px rgba(0,0,0,0.4)',
      
      primary: '#3b82f6',
      accent: '#1E88E5',
      success: '#10b981',
      warning: '#f59e0b',
      danger: '#ef4444',
      
      darkMode: true
    };
  } else {
    return {
      // Light mode - clean whites and grays
      bg: '#f8fafc',
      bgCard: '#ffffff',
      bgHover: '#f1f5f9',
      bgInput: '#ffffff',
      bgMuted: '#f1f5f9',
      bgAccent: 'rgba(30, 136, 229, 0.08)',
      bgSuccess: 'rgba(16, 185, 129, 0.08)',
      bgWarning: 'rgba(245, 158, 11, 0.08)',
      bgDanger: 'rgba(239, 68, 68, 0.08)',
      
      text: '#1e293b',
      textSecondary: '#475569',
      textMuted: '#64748b',
      
      border: '#e2e8f0',
      borderLight: '#cbd5e1',
      
      shadow: '0 1px 3px rgba(0,0,0,0.08)',
      shadowLg: '0 4px 12px rgba(0,0,0,0.1)',
      
      primary: '#2563eb',
      accent: '#1565C0',
      success: '#059669',
      warning: '#d97706',
      danger: '#dc2626',
      
      darkMode: false
    };
  }
};

// =============================================================================
// UI COMPONENTS
// =============================================================================
const Card = ({ children, style = {}, className = '' }) => {
  const colors = useColors();
  return (
    <div className={className} style={{
      background: colors.bgCard,
      borderRadius: 12, 
      border: `1px solid ${colors.border}`,
      boxShadow: colors.shadow,
      ...style
    }}>{children}</div>
  );
};

const Button = ({ children, variant = 'primary', size = 'md', icon: Icon, loading, disabled, onClick, style = {} }) => {
  const colors = useColors();
  
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return { background: colors.primary, color: '#fff', border: 'none' };
      case 'secondary':
        return { background: colors.bgMuted, color: colors.text, border: `1px solid ${colors.border}` };
      case 'ghost':
        return { background: 'transparent', color: colors.textSecondary, border: 'none' };
      case 'ai':
        return { background: `linear-gradient(135deg, ${colors.accent}, #1565C0)`, color: '#fff', border: 'none' };
      case 'success':
        return { background: colors.success, color: '#fff', border: 'none' };
      case 'danger':
        return { background: colors.danger, color: '#fff', border: 'none' };
      default:
        return { background: colors.primary, color: '#fff', border: 'none' };
    }
  };
  
  const getSizeStyles = () => {
    switch (size) {
      case 'sm': return { padding: '6px 12px', fontSize: 12 };
      case 'lg': return { padding: '12px 24px', fontSize: 15 };
      default: return { padding: '10px 18px', fontSize: 13 };
    }
  };
  
  return (
    <button 
      onClick={onClick} 
      disabled={disabled || loading} 
      style={{ 
        ...getVariantStyles(),
        ...getSizeStyles(),
        borderRadius: 8, 
        fontWeight: 600, 
        cursor: disabled || loading ? 'not-allowed' : 'pointer', 
        opacity: disabled || loading ? 0.6 : 1, 
        display: 'inline-flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: 6, 
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
        ...style 
      }}
    >
      {loading ? <Loader2 size={14} className="spin" /> : Icon && <Icon size={14} />}
      {children}
    </button>
  );
};

const Badge = ({ children, variant = 'default', size = 'md', onClick, style = {} }) => {
  const colors = useColors();
  
  const getStyles = () => {
    const base = { 
      display: 'inline-flex', alignItems: 'center', gap: 4,
      borderRadius: 16, fontWeight: 600, 
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s',
      padding: size === 'sm' ? '3px 8px' : '4px 10px',
      fontSize: size === 'sm' ? 10 : 11
    };
    
    switch (variant) {
      case 'ats': return { ...base, background: 'rgba(37, 99, 235, 0.1)', color: colors.primary };
      case 'knine': return { ...base, background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(59,130,246,0.08))', color: '#059669', border: '1px solid rgba(16,185,129,0.25)', fontWeight: 700 };
      case 'enriched': return { ...base, background: 'rgba(16,185,129,0.15)', color: '#047857', border: '1px solid rgba(16,185,129,0.3)' };
      case 'ai': return { ...base, background: colors.bgAccent, color: colors.accent };
      case 'excellent': return { ...base, background: colors.bgSuccess, color: colors.success };
      case 'good': return { ...base, background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' };
      case 'fair': return { ...base, background: colors.bgWarning, color: colors.warning };
      case 'suggestion': return { ...base, background: colors.bgWarning, color: colors.warning };
      default: return { ...base, background: colors.bgMuted, color: colors.textSecondary };
    }
  };
  
  return <span onClick={onClick} style={{ ...getStyles(), ...style }}>{children}</span>;
};

const Avatar = ({ name, size = 44 }) => {
  const initials = (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const colorSets = [
    ['#1E88E5', '#a78bfa'], ['#06b6d4', '#22d3ee'], ['#10b981', '#34d399'],
    ['#f59e0b', '#fbbf24'], ['#ef4444', '#f87171'], ['#ec4899', '#f472b6'], ['#3b82f6', '#60a5fa']
  ];
  const idx = name ? name.charCodeAt(0) % colorSets.length : 0;
  return (
    <div style={{ 
      width: size, height: size, borderRadius: '50%', 
      background: `linear-gradient(135deg, ${colorSets[idx][0]}, ${colorSets[idx][1]})`, 
      display: 'flex', alignItems: 'center', justifyContent: 'center', 
      color: '#fff', fontSize: size * 0.36, fontWeight: 700, flexShrink: 0 
    }}>{initials}</div>
  );
};

const EmptyState = ({ icon: Icon, title, description, action }) => {
  const colors = useColors();
  return (
    <div style={{ textAlign: 'center', padding: 48 }}>
      <div style={{ 
        width: 64, height: 64, borderRadius: '50%', 
        background: colors.bgMuted, 
        display: 'flex', alignItems: 'center', justifyContent: 'center', 
        margin: '0 auto 20px' 
      }}>
        <Icon size={28} style={{ color: colors.textMuted }} />
      </div>
      <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: colors.text }}>{title}</h3>
      <p style={{ margin: '0 0 20px', color: colors.textSecondary, fontSize: 14, lineHeight: 1.5 }}>{description}</p>
      {action}
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, link, color, copyable }) => {
  const colors = useColors();
  const [copied, setCopied] = React.useState(false);
  if (!value) return null;

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(typeof value === 'string' ? value : '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const content = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
      <Icon size={14} style={{ color: color || colors.textMuted, flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: colors.textMuted, minWidth: 70 }}>{label}</span>
      <span style={{ fontSize: 13, color: link ? colors.primary : colors.text, flex: 1 }}>{value}</span>
      {copyable && typeof value === 'string' && (
        <button onClick={handleCopy} title={copied ? 'Copied!' : 'Copy'} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0,
          color: copied ? colors.success : colors.textMuted, transition: 'color 0.2s'
        }}>
          {copied ? <CheckCircle size={13} /> : <Copy size={13} />}
        </button>
      )}
    </div>
  );

  if (link) {
    return <a href={link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>{content}</a>;
  }
  return content;
};

// =============================================================================
// SESSION STATE PERSISTENCE KEY
// =============================================================================
const SEARCH_STATE_KEY = 'kubi_search_state';

// =============================================================================
// KNINE MODAL — Replaces native alert/confirm/prompt with branded modals
// =============================================================================
function KNineModal({ modal, onClose }) {
  const colors = useColors();
  const [inputValue, setInputValue] = useState(modal?.defaultValue || '');

  if (!modal) return null;

  const isSuccess = modal.variant === 'success';
  const isError = modal.variant === 'error';
  const isWarning = modal.variant === 'warning';
  const iconColor = isSuccess ? colors.success : isError ? (colors.danger || '#ef4444') : isWarning ? '#f59e0b' : colors.primary;
  const IconComponent = isSuccess ? CheckCircle : isError ? AlertCircle : isWarning ? AlertCircle : CheckCircle;

  return (
    <>
      <div onMouseDown={() => { if (!modal.requireAction) onClose(null); }} className="fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200 }} />
      <div className="slide-in-up" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1201, width: 380, maxWidth: '90vw' }}>
        <Card style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: `linear-gradient(135deg, ${iconColor}, ${colors.primary})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <IconComponent size={18} style={{ color: '#fff' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: colors.text }}>
                {modal.title || 'kNine'}
              </h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
                {modal.message}
              </p>
            </div>
            {!modal.requireAction && (
              <button onClick={() => onClose(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={16} style={{ color: colors.textMuted }} />
              </button>
            )}
          </div>

          {modal.type === 'prompt' && (
            <input
              autoFocus
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder={modal.placeholder || ''}
              onKeyDown={e => { if (e.key === 'Enter' && inputValue.trim()) onClose(inputValue); }}
              style={{
                width: '100%', padding: '10px 12px', marginBottom: 14,
                background: colors.bgInput, color: colors.text,
                border: `1px solid ${colors.border}`, borderRadius: 8,
                fontSize: 13, outline: 'none', boxSizing: 'border-box'
              }}
            />
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {modal.type === 'confirm' && (
              <Button variant="ghost" size="sm" onClick={() => onClose(false)}>Cancel</Button>
            )}
            {modal.type === 'prompt' && (
              <Button variant="ghost" size="sm" onClick={() => onClose(null)}>Cancel</Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (modal.type === 'prompt') onClose(inputValue.trim() || null);
                else if (modal.type === 'confirm') onClose(true);
                else onClose(true);
              }}
            >
              {modal.type === 'confirm' ? (modal.confirmLabel || 'Continue') : modal.type === 'prompt' ? 'Save' : 'OK'}
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function EnhancedSearch() {
  const colors = useColors();
  const { setCredits, plan } = useBilling();
  const isTrial = !plan || plan === 'free' || plan === 'trial' || plan === 'trialing';

  // Restore saved state from sessionStorage (only on initial mount)
  const saved = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(SEARCH_STATE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  // Search state
  const [query, setQuery] = useState(saved?.query || '');
  const [sources, setSources] = useState(saved?.sources || { ats: true, knine: true });
  const [showAtsUpgradeHint, setShowAtsUpgradeHint] = useState(false);
  const [results, setResults] = useState(saved?.results || []);
  const [total, setTotal] = useState(saved?.total || 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(saved?.searched || false);
  const [activeSearch, setActiveSearch] = useState(saved?.activeSearch || null);

  // AI state
  const [aiInterpretation, setAiInterpretation] = useState(saved?.aiInterpretation || null);
  const [discoveryCriteria, setDiscoveryCriteria] = useState(saved?.discoveryCriteria || []);
  const [criteriaModified, setCriteriaModified] = useState(false);
  const [criteriaChipRemoved, setCriteriaChipRemoved] = useState(false);
  const [showDiscovery, setShowDiscovery] = useState(saved?.showDiscovery !== undefined ? saved.showDiscovery : true);
  const [expandedCriteria, setExpandedCriteria] = useState(saved?.expandedCriteria || {});
  const [addingToCategory, setAddingToCategory] = useState(null); // ephemeral UI
  const [addValueInput, setAddValueInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchSteps, setSearchSteps] = useState(saved?.searchSteps || []);
  const [conversationContext, setConversationContext] = useState(saved?.conversationContext || null);
  const [refinementQuery, setRefinementQuery] = useState('');
  const [creditsRemaining, setCreditsRemaining] = useState(saved?.creditsRemaining || null);
  const [knineTotal, setKnineTotal] = useState(saved?.knineTotal || 0);
  const [kninePoolSize, setKninePoolSize] = useState(saved?.kninePoolSize || 0);
  const [knineScrollToken, setKnineScrollToken] = useState(saved?.knineScrollToken || null);
  const [loadingMoreKnine, setLoadingMoreKnine] = useState(false);
  const KNINE_PAGE_SIZE = 50;
  const RESULTS_PAGE_SIZE = 25;
  const [knineDisplayLimit, setKnineDisplayLimit] = useState(saved?.knineDisplayLimit || KNINE_PAGE_SIZE);
  const [resultsDisplayLimit, setResultsDisplayLimit] = useState(saved?.resultsDisplayLimit || RESULTS_PAGE_SIZE);
  const [atsCount, setAtsCount] = useState(saved?.atsCount || 0);
  const [sourceErrors, setSourceErrors] = useState([]);

  // Profile & actions
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showAddToList, setShowAddToList] = useState(false);
  const [showSubmitToPosition, setShowSubmitToPosition] = useState(false);
  const [actionCandidates, setActionCandidates] = useState([]);
  const [rollups, setRollups] = useState([]);
  const [positions, setPositions] = useState([]);
  const [dataLoading, setDataLoading] = useState({ rollups: false, positions: false });
  const [dataError, setDataError] = useState({ rollups: null, positions: null });
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Search history & AI settings
  const [searchHistory, setSearchHistory] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [aiSettings, setAiSettings] = useState(null);
  const [showAiSettings, setShowAiSettings] = useState(false);

  // Job Description Scanner
  const [showJDScanner, setShowJDScanner] = useState(false);
  const [jdText, setJdText] = useState('');
  const [jdParsing, setJdParsing] = useState(false);
  const [jdProfile, setJdProfile] = useState(null);

  // Job Selector Modal
  const [showJobSelector, setShowJobSelector] = useState(false);
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [jobSearchResults, setJobSearchResults] = useState([]);
  const [jobSearchLoading, setJobSearchLoading] = useState(false);
  const [recentPositions, setRecentPositions] = useState([]);

  // View & sorting
  const [sortBy, setSortBy] = useState(saved?.sortBy || 'score');
  const [sortDir, setSortDir] = useState(saved?.sortDir || 'desc');

  // KNine branded modal (replaces native alert/confirm/prompt)
  const [knModal, setKnModal] = useState(null);
  const knModalResolveRef = useRef(null);

  const knAlert = (message, opts = {}) => {
    return new Promise(resolve => {
      knModalResolveRef.current = resolve;
      setKnModal({ type: 'alert', message, title: opts.title || 'kNine', variant: opts.variant || 'info', ...opts });
    });
  };
  const knConfirm = (message, opts = {}) => {
    return new Promise(resolve => {
      knModalResolveRef.current = resolve;
      setKnModal({ type: 'confirm', message, title: opts.title || 'kNine', variant: opts.variant || 'warning', requireAction: true, ...opts });
    });
  };
  const knPrompt = (message, defaultValue = '', opts = {}) => {
    return new Promise(resolve => {
      knModalResolveRef.current = resolve;
      setKnModal({ type: 'prompt', message, defaultValue, title: opts.title || 'kNine', variant: opts.variant || 'info', ...opts });
    });
  };
  const handleKnModalClose = (result) => {
    const resolve = knModalResolveRef.current;
    knModalResolveRef.current = null;
    setKnModal(null);
    if (resolve) resolve(result);
  };

  // FILTER TRACKING - Tracks all applied filters for accurate refinement
  const [allResults, setAllResults] = useState(saved?.allResults || []); // Original unfiltered results
  const defaultFilters = {
    hasResume: null,         // null = no filter, true = must have
    cityMatch: null,         // null = no filter, true = must have a verified city/proximity match
    minExperience: null,     // minimum years
    maxExperience: null,     // maximum years
    locations: [],           // include these states/cities
    excludeLocations: [],    // exclude these
    minMatchScore: null,     // minimum match %
    hasEmail: null,
    noEmail: null,           // true = show only results without email
    hasPhone: null,
    hasLinkedin: null
  };
  const [appliedFilters, setAppliedFilters] = useState(saved?.appliedFilters || defaultFilters);
  
  // Integration connection status — drives the "connect your database" banner
  const [hasIntegration, setHasIntegration] = useState(() => {
    return localStorage.getItem('kubi_has_integration') !== 'false';
  });

  useEffect(() => {
    const checkIntegration = async () => {
      try {
        const res = await fetch(buildApiUrl('/api/integrations/status'), {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (res.ok) {
          const data = await res.json();
          const connected = !!data.hasActiveIntegration;
          setHasIntegration(connected);
          localStorage.setItem('kubi_has_integration', connected ? 'true' : 'false');
        }
      } catch (e) {
        // Keep optimistic state from localStorage on error
      }
    };
    checkIntegration();
  }, []);

  useEffect(() => {
    fetchRollups();
    fetchPositions();
    fetchSearchHistory();
    fetchSavedSearches();
    fetchAiSettings();
  }, []);

  // ---- State persistence ----
  // searchStateRef.current is assigned inline after all useState declarations (line ~1628)
  // so the ref always holds the latest state values without stale closures.
  const searchStateRef = useRef();

  // Save on unmount (covers React Router navigation away)
  useEffect(() => {
    return () => {
      try { sessionStorage.setItem(SEARCH_STATE_KEY, JSON.stringify(searchStateRef.current)); }
      catch (e) { console.warn('[Search] Failed to persist state on unmount:', e.message); }
    };
  }, []);

  // Also save immediately whenever results arrive so navigation timing can't drop them.
  // searchStateRef.current is updated in the render body before this effect fires.
  useEffect(() => {
    if (!searched) return; // Don't save the empty initial state
    try { sessionStorage.setItem(SEARCH_STATE_KEY, JSON.stringify(searchStateRef.current)); }
    catch (e) { console.warn('[Search] Failed to persist state on results change:', e.message); }
  }, [results, searched]); // eslint-disable-line react-hooks/exhaustive-deps
  // ---- End state persistence ----

  // Check for position data from Yard page (Find Candidates)
  useEffect(() => {
    const storedData = localStorage.getItem('kubi_searchFromPosition');
    if (storedData) {
      try {
        const positionData = JSON.parse(storedData);
        // Only use if recent (within 30 seconds)
        if (positionData.timestamp && Date.now() - positionData.timestamp < 30000) {
          console.log('[Search] Loading position from Yard:', positionData);

          // Set the job title as the search query
          if (positionData.title) {
            setQuery(positionData.title);

            // Show AI steps
            setSearchSteps([
              { step: 'analyze', text: `Analyzing position: ${positionData.title}`, done: true },
              { step: 'location', text: `Location: ${[positionData.city, positionData.state].filter(Boolean).join(', ') || 'Any'}`, done: true },
              { step: 'skills', text: 'Inferring relevant skills from job title...', done: true },
              { step: 'search', text: 'Searching for matching candidates...', done: false }
            ]);

            // Store the title to search with, will be picked up after state update
            localStorage.setItem('kubi_pendingSearch', positionData.title);
          }
        }
        // Clear the stored data
        localStorage.removeItem('kubi_searchFromPosition');
      } catch (e) {
        console.error('[Search] Failed to parse position data:', e);
        localStorage.removeItem('kubi_searchFromPosition');
      }
    }
  }, []);

  // Execute pending search after query state is set
  useEffect(() => {
    const pendingSearch = localStorage.getItem('kubi_pendingSearch');
    if (pendingSearch && query === pendingSearch) {
      localStorage.removeItem('kubi_pendingSearch');
      // Trigger search with null event
      setTimeout(() => {
        handleSearch(null, false);
      }, 100);
    }
  }, [query]);

  // ==========================================================================
  // SEARCH HISTORY & AI SETTINGS
  // ==========================================================================

  const fetchSearchHistory = async () => {
    try {
      const res = await fetch(buildApiUrl('/api/search/history?limit=10'), {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSearchHistory(data.history || []);
      }
    } catch (err) { console.warn('Failed to load search history:', err); }
  };

  const fetchSavedSearches = async () => {
    try {
      const res = await fetch(buildApiUrl('/api/search/saved'), {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSavedSearches(data.savedSearches || []);
      }
    } catch (err) { console.warn('Failed to load saved searches:', err); }
  };

  const fetchAiSettings = async () => {
    try {
      const res = await fetch(buildApiUrl('/api/search/settings'), {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAiSettings(data);
      }
    } catch (err) { console.warn('Failed to load AI settings:', err); }
  };

  const saveSearchToHistory = async (searchQuery, filters, resultsCount, aiSummary) => {
    try {
      await fetch(buildApiUrl('/api/search/history'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({
          searchQuery,
          parsedFilters: filters,
          resultsCount,
          aiInterpretation: aiSummary,
          searchSource: sources.ats && sources.knine ? 'unified' : sources.ats ? 'ats' : 'knine'
        })
      });
      fetchSearchHistory(); // Refresh history
    } catch (err) { console.warn('Failed to save search history:', err); }
  };

  const saveCurrentSearch = async () => {
    if (!activeSearch) return;
    const name = await knPrompt('Name this search:', activeSearch, { title: 'Save Search' });
    if (!name) return;
    try {
      await fetch(buildApiUrl('/api/search/saved'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({
          name,
          searchQuery: activeSearch,
          parsedFilters: conversationContext?.filters
        })
      });
      await knAlert('Search saved!', { title: 'Search Saved', variant: 'success' });
      fetchSavedSearches();
    } catch (err) { knAlert('Failed to save search', { title: 'Error', variant: 'error' }); }
  };

  // Parse Job Description with AI
  const parseJobDescription = async () => {
    if (!jdText.trim() || jdText.length < 50) {
      knAlert('Please enter a job description (at least 50 characters)', { title: 'Job Description', variant: 'warning' });
      return;
    }
    setJdParsing(true);
    setJdProfile(null);
    try {
      const res = await fetch(buildApiUrl('/api/search/parse-jd'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ jobDescription: jdText })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to parse job description');
      }
      const data = await res.json();
      setJdProfile(data.profile);
    } catch (err) {
      knAlert('Error parsing job description: ' + err.message, { title: 'Parse Error', variant: 'error' });
    } finally {
      setJdParsing(false);
    }
  };

  // Use the parsed JD profile to start a search
  const searchFromJDProfile = () => {
    if (jdProfile?.suggestedSearch) {
      setQuery(jdProfile.suggestedSearch);
      setShowJDScanner(false);
      setJdProfile(null);
      setJdText('');
      // Trigger search after a short delay
      setTimeout(() => {
        const fakeEvent = { preventDefault: () => {} };
        handleSearch(fakeEvent);
      }, 100);
    }
  };

  const runSavedSearch = async (saved) => {
    setQuery(saved.searchQuery);
    setTimeout(() => {
      const fakeEvent = { preventDefault: () => {} };
      handleSearch(fakeEvent);
    }, 100);
    try {
      await fetch(buildApiUrl(`/api/search/saved/${saved.id}/run`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
    } catch (err) { /* ignore */ }
  };

  const updateAiSettings = async (newSettings) => {
    try {
      const res = await fetch(buildApiUrl('/api/search/settings'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify(newSettings)
      });
      if (res.ok) {
        const data = await res.json();
        setAiSettings(data);
        knAlert('Settings saved!', { title: 'Settings', variant: 'success' });
      }
    } catch (err) { knAlert('Failed to save settings', { title: 'Error', variant: 'error' }); }
  };

  // ==========================================================================
  // FILTER APPLICATION - Filters allResults into results
  // ==========================================================================
  
  const applyFiltersToResults = (candidates, filters) => {
    let filtered = [...candidates];

    if (filters.sourceFilter) {
      filtered = filtered.filter(c => c.source === filters.sourceFilter);
    }
    if (filters.hasResume === true) {
      filtered = filtered.filter(c => c.hasResume === true);
    }
    if (filters.cityMatch === true) {
      filtered = filtered.filter(c =>
        typeof c.distanceMiles === 'number' ||
        c.matchedOn?.some(m => m.field === 'city')
      );
    }
    if (filters.minExperience !== null) {
      filtered = filtered.filter(c => (c.yearsExperience || 0) >= filters.minExperience);
    }
    if (filters.maxExperience !== null) {
      filtered = filtered.filter(c => (c.yearsExperience || 0) <= filters.maxExperience);
    }
    if (filters.locations.length > 0) {
      const locs = filters.locations.map(l => l.toLowerCase());
      filtered = filtered.filter(c => {
        const cLoc = `${c.state || ''} ${c.city || ''} ${c.location || ''}`.toLowerCase();
        return locs.some(l => cLoc.includes(l));
      });
    }
    if (filters.excludeLocations.length > 0) {
      const excLocs = filters.excludeLocations.map(l => l.toLowerCase());
      filtered = filtered.filter(c => {
        const cLoc = `${c.state || ''} ${c.city || ''}`.toLowerCase();
        return !excLocs.some(l => cLoc.includes(l));
      });
    }
    if (filters.minMatchScore !== null) {
      filtered = filtered.filter(c => (c.matchScore || 0) >= filters.minMatchScore);
    }
    if (filters.hasEmail === true) {
      filtered = filtered.filter(c => c.email && typeof c.email === 'string' && c.email.includes('@'));
    }
    if (filters.noEmail === true) {
      filtered = filtered.filter(c => !c.email || typeof c.email !== 'string' || !c.email.includes('@'));
    }
    if (filters.hasPhone === true) {
      filtered = filtered.filter(c => c.phone && c.phone.length > 5);
    }
    if (filters.hasLinkedin === true) {
      filtered = filtered.filter(c => c.linkedinUrl);
    }

    return filtered;
  };
  
  // Re-filter when filters change
  useEffect(() => {
    if (allResults.length > 0) {
      const filtered = applyFiltersToResults(allResults, appliedFilters);
      setResults(filtered);
      setTotal(filtered.length);
      console.log(`[Filter] Applied filters: ${allResults.length} → ${filtered.length}`);
    }
  }, [appliedFilters, allResults]);
  
  // Generate SMART suggestions based on current state (excludes already-applied filters)
  const generateSmartSuggestions = (candidates, filters) => {
    const suggestions = [];
    
    // Stats from current results
    const withResume = candidates.filter(c => c.hasResume === true).length;
    const withEmail = candidates.filter(c => c.email && typeof c.email === 'string' && c.email.includes('@')).length;
    const withLinkedin = candidates.filter(c => c.linkedinUrl).length;
    const highMatch = candidates.filter(c => (c.matchScore || 0) >= 80).length;
    
    // Experience stats
    const senior = candidates.filter(c => (c.yearsExperience || 0) >= 7).length;
    const mid = candidates.filter(c => { const e = c.yearsExperience || 0; return e >= 3 && e < 7; }).length;
    
    // Location stats
    const locations = {};
    candidates.forEach(c => { if (c.state) locations[c.state] = (locations[c.state] || 0) + 1; });
    const topLocs = Object.entries(locations).sort((a, b) => b[1] - a[1]).slice(0, 3);
    
    // Only suggest filters NOT already applied
    if (filters.hasResume !== true && withResume > 0 && withResume < candidates.length) {
      suggestions.push({ 
        id: 'resume', 
        label: `With resume (${withResume})`, 
        action: () => setAppliedFilters(f => ({ ...f, hasResume: true }))
      });
    }
    
    const verifiedCityMatch = candidates.filter(c => typeof c.distanceMiles === 'number' || c.matchedOn?.some(m => m.field === 'city')).length;
    if (filters.cityMatch !== true && verifiedCityMatch > 0 && verifiedCityMatch < candidates.length) {
      suggestions.push({
        id: 'cityMatch',
        label: `Verified city match (${verifiedCityMatch})`,
        action: () => setAppliedFilters(f => ({ ...f, cityMatch: true }))
      });
    }

    if (filters.minMatchScore === null && highMatch > 0 && highMatch < candidates.length) {
      suggestions.push({ 
        id: 'highMatch', 
        label: `80%+ match (${highMatch})`, 
        action: () => setAppliedFilters(f => ({ ...f, minMatchScore: 80 }))
      });
    }
    
    if (filters.minExperience === null && senior > 2) {
      suggestions.push({ 
        id: 'senior', 
        label: `7+ years (${senior})`, 
        action: () => setAppliedFilters(f => ({ ...f, minExperience: 7 }))
      });
    }
    
    if (filters.minExperience === null && mid > 2) {
      suggestions.push({ 
        id: 'mid', 
        label: `3-7 years (${mid})`, 
        action: () => setAppliedFilters(f => ({ ...f, minExperience: 3, maxExperience: 7 }))
      });
    }
    
    if (filters.locations.length === 0) {
      topLocs.forEach(([loc, count]) => {
        if (count >= 3 && count < candidates.length * 0.9) {
          suggestions.push({ 
            id: `loc_${loc}`, 
            label: `In ${loc} (${count})`, 
            action: () => setAppliedFilters(f => ({ ...f, locations: [loc] }))
          });
        }
      });
    }
    
    if (filters.hasEmail !== true && withEmail > 0 && withEmail < candidates.length) {
      suggestions.push({
        id: 'email',
        label: `Has email (${withEmail})`,
        action: () => setAppliedFilters(f => ({ ...f, hasEmail: true }))
      });
    }

    const withoutEmail = candidates.length - withEmail;
    if (filters.noEmail !== true && filters.hasEmail !== true && withoutEmail > 0 && withoutEmail < candidates.length) {
      suggestions.push({
        id: 'noEmail',
        label: `No email (${withoutEmail})`,
        action: () => setAppliedFilters(f => ({ ...f, noEmail: true }))
      });
    }

    return suggestions.slice(0, 6);
  };
  
  // Parse refinement text into filter changes (filters in-place, no re-search)
  const parseRefinementToFilters = (text) => {
    const lower = text.toLowerCase().trim();
    const updates = {};

    // Resume filters
    if (lower.includes('with resume') || lower.includes('has resume') || lower.includes('must have resume')) {
      updates.hasResume = true;
    }

    // Experience filters
    const expRangeMatch = lower.match(/(\d+)\s*[-–to]+\s*(\d+)\s*years?/);
    const expMinMatch = lower.match(/(\d+)\+?\s*years?/);
    if (expRangeMatch) {
      updates.minExperience = parseInt(expRangeMatch[1]);
      updates.maxExperience = parseInt(expRangeMatch[2]);
    } else if (expMinMatch) {
      updates.minExperience = parseInt(expMinMatch[1]);
    }

    // Location - state names and abbreviations
    const locMatch = lower.match(/(?:in|only in|near|from)\s+([a-z\s]+?)(?:\s*$|,|\.|!)/i);
    if (locMatch) {
      updates.locations = [locMatch[1].trim()];
    }

    // Match score filters
    if (lower.includes('top match') || lower.includes('best match') || lower.includes('80%') || lower.includes('excellent')) {
      updates.minMatchScore = 80;
    }
    if (lower.match(/(\d+)%\s*match/) || lower.match(/match.*(\d+)%/)) {
      const pctMatch = lower.match(/(\d+)%/);
      if (pctMatch) updates.minMatchScore = parseInt(pctMatch[1]);
    }

    // Contact info filters
    if (lower.includes('with email') || lower.includes('has email')) updates.hasEmail = true;
    if (lower.includes('no email') || lower.includes('without email') || lower.includes('missing email')) updates.noEmail = true;
    if (lower.includes('with phone') || lower.includes('has phone')) updates.hasPhone = true;
    if (lower.includes('with linkedin') || lower.includes('has linkedin')) updates.hasLinkedin = true;

    // Source filter - show only knine or ats
    if (lower.includes('only knine') || lower.includes('knine only') || lower.includes('only kNine')) {
      updates.sourceFilter = 'knine';
    }
    if (lower.includes('only ats') || lower.includes('ats only')) {
      updates.sourceFilter = 'ats';
    }

    return updates;
  };
  
  // Clear individual filter
  const clearFilter = (key) => {
    setAppliedFilters(f => {
      const updated = { ...f };
      if (Array.isArray(updated[key])) {
        updated[key] = [];
      } else {
        updated[key] = null;
      }
      return updated;
    });
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    setAppliedFilters({
      hasResume: null, cityMatch: null, minExperience: null, maxExperience: null,
      locations: [], excludeLocations: [], minMatchScore: null,
      hasEmail: null, noEmail: null, hasPhone: null, hasLinkedin: null,
      sourceFilter: null
    });
  };
  
  // Get count of active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (appliedFilters.hasResume !== null) count++;
    if (appliedFilters.cityMatch !== null) count++;
    if (appliedFilters.minExperience !== null) count++;
    if (appliedFilters.maxExperience !== null) count++;
    if (appliedFilters.locations.length > 0) count++;
    if (appliedFilters.minMatchScore !== null) count++;
    if (appliedFilters.hasEmail !== null) count++;
    if (appliedFilters.noEmail !== null) count++;
    if (appliedFilters.hasPhone !== null) count++;
    if (appliedFilters.hasLinkedin !== null) count++;
    return count;
  };
  
  // ==========================================================================
  // DATA FETCHING - With detailed logging
  // ==========================================================================
  
  const fetchRollups = async () => {
    setDataLoading(p => ({ ...p, rollups: true }));
    setDataError(p => ({ ...p, rollups: null }));
    try {
      console.log('[Rollups] Fetching...');
      const res = await fetch(buildApiUrl('/api/pcr/rollups'), { 
        headers: { 'Authorization': `Bearer ${getToken()}` } 
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const data = await res.json();
      console.log('[Rollups] Raw response:', data);
      
      // Handle the response - the API returns { rollups: [...] }
      let lists = data.rollups || data.Results || data || [];
      if (!Array.isArray(lists)) lists = [];
      
      console.log(`[Rollups] Found ${lists.length} lists`);
      if (lists.length > 0) {
        console.log('[Rollups] Sample:', JSON.stringify(lists[0]));
      }
      
      // Normalize - the PCRecruiterService already normalizes, but ensure we have expected fields
      const normalized = lists.map((r, idx) => ({
        id: r.id || r.RollupId || r.Code || `rollup-${idx}`,
        code: r.code || r.Code || r.RollupId || r.id || `CODE-${idx}`,
        name: r.name || r.Name || r.RollupName || r.Description || r.code || r.Code || `List ${idx + 1}`,
        memberCount: r.memberCount || r.MemberCount || r.RecordCount || r.Count || 0
      }));
      
      setRollups(normalized);
    } catch (e) { 
      console.error('[Rollups] Error:', e);
      setDataError(p => ({ ...p, rollups: e.message }));
      setRollups([]);
    }
    finally { setDataLoading(p => ({ ...p, rollups: false })); }
  };
  
  const fetchPositions = async () => {
    setDataLoading(p => ({ ...p, positions: true }));
    setDataError(p => ({ ...p, positions: null }));
    try {
      console.log('[Positions] Fetching...');
      // Request all positions (status=all) to avoid filtering issues
      const res = await fetch(buildApiUrl('/api/pcr/positions?status=all&limit=200'), { 
        headers: { 'Authorization': `Bearer ${getToken()}` } 
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const data = await res.json();
      console.log('[Positions] Raw response:', data);
      
      // Handle the response - the API returns { positions: [...], total }
      let jobs = data.positions || data.Results || data || [];
      if (!Array.isArray(jobs)) jobs = [];
      
      console.log(`[Positions] Found ${jobs.length} positions`);
      if (jobs.length > 0) {
        console.log('[Positions] Sample:', JSON.stringify(jobs[0]));
      }
      
      if (data.debug) {
        console.log('[Positions] Debug info:', data.debug);
      }
      
      // Normalize
      const normalized = jobs.map((p, idx) => ({
        id: p.id || p.JobId || p.PositionId || `pos-${idx}`,
        title: p.title || p.JobTitle || p.Title || `Position ${idx + 1}`,
        companyName: p.companyName || p.CompanyName || p.company || '',
        location: p.location || [p.city || p.City, p.state || p.State].filter(Boolean).join(', ') || '',
        status: p.status || p.Status || 'Open'
      }));
      
      setPositions(normalized);
    } catch (e) { 
      console.error('[Positions] Error:', e);
      setDataError(p => ({ ...p, positions: e.message }));
      setPositions([]);
    }
    finally { setDataLoading(p => ({ ...p, positions: false })); }
  };

  // ==========================================================================
  // JOB SELECTOR - Search and select positions from PCR
  // ==========================================================================

  // Fetch recent positions when job selector opens
  useEffect(() => {
    if (showJobSelector && positions.length > 0) {
      // Use the already-fetched positions as recent positions
      setRecentPositions(positions.slice(0, 10));
    }
  }, [showJobSelector, positions]);

  // Search jobs in PCR
  const searchJobs = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setJobSearchResults([]);
      return;
    }

    setJobSearchLoading(true);
    try {
      const res = await fetch(buildApiUrl(`/api/pcr/positions?search=${encodeURIComponent(searchQuery)}&limit=20`), {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      let jobs = data.positions || data.Results || data || [];
      if (!Array.isArray(jobs)) jobs = [];

      const normalized = jobs.map((p, idx) => ({
        id: p.id || p.JobId || p.PositionId || `job-${idx}`,
        title: p.title || p.JobTitle || p.Title || `Position ${idx + 1}`,
        companyName: p.companyName || p.CompanyName || p.company || '',
        location: p.location || [p.city || p.City, p.state || p.State].filter(Boolean).join(', ') || '',
        status: p.status || p.Status || 'Open',
        description: p.description || p.Description || p.JobDescription || ''
      }));

      setJobSearchResults(normalized);
    } catch (e) {
      console.error('[JobSearch] Error:', e);
      setJobSearchResults([]);
    } finally {
      setJobSearchLoading(false);
    }
  };

  // Handle job selection - populate search with job title and open JD scanner if description available
  const handleJobSelect = (job) => {
    setQuery(job.title);
    setShowJobSelector(false);

    // If the job has a description, also set it up for parsing
    if (job.description && job.description.length > 50) {
      setJdText(job.description);
      // Optionally auto-open JD scanner: setShowJDScanner(true);
    }

    // Trigger search with the job title
    setTimeout(() => {
      handleSearch(null, false);
    }, 100);
  };

  // ==========================================================================
  // SEARCH
  // ==========================================================================
  
  const handleSearch = async (e, isRefinement = false, overrideQuery = null) => {
    e?.preventDefault();
    const searchText = overrideQuery || (isRefinement ? refinementQuery.trim() : query.trim());
    if (!searchText) return;
    
    // For refinements, try to parse as a filter first
    if (isRefinement) {
      const filterUpdates = parseRefinementToFilters(searchText);
      if (Object.keys(filterUpdates).length > 0) {
        console.log('[Refine] Parsed as filters:', filterUpdates);
        setAppliedFilters(f => ({ ...f, ...filterUpdates }));
        setRefinementQuery('');
        return; // Apply filter locally, no API call needed
      }
      // If not parsed as filter, fall through to AI search
    }
    
    setLoading(true); setSearched(true); setError(null); setSearchSteps([]); setSourceErrors([]);
    setSelectedIds(new Set());
    // Clear source filter on new search so kNine results aren't hidden by a stale ATS-only filter
    if (!isRefinement) {
      setAppliedFilters(f => ({ ...f, sourceFilter: null }));
    }
    
    if (!isRefinement) {
      // New search - clear everything
      setAllResults([]);
      setResults([]);
      setConversationContext(null);
      setActiveSearch(searchText);
      setKnineScrollToken(null);
      setKninePoolSize(0);
      setKnineDisplayLimit(KNINE_PAGE_SIZE);
      setResultsDisplayLimit(RESULTS_PAGE_SIZE);
      clearAllFilters(); // Reset filters on new search
    }
    
    const steps = isRefinement ? [
      { text: 'AI refining your search...', icon: Brain },
      { text: 'Applying criteria...', icon: Sparkles }
    ] : [
      { text: 'Roxi AI interpreting your search...', icon: Brain },
      { text: 'Expanding search terms...', icon: Wand2 },
      ...(sources.ats ? [{ text: 'Searching your ATS...', icon: Database }] : []),
      ...(sources.knine ? [{ text: 'Searching kNine database...', icon: Globe }] : []),
      { text: 'AI ranking candidates...', icon: Sparkles }
    ];
    
    for (let i = 0; i < steps.length; i++) {
      await new Promise(r => setTimeout(r, 200));
      setSearchSteps(p => [...p, steps[i]]);
    }
    
    try {
      const searchSources = [];
      if (sources.ats) searchSources.push('ats');
      if (sources.knine) searchSources.push('knine');

      // Fetch excluded kNine IDs (enriched + dismissed) to filter from results
      let currentExcludedIds = excludedKnineIds;
      if (searchSources.includes('knine')) {
        try {
          const exRes = await fetch(buildApiUrl('/api/knine/excluded-ids'), {
            headers: { 'Authorization': `Bearer ${getToken()}` }
          });
          if (exRes.ok) {
            const exData = await exRes.json();
            currentExcludedIds = new Set(exData.excludedIds || []);
            setExcludedKnineIds(currentExcludedIds);
          }
        } catch (e) { console.warn('Failed to fetch excluded IDs:', e); }
      }

      let data = null;

      // Build the full query for refinement (include context)
      const fullQuery = isRefinement && conversationContext?.originalQuery
        ? `${conversationContext.originalQuery} - ${searchText}`
        : searchText;

      // Try AI search with credit deduction (uses /api/ai-search which has proper credit handling)
      try {
        const aiRes = await fetch(buildApiUrl('/api/ai-search'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
          body: JSON.stringify({
            query: fullQuery,
            sources: searchSources,
            limit: 500, // Fetch all matching ATS candidates (PCR max per page)
            previousContext: isRefinement ? conversationContext : null,
            isRefinement: isRefinement // Important: tells backend not to deduct credits for refinements
          })
        });
        if (aiRes.ok) {
          data = await aiRes.json();
        } else {
          const errData = await aiRes.json().catch(() => ({}));
          // Handle insufficient credits error
          if (aiRes.status === 402) {
            throw new Error(errData.message || 'Insufficient credits. Please purchase more credits to continue searching.');
          }
          throw new Error(errData.error || 'Search failed');
        }
      } catch (err) {
        console.warn('AI search error:', err.message);
        throw err; // Re-throw to be caught by outer try-catch
      }
      
      const candidates = (data.candidates || []).map(c => ({
        candidateId: c.candidateId || c.id,
        fullName: c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown',
        title: c.title || c.currentOccupation || '',
        company: c.company || c.currentEmployer || '',
        location: c.location || '',
        city: c.city, state: c.state,
        email: c.email || '',
        phone: c.phone || '',
        linkedinUrl: c.linkedinUrl || '',
        skills: c.skills || [],
        keywords: c.keywords || [],
        hasResume: c.hasResume,
        industry: c.industry || '',
        specialty: c.specialty || '',
        yearsExperience: c.yearsExperience,
        currentSalary: c.currentSalary,
        desiredSalary: c.desiredSalary,
        status: c.status || '',
        source: c.source || 'ats',
        dateEntered: c.dateEntered,
        lastModified: c.lastModified,
        matchScore: c.matchScore || 50,
        matchLevel: c.matchLevel || (c.matchScore >= 85 ? 'Excellent' : c.matchScore >= 70 ? 'Good' : 'Fair'),
        matchReasons: c.matchReasons || [],
        ...c
      })).filter(c => {
        // Exclude previously enriched/dismissed kNine candidates
        if (c.source === 'knine' && currentExcludedIds.size > 0) {
          const cid = c.candidateId || c.id;
          return !currentExcludedIds.has(cid) && !currentExcludedIds.has(String(cid));
        }
        return true;
      });
      
      console.log(`[Search] Got ${candidates.length} candidates from API`);
      const errs = [];
      if (data.sources) {
        data.sources.forEach(s => {
          console.log(`[Search] Source ${s.source}: ${s.count} results${s.error ? ` | ERROR: ${s.error}` : ''}`);
          if (s.error) errs.push({ source: s.source, error: s.error });
        });
      }
      setSourceErrors(errs);
      
      // Store raw results
      setAllResults(candidates);
      
      // Apply current filters (initially empty, so all results show)
      const filtered = applyFiltersToResults(candidates, appliedFilters);
      setResults(filtered);
      setTotal(filtered.length);
      
      if (data.creditsRemaining !== undefined) {
        setCreditsRemaining(data.creditsRemaining);
        setCredits(data.creditsRemaining); // Update toolbar credits display
      }

      // Store PDL total, pool size, scroll token, and ATS count
      if (data.knineTotal !== undefined) setKnineTotal(data.knineTotal);
      if (data.kninePoolSize !== undefined) setKninePoolSize(data.kninePoolSize);
      if (data.pagination?.scrollToken !== undefined) setKnineScrollToken(data.pagination.scrollToken);
      else if (!data.isPagination) setKnineScrollToken(null);
      if (data.atsCount !== undefined) setAtsCount(data.atsCount);
      
      if (data.aiInterpretation) {
        const ai = data.aiInterpretation;
        const f = ai.filters || {};

        setAiInterpretation({
          summary: ai.summary,
          searchTerms: [f.job_title, ...(f.skills || []).slice(0, 3)].filter(Boolean),
          expandedTerms: (f.job_title_variations || []).slice(0, 6),
          suggestions: generateSmartSuggestions(filtered, appliedFilters),
          filters: f
        });

        // Store discovery criteria from backend
        if (data.discoveryCriteria) {
          let criteria = data.discoveryCriteria;
          // If PDL had to relax the query to find results, downgrade required filters
          // that were loosened so the display reflects what was actually searched.
          if (data.knineRelaxed) {
            criteria = criteria.map(c => {
              if ((c.category === 'industry' || c.category === 'companies') && c.weight === 'required') {
                return { ...c, weight: 'preferred', relaxedNote: 'Relaxed to find results' };
              }
              return c;
            });
          }
          setDiscoveryCriteria(criteria);
          setCriteriaModified(false);
          setShowDiscovery(true);
        }

        setConversationContext({
          originalQuery: isRefinement ? conversationContext?.originalQuery : searchText,
          lastQuery: searchText,
          filters: f
        });
      } else {
        setAiInterpretation({
          summary: `Found ${candidates.length} candidates`,
          searchTerms: searchText.split(/\s+/).slice(0, 3),
          expandedTerms: [],
          suggestions: generateSmartSuggestions(filtered, appliedFilters)
        });
        setDiscoveryCriteria([]);
      }
      
      if (isRefinement) setRefinementQuery('');

      // Save to search history (only for new searches, not refinements)
      if (!isRefinement && searchText) {
        saveSearchToHistory(
          searchText,
          data.aiInterpretation?.filters || {},
          candidates.length,
          data.aiInterpretation?.summary || `Found ${candidates.length} candidates`
        );
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Re-run search using the current discoveryCriteria state exactly as edited.
  // Passes prebuiltFilters directly to the backend so AI re-interpretation is
  // skipped — removed title variations stay removed, added ones stay added.
  const handleRefreshResults = async () => {
    const titleCrit    = discoveryCriteria.find(c => c.category === 'job_title');
    const locationCrit = discoveryCriteria.find(c => c.category === 'location');
    const skillsCrit   = discoveryCriteria.find(c => c.category === 'skills');
    const skillsOptCrit = discoveryCriteria.find(c => c.category === 'skills_optional');
    const expCrit      = discoveryCriteria.find(c => c.category === 'experience');
    const industryCrit = discoveryCriteria.find(c => c.category === 'industry');
    const seniorityCrit = discoveryCriteria.find(c => c.category === 'seniority');
    const certCrit     = discoveryCriteria.find(c => c.category === 'certifications');
    const eduCrit      = discoveryCriteria.find(c => c.category === 'education');
    const companiesCrit = discoveryCriteria.find(c => c.category === 'companies');

    const titleValues = titleCrit?.values || [];
    const primaryTitle = titleCrit?.primary || titleValues[0] || null;
    const titleVariations = titleValues.filter(v => v !== primaryTitle);

    // Rebuild filters exactly matching the backend's normalizeFilters schema
    const prebuiltFilters = {
      job_title: primaryTitle,
      job_title_variations: titleVariations,
      skills: skillsCrit?.values || [],
      skills_optional: skillsOptCrit?.values || [],
      industries: industryCrit?.values || [],
      seniority_levels: seniorityCrit?.values || [],
      certifications: certCrit?.values || [],
      education: eduCrit?.raw || null,
      locations: locationCrit?.raw || [],
      is_remote: !!discoveryCriteria.find(c => c.category === 'remote'),
      experience: expCrit?.raw || {},
      companies: companiesCrit?.raw || null,
      criteria_weights: discoveryCriteria.reduce((acc, c) => {
        acc[c.category === 'job_title' ? 'job_title'
          : c.category === 'skills' ? 'skills'
          : c.category === 'location' ? 'location'
          : c.category === 'experience' ? 'experience'
          : c.category === 'industry' ? 'industry'
          : c.category === 'education' ? 'education'
          : c.category === 'seniority' ? 'seniority_level'
          : c.category === 'companies' ? 'past_companies'
          : c.category] = c.weight;
        return acc;
      }, {})
    };

    setCriteriaModified(false);
    setLoading(true);
    setSearched(true);
    setError(null);
    setSearchSteps([{ text: 'Applying your edited criteria...', icon: Sparkles }]);
    setAllResults([]);
    setResults([]);
    setKnineScrollToken(null);
    setKninePoolSize(0);
    setKnineDisplayLimit(KNINE_PAGE_SIZE);
      setResultsDisplayLimit(RESULTS_PAGE_SIZE);

    try {
      const searchSources = [];
      if (sources.ats) searchSources.push('ats');
      if (sources.knine) searchSources.push('knine');

      const res = await fetch(buildApiUrl('/api/ai-search'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({
          query,
          sources: searchSources,
          limit: 500,
          prebuiltFilters,
          isRefinement: true  // don't deduct credits for a criteria edit refresh
        })
      });
      if (!res.ok) throw new Error('Refresh failed');
      const data = await res.json();

      const candidates = (data.candidates || []).map(c => ({
        candidateId: c.candidateId || c.id,
        fullName: c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown',
        ...c
      }));
      setAllResults(candidates);
      setResults(candidates);
      if (data.knineTotal !== undefined) setKnineTotal(data.knineTotal);
      if (data.pagination?.scrollToken !== undefined) setKnineScrollToken(data.pagination.scrollToken);
      if (data.discoveryCriteria) setDiscoveryCriteria(data.discoveryCriteria);
    } catch (err) {
      setError('Refresh failed: ' + err.message);
    } finally {
      setLoading(false);
      setSearchSteps([]);
    }
  };

  // Fetch the next page of kNine results from PDL and merge into existing results
  const loadMoreKnine = async () => {
    if (!knineScrollToken || loadingMoreKnine) return;
    setLoadingMoreKnine(true);
    try {
      const res = await fetch(buildApiUrl('/api/ai-search'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({
          query: query,
          sources: ['knine'],
          limit: 500,
          scrollToken: knineScrollToken,
          isRefinement: true  // pagination — no credit deduction
        })
      });
      if (!res.ok) throw new Error('Failed to load more results');
      const data = await res.json();
      const newCandidates = (data.candidates || []).filter(c =>
        c.source === 'knine' && !allResults.some(existing => (existing.candidateId || existing.id) === (c.candidateId || c.id))
      );
      if (newCandidates.length > 0) {
        setAllResults(prev => [...prev, ...newCandidates]);
        setResults(prev => [...prev, ...newCandidates]);
      }
      setKnineScrollToken(data.pagination?.scrollToken || null);
      if (data.kninePoolSize) setKninePoolSize(p => p + data.kninePoolSize);
    } catch (err) {
      console.warn('Load more kNine failed:', err.message);
    } finally {
      setLoadingMoreKnine(false);
    }
  };

  // Auto-refresh when a filter chip is removed (not just weight cycling)
  useEffect(() => {
    if (!criteriaChipRemoved) return;
    setCriteriaChipRemoved(false);
    if (allResults.length === 0) return;
    const timer = setTimeout(() => {
      handleRefreshResults();
    }, 300);
    return () => clearTimeout(timer);
  }, [criteriaChipRemoved]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update suggestions when filters change
  useEffect(() => {
    if (results.length > 0 && aiInterpretation) {
      setAiInterpretation(ai => ({
        ...ai,
        suggestions: generateSmartSuggestions(results, appliedFilters)
      }));
    }
  }, [results, appliedFilters]);
  
  // DEPRECATED: Old static suggestions - kept for reference but not used
  const generateSuggestions = (candidates) => {
    // Use generateSmartSuggestions instead
    return generateSmartSuggestions(candidates, appliedFilters);
  };
  
  const generateSuggestionsOld = (candidates) => {
    const suggestions = [];
    const locations = {};
    candidates.forEach(c => { if (c.state) locations[c.state] = (locations[c.state] || 0) + 1; });
    Object.entries(locations).sort((a, b) => b[1] - a[1]).slice(0, 2).forEach(([loc, count]) => {
      if (count >= 3) suggestions.push({ label: `Only in ${loc}`, query: `only in ${loc}`, count });
    });
    suggestions.push({ label: '5+ years experience', query: 'with 5+ years experience' });
    const withResume = candidates.filter(c => c.hasResume).length;
    if (withResume > 0 && withResume < candidates.length) {
      suggestions.push({ label: `With resume (${withResume})`, query: 'must have resume', count: withResume });
    }
    return suggestions.slice(0, 4);
  };
  
  const handleRefinement = (e) => {
    e?.preventDefault();
    const text = refinementQuery.trim();
    if (!text) return;
    // Always try to parse as in-place filters first
    const filterUpdates = parseRefinementToFilters(text);
    if (Object.keys(filterUpdates).length > 0) {
      setAppliedFilters(f => ({ ...f, ...filterUpdates }));
      setRefinementQuery('');
    } else {
      // Could not parse as filter — still don't re-search, just show message
      setRefinementQuery('');
      knAlert('Try specific filters like "only in Ohio", "5+ years", "with email", "has resume", "only knine", etc.', { title: 'Filter Tip', variant: 'info' });
    }
  };
  
  // Apply suggestion - supports both old query-based and new action-based
  const applySuggestion = (s) => { 
    if (s.action) {
      // New action-based suggestion
      s.action();
    } else if (s.query) {
      // Old query-based suggestion (fallback)
      setRefinementQuery(s.query); 
      setTimeout(() => handleSearch(null, true), 100); 
    }
  };
  
  const startOver = () => {
    setQuery(''); setRefinementQuery(''); setResults([]); setAllResults([]); setSearched(false);
    setAiInterpretation(null); setConversationContext(null); setError(null);
    setSearchSteps([]); setActiveSearch(null); setSelectedIds(new Set());
    setKnineTotal(0); setKninePoolSize(0); setKnineScrollToken(null); setAtsCount(0);
    setCriteriaModified(false); setDiscoveryCriteria([]);
    clearAllFilters();
  };

  // Sorting
  const sortedResults = [...results].sort((a, b) => {
    let valA, valB;
    switch (sortBy) {
      case 'name': valA = a.fullName || ''; valB = b.fullName || ''; break;
      case 'experience': valA = a.yearsExperience || 0; valB = b.yearsExperience || 0; break;
      case 'date': valA = new Date(a.lastModified || 0); valB = new Date(b.lastModified || 0); break;
      default: valA = a.matchScore || 0; valB = b.matchScore || 0;
    }
    if (typeof valA === 'string') return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    return sortDir === 'asc' ? valA - valB : valB - valA;
  });

  // ==========================================================================
  // SELECTION & BULK ACTIONS
  // ==========================================================================
  
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  
  const toggleSelectAll = () => {
    if (selectedIds.size === results.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(results.map(r => r.candidateId)));
    }
  };
  
  const getSelectedCandidates = () => results.filter(r => selectedIds.has(r.candidateId));
  
  const openBulkAddToList = () => {
    const selected = getSelectedCandidates();
    if (selected.length === 0) return;
    setActionCandidates(selected);
    setShowAddToList(true);
  };
  
  const openBulkSubmitToPosition = () => {
    const selected = getSelectedCandidates();
    if (selected.length === 0) return;
    setActionCandidates(selected);
    setShowSubmitToPosition(true);
  };

  // Enrich selected candidates - deducts 1 credit per candidate
  const [enriching, setEnriching] = useState(false);
  const handleBulkEnrich = async () => {
    const selected = getSelectedCandidates();
    if (selected.length === 0) return;

    setEnriching(true);
    let successCount = 0;
    let errorMessage = null;

    for (const candidate of selected) {
      try {
        const res = await fetch(buildApiUrl('/api/contacts/enrich'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
          body: JSON.stringify({
            candidate: {
              id: candidate.candidateId,
              full_name: candidate.fullName,
              email: candidate.email,
              linkedin_url: candidate.linkedinUrl,
              company: candidate.company
            }
          })
        });

        if (res.status === 402) {
          const errData = await res.json();
          errorMessage = errData.message || 'Insufficient credits';
          break;
        }

        if (res.ok) {
          const data = await res.json();
          successCount++;
          // Update credits display
          if (data.creditsRemaining !== undefined) {
            setCredits(data.creditsRemaining);
          }
        }
      } catch (err) {
        console.error('Enrich error:', err);
      }
    }

    setEnriching(false);
    setSelectedIds(new Set());

    if (errorMessage) {
      knAlert(errorMessage, { title: 'Enrichment Error', variant: 'error' });
    } else if (successCount > 0) {
      knAlert(`Successfully enriched ${successCount} contact${successCount > 1 ? 's' : ''}`, { title: 'Enrichment Complete', variant: 'success' });
    }
  };
  
  const openSingleAddToList = (candidate) => {
    setActionCandidates([candidate]);
    setShowAddToList(true);
  };
  
  const openSingleSubmitToPosition = (candidate) => {
    setActionCandidates([candidate]);
    setShowSubmitToPosition(true);
  };

  // ==========================================================================
  // PROFILE
  // ==========================================================================
  
  // Track enriched but unsaved kNine results for navigation guard
  const [enrichedUnsaved, setEnrichedUnsaved] = useState(() => new Set(saved?.enrichedUnsaved || []));
  const [atsUploadResult, setAtsUploadResult] = useState(null);
  const [enrichingProfile, setEnrichingProfile] = useState(false);
  const [excludedKnineIds, setExcludedKnineIds] = useState(() => new Set(saved?.excludedKnineIds || []));

  // ---- Assign searchStateRef AFTER all useState declarations (avoids TDZ) ----
  searchStateRef.current = {
    query, sources, results, allResults, total, searched, activeSearch,
    aiInterpretation, discoveryCriteria, showDiscovery, expandedCriteria,
    searchSteps, conversationContext, creditsRemaining,
    sortBy, sortDir, appliedFilters,
    enrichedUnsaved: [...enrichedUnsaved],
    excludedKnineIds: [...excludedKnineIds],
    knineTotal, kninePoolSize, knineScrollToken, knineDisplayLimit
  };

  // Navigation guard removed — no more Chrome native "leave page?" popup.
  // Instead, an in-app warning banner is shown when enriched data is unsaved (see render).

  // Enrich a kNine preview record on profile open
  const enrichKnineCandidate = async (candidate) => {
    setEnrichingProfile(true);
    try {
      const res = await fetch(buildApiUrl('/api/knine/enrich'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({
          recordId: candidate.id || candidate.candidateId,
          linkedinUrl: candidate.linkedinUrl,
          fullName: candidate.fullName,
          company: candidate.company
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        if (res.status === 402) {
          knAlert(errData.message || 'Insufficient credits to view contact information.', { title: 'Insufficient Credits', variant: 'warning' });
          return null;
        }
        throw new Error(errData.error || 'Enrichment failed');
      }

      const data = await res.json();
      if (data.creditsRemaining !== undefined) {
        setCreditsRemaining(data.creditsRemaining);
        setCredits(data.creditsRemaining);
      }

      if (!data.cached) {
        // Track as unsaved enriched data (credit was charged)
        setEnrichedUnsaved(prev => new Set([...prev, candidate.candidateId || candidate.id]));
      }

      // Return enriched data, dupe detection, ATS connection status, and contact validation result
      return { enrichedData: data.data, dupeDetection: data.dupeDetection || null, atsConnected: data.atsConnected, validation: data.validation || null };
    } catch (err) {
      console.error('kNine enrichment error:', err);
      knAlert('Failed to load contact information: ' + err.message, { title: 'Enrichment Error', variant: 'error' });
      return null;
    } finally {
      setEnrichingProfile(false);
    }
  };

  // Dismiss a kNine candidate (don't show again)
  const dismissKnineCandidate = async (candidate) => {
    const candId = candidate.candidateId || candidate.id;

    // Close the drawer and remove from results immediately — don't wait for the server
    setSelectedCandidate(null);
    setAtsUploadResult(null);
    setExcludedKnineIds(prev => new Set([...prev, candId]));
    setAllResults(prev => prev.filter(c => (c.candidateId || c.id) !== candId));
    setResults(prev => prev.filter(c => (c.candidateId || c.id) !== candId));

    // Fire-and-forget: record the dismissal server-side
    fetch(buildApiUrl('/api/knine/dismiss'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
      body: JSON.stringify({
        candidateId: candId,
        linkedinUrl: candidate.linkedinUrl,
        fullName: candidate.fullName
      })
    }).catch(err => console.warn('Dismiss record failed (UI already updated):', err.message));
  };

  // Bulk enrich selected kNine candidates
  const handleBulkKnineEnrich = async () => {
    const selected = getSelectedCandidates().filter(c => c.source === 'knine' && c.isPreview && !c.enriched);
    if (selected.length === 0) return;

    setEnriching(true);
    try {
      const res = await fetch(buildApiUrl('/api/knine/bulk-enrich'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({
          candidates: selected.map(c => ({
            recordId: c.id || c.candidateId,
            linkedinUrl: c.linkedinUrl,
            fullName: c.fullName,
            company: c.company
          }))
        })
      });

      if (res.status === 402) {
        const errData = await res.json();
        knAlert(errData.message || 'Insufficient credits', { title: 'Insufficient Credits', variant: 'warning' });
        setEnriching(false);
        return;
      }

      const data = await res.json();
      if (data.creditsRemaining !== undefined) {
        setCreditsRemaining(data.creditsRemaining);
        setCredits(data.creditsRemaining);
      }

      // Update enriched candidates in results
      if (data.results) {
        const enrichedMap = {};
        data.results.forEach(r => { if (r.success && r.data) enrichedMap[r.id] = r.data; });

        const updateCand = c => {
          const enriched = enrichedMap[c.candidateId || c.id];
          if (!enriched) return c;
          return {
            ...c, ...enriched, enriched: true, enrichedData: enriched,
            isPreview: false, source: 'knine', sourceSystem: 'kNine'
          };
        };
        setAllResults(prev => prev.map(updateCand));
        setResults(prev => prev.map(updateCand));
      }

      setSelectedIds(new Set());
      if (data.successCount > 0) {
        knAlert(`Enriched ${data.successCount} candidate${data.successCount > 1 ? 's' : ''}${data.failCount > 0 ? ` (${data.failCount} failed)` : ''}`, { title: 'Enrichment Complete', variant: 'success' });
      }
    } catch (err) {
      console.error('Bulk enrich error:', err);
      knAlert('Bulk enrichment failed: ' + err.message, { title: 'Enrichment Error', variant: 'error' });
    }
    setEnriching(false);
  };

  // Upload enriched kNine record to ATS
  const uploadToATS = async (enrichedData, validation = null) => {
    setAtsUploadResult({ action: 'saving', message: 'Saving to ATS...' });
    try {
      const res = await fetch(buildApiUrl('/api/knine/upload-to-ats'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ enrichedData, validation: validation || enrichedData.validation || null })
      });
      const data = await res.json();

      // Normalize error responses — if no action field, treat as error so UI shows something
      if (!res.ok || (!data.action && data.error)) {
        const errorResult = { action: 'error', message: data.error || data.message || 'Upload failed. Please try again.' };
        setAtsUploadResult(errorResult);
        return null;
      }

      setAtsUploadResult(data);

      // Mark as saved (remove from unsaved set) and store ATS record ID in candidates
      if (data.action === 'created' || data.action === 'updated') {
        const candId = enrichedData.id || enrichedData.candidateId;
        setEnrichedUnsaved(prev => {
          const next = new Set(prev);
          next.delete(candId);
          return next;
        });
        // Mark candidate in list as ATS-uploaded so future searches know it exists
        const updateCand = c =>
          (c.candidateId || c.id) === candId || (c.id === candId)
            ? { ...c, atsRecordId: data.candidateId, inATS: true } : c;
        setAllResults(prev => prev.map(updateCand));
        setResults(prev => prev.map(updateCand));
        // Show prominent success alert
        knAlert(`Name Record for "${data.candidateName || titleCase(enrichedData.fullName || enrichedData.full_name || '')}" has been created in your ATS`, {
          title: 'Record Saved', variant: 'success'
        });
      }

      return data;
    } catch (err) {
      console.error('ATS upload error:', err);
      setAtsUploadResult({ action: 'error', message: err.message });
      return null;
    }
  };

  // Update existing ATS candidate with enriched data
  const updateATSCandidate = async (candidateId, enrichedData, selectedChanges) => {
    try {
      const res = await fetch(buildApiUrl('/api/knine/update-ats-candidate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ candidateId, enrichedData, selectedChanges, validation: enrichedData.validation || null })
      });
      const data = await res.json();
      setAtsUploadResult(data);

      if (data.action === 'updated') {
        setEnrichedUnsaved(prev => {
          const next = new Set(prev);
          next.delete(enrichedData.id);
          return next;
        });
      }

      return data;
    } catch (err) {
      console.error('ATS update error:', err);
      return null;
    }
  };

  // Dismiss enriched result without saving (acknowledge credit was used)
  const dismissEnrichedResult = (candidateId) => {
    setEnrichedUnsaved(prev => {
      const next = new Set(prev);
      next.delete(candidateId);
      return next;
    });
    setSelectedCandidate(null);
    setAtsUploadResult(null);
  };

  const openProfile = async (candidate) => {
    const { matchScore, matchLevel, matchReasons } = candidate;
    setAtsUploadResult(null);

    setSelectedCandidate({
      ...candidate,
      attachments: [],
      loadingAttachments: true,
      matchScore, matchLevel, matchReasons
    });

    // kNine records that are already enriched — just open with existing data (no re-charge)
    if (candidate.source === 'knine' && candidate.enriched) {
      setSelectedCandidate(prev => prev ? ({
        ...prev, loadingAttachments: false, attachments: []
      }) : null);
      // Restore dupe detection if it was stored
      if (candidate.dupeDetection?.found) {
        setAtsUploadResult({
          action: 'duplicate_found',
          duplicateCandidate: candidate.dupeDetection.candidate,
          changes: candidate.dupeDetection.changes || [],
          message: candidate.dupeDetection.message
        });
      }
      return;
    }

    // kNine preview records: open drawer with preview data + LinkedIn dupe check (NO auto-enrich)
    if (candidate.source === 'knine' && candidate.isPreview && !candidate.enriched) {
      setSelectedCandidate(prev => prev ? ({
        ...prev, loadingAttachments: false, attachments: [],
        matchScore, matchLevel, matchReasons
      }) : null);

      // Run LinkedIn dupe check in background
      if (candidate.linkedinUrl) {
        try {
          const dupeRes = await fetch(buildApiUrl('/api/knine/linkedin-dupe-check'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
            body: JSON.stringify({ linkedinUrl: candidate.linkedinUrl, fullName: candidate.fullName })
          });
          if (dupeRes.ok) {
            const dupeData = await dupeRes.json();
            if (dupeData.found) {
              setSelectedCandidate(prev => prev ? ({
                ...prev,
                dupeDetection: dupeData,
                existingAtsRecord: dupeData.candidate
              }) : null);
            }
          }
        } catch (e) { console.warn('LinkedIn dupe check failed:', e.message); }
      }
      return;
    }

    // kNine non-preview records (already have data but not enriched)
    if (candidate.source === 'knine') {
      setSelectedCandidate(prev => prev ? ({ ...prev, loadingAttachments: false }) : null);
      return;
    }
    
    setProfileLoading(true);
    try {
      const [profileRes, attRes] = await Promise.all([
        fetch(buildApiUrl(`/api/pcr/candidates/${candidate.candidateId}/profile`), { headers: { 'Authorization': `Bearer ${getToken()}` } }),
        fetch(buildApiUrl(`/api/pcr/candidates/${candidate.candidateId}/attachments`), { headers: { 'Authorization': `Bearer ${getToken()}` } })
      ]);
      
      if (profileRes.ok) {
        const data = await profileRes.json();
        console.log('[Profile] Data:', data);
        // Extract pipeline stage from most recent submission
        const latestSubmission = data.submissions?.[0];
        const pipelineStage = latestSubmission?.status || null;
        // Extract last note from activities
        const noteActivity = (data.activities || []).find(a =>
          a.type === 'Note' || a.action === 'Note' || a.Description?.includes('note') || a.ActivityType === 'Note'
        );
        const lastNote = noteActivity?.description || noteActivity?.Description || noteActivity?.notes || null;
        setSelectedCandidate(prev => prev ? ({
          ...prev, ...data.candidate, matchScore, matchLevel, matchReasons,
          pipelineStage, lastNote,
          lastActivity: data.activities?.[0]?.date || data.activities?.[0]?.ActivityDate || prev.lastActivity
        }) : null);
      }
      
      if (attRes.ok) {
        const attData = await attRes.json();
        console.log('[Attachments]:', attData);
        setSelectedCandidate(prev => prev ? ({ ...prev, attachments: attData.attachments || attData || [], loadingAttachments: false }) : null);
      }
    } catch (e) { console.error('Profile error:', e); }
    finally { 
      setProfileLoading(false); 
      setSelectedCandidate(prev => prev ? ({ ...prev, loadingAttachments: false }) : null); 
    }
  };

  // Enrich a kNine preview from within the ProfileDrawer (user clicks Enrich button)
  const enrichFromDrawer = async (candidate) => {
    const result = await enrichKnineCandidate(candidate);
    if (result && result.enrichedData) {
      const ed = result.enrichedData;
      // Merge enriched data over preview: take enriched value when non-null, keep preview value as fallback.
      // This prevents null fields from PDL enrichment (e.g. location_name not returned) from
      // overwriting good preview values like location/city/state/company/title.
      const enrichedCandidate = {
        ...candidate,
        // Profile fields: prefer enriched, keep preview as fallback
        title: ed.title || candidate.title,
        company: ed.company || candidate.company,
        location: ed.location || candidate.location,
        city: ed.city || candidate.city,
        state: ed.state || candidate.state,
        country: ed.country || candidate.country,
        industry: ed.industry || candidate.industry,
        companySize: ed.companySize || candidate.companySize,
        companyIndustry: ed.companyIndustry || candidate.companyIndustry,
        companyWebsite: ed.companyWebsite || candidate.companyWebsite,
        linkedinUrl: ed.linkedinUrl || candidate.linkedinUrl,
        titleLevels: ed.titleLevels?.length ? ed.titleLevels : candidate.titleLevels,
        titleRole: ed.titleRole || candidate.titleRole,
        seniorityLevel: ed.seniorityLevel || candidate.seniorityLevel,
        inferredSalary: ed.inferredSalary || candidate.inferredSalary,
        // Contact fields — authoritative from enrichment only
        email: ed.email || null,
        workEmail: ed.workEmail || null,
        personalEmails: ed.personalEmails || [],
        phone: ed.phone || null,
        mobilePhone: ed.mobilePhone || null,
        phoneNumbers: ed.phoneNumbers || [],
        // Social — enrichment returns actual URLs (preview had booleans)
        facebookUrl: ed.facebookUrl || null,
        twitterUrl: ed.twitterUrl || null,
        githubUrl: ed.githubUrl || null,
        // Full enrichment-only fields
        skills: ed.skills || candidate.skills || [],
        experience: ed.experience || candidate.experience || [],
        education: ed.education || candidate.education || [],
        certifications: ed.certifications || [],
        languages: ed.languages || [],
        yearsExperience: ed.yearsExperience || candidate.yearsExperience,
        summary: ed.summary || candidate.summary,
        headline: ed.headline || candidate.headline,
        // Metadata
        enriched: true,
        enrichedData: ed,
        isPreview: false,
        matchScore: candidate.matchScore,
        matchLevel: candidate.matchLevel,
        matchReasons: candidate.matchReasons,
        source: 'knine',
        sourceSystem: 'kNine',
        dupeDetection: result.dupeDetection,
        atsConnected: result.atsConnected,
        validation: result.validation || null,
      };

      setSelectedCandidate(prev => prev ? ({
        ...enrichedCandidate,
        loadingAttachments: false,
        attachments: []
      }) : null);

      // Store enriched state back in results array so re-opening doesn't re-charge
      const updateEnriched = c =>
        (c.candidateId || c.id) === (candidate.candidateId || candidate.id) ? enrichedCandidate : c;
      setAllResults(prev => prev.map(updateEnriched));
      setResults(prev => prev.map(updateEnriched));

      // Auto dupe detection — if a match was found in ATS, show update prompt instead of create
      const existingRecord = result.dupeDetection?.candidate || candidate.existingAtsRecord;
      if (existingRecord) {
        setAtsUploadResult({
          action: 'duplicate_found',
          duplicateCandidate: existingRecord,
          changes: result.dupeDetection?.changes || [],
          message: result.dupeDetection?.message || 'This person already exists in your ATS. You can update their record with the enriched contact info.'
        });
      } else if (result.dupeDetection) {
        setAtsUploadResult(null);
      }

      // Track unsaved enrichment
      setEnrichedUnsaved(prev => new Set([...prev, candidate.candidateId || candidate.id]));
    }
  };

  const openInATS = async (candidate) => {
    if (candidate.source === 'knine' && candidate.linkedinUrl) {
      window.open(candidate.linkedinUrl, '_blank');
      return;
    }

    const token = getToken();
    if (!token) {
      knAlert('Session expired. Please log in again.', { title: 'Session Expired', variant: 'warning' });
      return;
    }

    try {
      console.log('[ATS] Fetching URL for candidate:', candidate.candidateId);
      const res = await fetch(buildApiUrl(`/api/pcr/candidates/${candidate.candidateId}/ats-url`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed to get ATS URL' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      console.log('[ATS] Response:', data);

      if (data.atsUrl) {
        console.log('[ATS] Opening:', data.atsUrl);
        window.open(data.atsUrl, '_blank');
      } else {
        knAlert('Could not generate ATS URL. Make sure you are connected to PCRecruiter.', { title: 'ATS Error', variant: 'error' });
      }
    } catch (e) {
      console.error('ATS URL error:', e);
      knAlert(`Could not open in ATS: ${e.message}`, { title: 'ATS Error', variant: 'error' });
    }
  };

  // Load an existing ATS (PCR) candidate by ID and show them in the profile drawer
  const loadAndViewATSRecord = async (candidateId) => {
    if (!candidateId) return;
    const token = getToken();
    if (!token) return;
    setProfileLoading(true);
    setAtsUploadResult(null);
    try {
      const res = await fetch(buildApiUrl(`/api/candidates/${candidateId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Shape the PCR response into the candidate format the drawer expects
      const atsCand = {
        ...data,
        candidateId: data.candidateId || candidateId,
        source: data.source || 'database',
        sourceSystem: data.sourceSystem || 'ATS',
        attachments: data.attachments || data.Attachments || [],
        loadingAttachments: false,
        matchScore: data.matchScore || 50,
        matchLevel: data.matchLevel || 'Good'
      };
      setSelectedCandidate(atsCand);
    } catch (e) {
      knAlert(`Could not load ATS record: ${e.message}`, { title: 'Error', variant: 'error' });
    } finally {
      setProfileLoading(false);
    }
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  
  return (
    <div style={{ position: 'relative' }}>
      {/* Unsaved enrichment warning banner */}
      {enrichedUnsaved.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
          marginBottom: 12, borderRadius: 10,
          background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.3)'
        }}>
          <AlertCircle size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: '#f59e0b', fontWeight: 500, flex: 1 }}>
            You have {enrichedUnsaved.size} enriched candidate{enrichedUnsaved.size > 1 ? 's' : ''} with unsaved data. Credits have been charged — save or export before leaving.
          </span>
        </div>
      )}

      {/* No integration banner */}
      {!hasIntegration && (
        <div style={{
          marginBottom: 20, borderRadius: 14, overflow: 'hidden',
          border: '1px solid rgba(99,102,241,0.3)',
          background: colors.darkMode ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.04)'
        }}>
          <div style={{
            padding: '10px 16px',
            background: 'linear-gradient(135deg, #1565C0, #1E88E5)',
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            <Link2 size={16} style={{ color: '#fff' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: 0.3 }}>
              NO DATABASE CONNECTED
            </span>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 4 }}>
                Connect your ATS or CRM to search your live candidate data
              </div>
              <div style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
                Roxi can search your existing database alongside kNine records — all ranked together by AI. Connect your system in{' '}
                <a href="/settings/integrations" style={{ color: '#1565C0', fontWeight: 600, textDecoration: 'none' }}>
                  Settings → Integrations
                </a>{' '}
                to get started.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <a href="/settings/integrations" style={{ textDecoration: 'none' }}>
                <button style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 8, border: `1px solid ${colors.border}`,
                  background: colors.bg, color: colors.text,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer'
                }}>
                  <Settings size={14} />
                  Set Up Integration
                </button>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* INITIAL SEARCH */}
      {!activeSearch && (
        <Card style={{ padding: 24, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: `linear-gradient(135deg, ${colors.accent}, #1565C0)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Brain size={22} style={{ color: '#fff' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: colors.text }}>Roxi Search</h1>
              <p style={{ margin: 0, fontSize: 13, color: colors.textSecondary }}>
                Describe who you're looking for below or select a{' '}
                <button
                  onClick={() => setShowJobSelector(true)}
                  type="button"
                  style={{
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
                    color: '#fff',
                    border: 'none',
                    padding: '3px 10px',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    verticalAlign: 'middle'
                  }}
                >
                  <Briefcase size={12} /> Job
                </button>
              </p>
            </div>
          </div>

          <form onSubmit={handleSearch}>
            <div style={{
              display: 'flex', alignItems: 'center',
              background: colors.bgInput,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              padding: '4px 4px 4px 14px',
              marginBottom: 14
            }}>
              <Wand2 size={18} style={{ color: colors.accent, marginRight: 10, flexShrink: 0 }} />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Try: 'senior sales managers in Ohio with CRM experience'"
                style={{ flex: 1, padding: '12px 0', border: 'none', background: 'transparent', fontSize: 14, color: colors.text, outline: 'none' }}
              />
              <Button variant="ai" icon={Search} loading={loading} onClick={handleSearch}>Search</Button>
            </div>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            {/* Source pills — equal size, animated, ATS locked on trial */}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 4, padding: 3, borderRadius: 8, background: colors.bgMuted }}>
              <style>{`
                @keyframes kn-ats-pulse {
                  0%,100% { box-shadow: 0 0 0 0 rgba(10,102,194,0.35); }
                  50%      { box-shadow: 0 0 0 5px rgba(10,102,194,0); }
                }
                @keyframes kn-knine-pulse {
                  0%,100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.35); }
                  50%      { box-shadow: 0 0 0 5px rgba(16,185,129,0); }
                }
                .kn-pill-ats   { animation: kn-ats-pulse   2.4s ease-in-out infinite; }
                .kn-pill-knine { animation: kn-knine-pulse  2.4s ease-in-out infinite; animation-delay: 0.8s; }
              `}</style>

              {/* ATS pill */}
              <button
                className={sources.ats && !isTrial ? 'kn-pill-ats' : ''}
                onClick={() => {
                  if (isTrial) {
                    setShowAtsUpgradeHint(h => !h);
                    return;
                  }
                  setSources({ ...sources, ats: !sources.ats });
                }}
                title={isTrial ? 'Upgrade to search your ATS database' : 'Toggle ATS candidates'}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  padding: '6px 0', width: 86, borderRadius: 6, border: 'none',
                  background: isTrial ? 'transparent' : (sources.ats ? colors.bgAccent : 'transparent'),
                  color: isTrial ? colors.textMuted : (sources.ats ? colors.primary : colors.textMuted),
                  fontSize: 12, fontWeight: 600,
                  cursor: isTrial ? 'not-allowed' : 'pointer',
                  opacity: isTrial ? 0.5 : 1, transition: 'all 0.2s'
                }}
              >
                <Database size={13} />
                ATS
                {isTrial ? <Lock size={10} /> : (sources.ats && <CheckCircle size={11} />)}
              </button>

              {/* kNine pill */}
              <button
                className={sources.knine ? 'kn-pill-knine' : ''}
                onClick={() => setSources({ ...sources, knine: !sources.knine })}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  padding: '6px 0', width: 86, borderRadius: 6, border: 'none',
                  background: sources.knine ? colors.bgSuccess : 'transparent',
                  color: sources.knine ? colors.success : colors.textMuted,
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
                }}
              >
                <Globe size={13} />
                kNine
                {sources.knine && <CheckCircle size={11} />}
              </button>

              {/* Trial upgrade tooltip */}
              {showAtsUpgradeHint && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 200,
                  background: '#1e293b', color: '#fff', fontSize: 12, fontWeight: 500,
                  padding: '10px 14px', borderRadius: 10, whiteSpace: 'nowrap',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  border: '1px solid rgba(99,102,241,0.4)',
                }}>
                  🔒 Upgrade your account to search across your ATS database
                  <br />
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    <a href="mailto:support@knine.ai?subject=Upgrade" style={{ color: '#42A5F5', textDecoration: 'none' }}>Contact us to upgrade →</a>
                  </span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setShowSearchHistory(!showSearchHistory)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 6,
                  border: `1px solid ${colors.border}`, background: 'transparent',
                  color: colors.textSecondary, fontSize: 12, fontWeight: 600, cursor: 'pointer'
                }}
              >
                <Clock size={13} /> History
              </button>
              <button
                onClick={() => setShowAiSettings(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 6,
                  border: `1px solid ${colors.border}`, background: 'transparent',
                  color: colors.textSecondary, fontSize: 12, fontWeight: 600, cursor: 'pointer'
                }}
              >
                <Lightbulb size={13} /> AI Settings
              </button>
            </div>
          </div>

          {/* Search History Panel */}
          {showSearchHistory && (
            <div style={{ marginTop: 16, padding: 16, background: colors.bgMuted, borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>Recent Searches</span>
                <button onClick={() => setShowSearchHistory(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted }}>
                  <X size={16} />
                </button>
              </div>
              {searchHistory.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: colors.textMuted, fontSize: 13 }}>
                  No recent searches yet
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {searchHistory.slice(0, 8).map((h, idx) => (
                    <button
                      key={h.id || idx}
                      onClick={() => { setQuery(h.searchQuery); setShowSearchHistory(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 12px', borderRadius: 8,
                        background: colors.bgCard, border: `1px solid ${colors.border}`,
                        cursor: 'pointer', textAlign: 'left'
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: colors.text, marginBottom: 2 }}>
                          {h.searchQuery.length > 50 ? h.searchQuery.slice(0, 50) + '...' : h.searchQuery}
                        </div>
                        <div style={{ fontSize: 11, color: colors.textMuted }}>
                          {h.resultsCount || 0} results • {new Date(h.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <Search size={14} style={{ color: colors.textMuted }} />
                    </button>
                  ))}
                </div>
              )}
              {savedSearches.length > 0 && (
                <>
                  <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600, fontSize: 13, color: colors.textSecondary }}>
                    <Star size={12} style={{ marginRight: 4 }} /> Saved Searches
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {savedSearches.slice(0, 5).map((s, idx) => (
                      <button
                        key={s.id || idx}
                        onClick={() => { runSavedSearch(s); setShowSearchHistory(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 12px', borderRadius: 8,
                          background: colors.bgAccent, border: 'none',
                          cursor: 'pointer', color: colors.primary, fontSize: 13, fontWeight: 500
                        }}
                      >
                        <Star size={12} /> {s.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </Card>
      )}

      {/* ACTIVE SEARCH HEADER */}
      {activeSearch && (
        <Card style={{ padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${colors.accent}, #1565C0)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Brain size={16} style={{ color: '#fff' }} />
              </div>
              <div>
                <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase', fontWeight: 600 }}>Active Search</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>"{activeSearch}"</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {creditsRemaining !== null && <Badge variant="ai"><Coins size={11} /> {creditsRemaining} credits</Badge>}
              <Button variant="ghost" size="sm" icon={Star} onClick={saveCurrentSearch}>Save</Button>
              <Button variant="ghost" size="sm" icon={RefreshCw} onClick={startOver}>Start Over</Button>
            </div>
          </div>
        </Card>
      )}

      {/* AI Interpretation + Kubi AI Optimized Filters */}
      {aiInterpretation && activeSearch && !loading && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Zap size={16} style={{ color: colors.accent }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: colors.accent }}>Roxi AI Analysis</span>
            <span style={{ fontSize: 12, color: colors.textMuted, marginLeft: 'auto' }}>
              {kninePoolSize > 0
                ? `top ${results.length} of ${kninePoolSize} scored${atsCount > 0 ? ` + ${formatCount(atsCount)} ATS` : ''}`
                : knineTotal > 0 || atsCount > 0
                  ? `${results.length} of ${formatCount(knineTotal)} kNine${atsCount > 0 ? ` + ${formatCount(atsCount)} ATS` : ''}`
                  : `${results.length} of ${allResults.length} results`
              }
            </span>
          </div>

          {aiInterpretation.summary && <p style={{ margin: '0 0 10px', fontSize: 13, color: colors.text, lineHeight: 1.5 }}>{aiInterpretation.summary}</p>}

          {/* Kubi AI Optimized Filters */}
          {discoveryCriteria.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <button
                onClick={() => setShowDiscovery(p => !p)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', marginBottom: 6
                }}
              >
                <Sparkles size={13} style={{ color: colors.accent }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: colors.accent, textTransform: 'uppercase', letterSpacing: 0.5 }}>AI Optimized Filters</span>
                {showDiscovery ? <ChevronUp size={14} style={{ color: colors.textMuted }} /> : <ChevronDown size={14} style={{ color: colors.textMuted }} />}
              </button>

              {showDiscovery && (
                <div style={{
                  padding: 12, borderRadius: 8,
                  background: colors.bgMuted, border: `1px solid ${colors.border}`
                }}>
                  {discoveryCriteria.map((c, idx) => {
                    const weightColor = c.weight === 'required' ? colors.accent
                      : c.weight === 'preferred' ? '#3b82f6' : colors.textMuted;
                    const weightLabel = c.weight === 'required' ? 'REQUIRED'
                      : c.weight === 'preferred' ? 'PREFERRED' : 'NICE TO HAVE';
                    const isExpanded = expandedCriteria[c.category];
                    const displayValues = c.category === 'job_title'
                      ? (isExpanded ? c.values : [c.primary, ...(c.values || []).filter(v => v !== c.primary).slice(0, 2)])
                      : (isExpanded ? c.values : c.values.slice(0, 3));
                    const hiddenCount = c.category === 'job_title'
                      ? Math.max(0, (c.values || []).length - 3)
                      : Math.max(0, c.values.length - 3);

                    const cycleWeight = () => {
                      const order = ['required', 'preferred', 'nice_to_have'];
                      const currentIdx = order.indexOf(c.weight);
                      const nextWeight = order[(currentIdx + 1) % order.length];
                      setDiscoveryCriteria(prev => prev.map(cr =>
                        cr.category === c.category ? { ...cr, weight: nextWeight } : cr
                      ));
                      setCriteriaModified(true);
                      setCriteriaChipRemoved(true); // triggers auto-refresh
                      // Save preference for AI learning
                      try {
                        fetch(buildApiUrl('/api/search/filter-preference'), {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
                          body: JSON.stringify({ category: c.category, weight: nextWeight, label: c.label, values: c.values })
                        }).catch(() => {});
                      } catch (e) { /* fire and forget */ }
                    };

                    const addValueToCategory = (val) => {
                      if (!val.trim()) return;
                      setDiscoveryCriteria(prev => prev.map(cr =>
                        cr.category === c.category
                          ? { ...cr, values: [...cr.values, val.trim()] }
                          : cr
                      ));
                      setCriteriaModified(true);
                      setAddValueInput('');
                      setAddingToCategory(null);
                    };

                    return (
                      <div key={idx} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8,
                        padding: '7px 0',
                        borderBottom: idx < discoveryCriteria.length - 1 ? `1px solid ${colors.border}` : 'none'
                      }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, color: colors.textSecondary,
                          minWidth: 110, paddingTop: 3, flexShrink: 0
                        }}>{c.label}:</span>
                        <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                          {displayValues.map((v, vi) => (
                            <span key={vi} style={{
                              padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                              background: c.weight === 'required' ? colors.bgAccent
                                : c.weight === 'preferred' ? 'rgba(59,130,246,0.1)' : colors.bgMuted,
                              color: c.weight === 'required' ? colors.accent
                                : c.weight === 'preferred' ? '#3b82f6' : colors.textSecondary,
                              border: `1px solid ${c.weight === 'required' ? colors.accent + '30'
                                : c.weight === 'preferred' ? 'rgba(59,130,246,0.25)' : colors.border}`,
                              transition: 'all 0.15s'
                            }}
                              title="Click to remove"
                              onClick={() => {
                                setDiscoveryCriteria(prev => prev.map(cr => {
                                  if (cr.category !== c.category) return cr;
                                  const removeIdx = cr.values.indexOf(v);
                                  const newValues = cr.values.filter((val, vidx) => vidx !== removeIdx);
                                  // Keep `raw` (structured data sent to the backend) in sync with
                                  // `values` (display strings) — they're parallel arrays for
                                  // categories like location, education, companies, etc.
                                  const newRaw = Array.isArray(cr.raw) && cr.raw.length === cr.values.length
                                    ? cr.raw.filter((rv, ridx) => ridx !== removeIdx)
                                    : cr.raw;
                                  return { ...cr, values: newValues, raw: newRaw };
                                }).filter(cr => cr.values.length > 0));
                                setCriteriaModified(true);
                                setCriteriaChipRemoved(true);
                              }}
                            >
                              {v}
                              <X size={10} style={{ opacity: 0.5 }} />
                            </span>
                          ))}
                          {!isExpanded && hiddenCount > 0 && (
                            <button
                              onClick={() => setExpandedCriteria(p => ({ ...p, [c.category]: true }))}
                              style={{
                                padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                color: colors.accent, background: 'transparent', border: `1px dashed ${colors.accent}40`,
                                cursor: 'pointer'
                              }}
                            >+{hiddenCount} more</button>
                          )}
                          {isExpanded && hiddenCount > 0 && (
                            <button
                              onClick={() => setExpandedCriteria(p => ({ ...p, [c.category]: false }))}
                              style={{
                                padding: '3px 8px', borderRadius: 6, fontSize: 11,
                                color: colors.textMuted, background: 'transparent', border: 'none',
                                cursor: 'pointer', textDecoration: 'underline'
                              }}
                            >collapse</button>
                          )}
                          {/* Add value button / inline input with suggestions */}
                          {addingToCategory === c.category ? (
                            <div style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column' }}>
                              <form onSubmit={(e) => { e.preventDefault(); addValueToCategory(addValueInput); setShowSuggestions(false); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                <input
                                  autoFocus
                                  value={addValueInput}
                                  onChange={e => { setAddValueInput(e.target.value); setShowSuggestions(true); }}
                                  onFocus={() => setShowSuggestions(true)}
                                  onBlur={() => { setTimeout(() => { if (!addValueInput.trim()) { setAddingToCategory(null); setAddValueInput(''); } setShowSuggestions(false); }, 200); }}
                                  onKeyDown={e => { if (e.key === 'Escape') { setAddingToCategory(null); setAddValueInput(''); setShowSuggestions(false); } }}
                                  placeholder={`Add ${c.label.toLowerCase()}...`}
                                  style={{
                                    padding: '3px 8px', borderRadius: 6, fontSize: 11, width: 150,
                                    border: `1px solid ${colors.accent}`, background: colors.bgCard,
                                    color: colors.text, outline: 'none'
                                  }}
                                />
                                <button type="submit" style={{ padding: '2px 6px', borderRadius: 4, border: 'none', background: colors.accent, color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 700 }}>+</button>
                              </form>
                              {showSuggestions && (() => {
                                const suggestions = (PDL_SUGGESTIONS[c.category] || [])
                                  .filter(s => !c.values.some(v => v.toLowerCase() === s.toLowerCase()))
                                  .filter(s => !addValueInput || s.toLowerCase().includes(addValueInput.toLowerCase()))
                                  .slice(0, 8);
                                if (suggestions.length === 0) return null;
                                return (
                                  <div style={{
                                    position: 'absolute', top: '100%', left: 0, zIndex: 50, marginTop: 2,
                                    background: colors.bgCard, border: `1px solid ${colors.border}`,
                                    borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                    maxHeight: 180, overflowY: 'auto', minWidth: 160
                                  }}>
                                    {suggestions.map((s, si) => (
                                      <button
                                        key={si}
                                        onMouseDown={(e) => { e.preventDefault(); addValueToCategory(s); setShowSuggestions(false); }}
                                        style={{
                                          display: 'block', width: '100%', textAlign: 'left',
                                          padding: '6px 10px', border: 'none', cursor: 'pointer',
                                          background: 'transparent', color: colors.text, fontSize: 11,
                                          borderBottom: si < suggestions.length - 1 ? `1px solid ${colors.border}` : 'none'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = colors.bgMuted}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                      >{s}</button>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          ) : (
                            <button
                              onClick={() => { setAddingToCategory(c.category); setAddValueInput(''); setShowSuggestions(true); }}
                              style={{
                                padding: '3px 7px', borderRadius: 6, fontSize: 11,
                                color: colors.accent, background: 'transparent', border: `1px dashed ${colors.accent}50`,
                                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 2
                              }}
                              title={`Add a ${c.label.toLowerCase()}`}
                            >
                              <Plus size={10} /> Add
                            </button>
                          )}
                        </div>
                        {/* Clickable weight badge - cycles on click */}
                        <button
                          onClick={cycleWeight}
                          title="Click to change priority"
                          style={{
                            fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                            padding: '3px 10px', borderRadius: 4,
                            color: weightColor, background: weightColor + '15',
                            border: `1px solid ${weightColor}30`, whiteSpace: 'nowrap',
                            cursor: 'pointer', transition: 'all 0.2s'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = `0 0 8px ${weightColor}30`; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
                        >{weightLabel}{c.relaxedNote ? ' ~' : ''}</button>
                        {c.relaxedNote && (
                          <span style={{ fontSize: 9, color: colors.textMuted, marginLeft: 2 }}
                            title={c.relaxedNote}>auto-relaxed</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Refresh Results — appears when user modifies criteria after a search */}
          {criteriaModified && allResults.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <button
                onClick={handleRefreshResults}
                disabled={loading}
                style={{
                  width: '100%', padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark || colors.accent})`,
                  color: '#fff', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                  boxShadow: `0 2px 10px ${colors.accent}40`,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
              >
                <RefreshCw size={14} className={loading ? 'spin' : ''} />
                Refresh Results
              </button>
              <p style={{ fontSize: 10, color: colors.textMuted, textAlign: 'center', margin: '4px 0 0' }}>
                Re-search with your updated criteria
              </p>
            </div>
          )}

          {/* Source toggle: My Database / kNine */}
          {allResults.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <button
                onClick={() => setAppliedFilters(f => ({ ...f, sourceFilter: f.sourceFilter === 'ats' ? null : 'ats' }))}
                style={{
                  padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: appliedFilters.sourceFilter === 'ats' ? 'rgba(249,115,22,0.15)' : colors.bgCard,
                  color: appliedFilters.sourceFilter === 'ats' ? '#ea580c' : colors.textSecondary,
                  border: `1px solid ${appliedFilters.sourceFilter === 'ats' ? '#ea580c' : colors.border}`
                }}
              >
                <Database size={12} /> My Database ({atsCount > 0 ? formatCount(atsCount) : allResults.filter(c => c.source === 'ats').length})
              </button>
              {(() => {
                const knineLoaded = allResults.filter(c => c.source === 'knine').length;
                // knineTotal is PDL's total matching pool (can be far larger than what's
                // actually loaded into allResults) — show the loaded count on the pill so
                // toggling to this tab doesn't surprise the user with "filters too narrow",
                // and surface the larger pool size as a tooltip hint instead.
                const poolHint = knineTotal > knineLoaded ? ` — ${formatCount(knineTotal)} available in kNine's pool` : '';
                return (
                  <button
                    onClick={() => setAppliedFilters(f => ({ ...f, sourceFilter: f.sourceFilter === 'knine' ? null : 'knine' }))}
                    title={`${knineLoaded} kNine candidate${knineLoaded === 1 ? '' : 's'} loaded${poolHint}`}
                    style={{
                      padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 5,
                      background: appliedFilters.sourceFilter === 'knine' ? 'rgba(16,185,129,0.15)' : colors.bgCard,
                      color: appliedFilters.sourceFilter === 'knine' ? '#059669' : colors.textSecondary,
                      border: `1px solid ${appliedFilters.sourceFilter === 'knine' ? '#059669' : colors.border}`
                    }}
                  >
                    <Globe size={12} /> kNine ({formatCount(knineLoaded)}{knineTotal > knineLoaded ? ` of ${formatCount(knineTotal)}` : ''})
                  </button>
                );
              })()}
              {appliedFilters.sourceFilter && (
                <button
                  onClick={() => setAppliedFilters(f => ({ ...f, sourceFilter: null }))}
                  style={{ padding: '4px 8px', borderRadius: 6, fontSize: 10, cursor: 'pointer', background: 'transparent', border: 'none', color: colors.textMuted, textDecoration: 'underline' }}
                >Show All</button>
              )}
            </div>
          )}

          {/* Quick filter suggestions */}
          {aiInterpretation.suggestions?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {aiInterpretation.suggestions.map((s, i) => (
                <Badge key={i} variant="suggestion" size="sm" onClick={() => applySuggestion(s)} style={{ cursor: 'pointer' }}>
                  <Target size={10} /> {s.label}
                </Badge>
              ))}
            </div>
          )}

          {/* Refinement input */}
          <div style={{ paddingTop: 12, borderTop: `1px solid ${colors.border}` }}>
            <form onSubmit={handleRefinement} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Lightbulb size={14} style={{ color: colors.accent, flexShrink: 0 }} />
              <input
                type="text"
                value={refinementQuery}
                onChange={e => setRefinementQuery(e.target.value)}
                placeholder="Filter: 'only in Cleveland', '5+ years', 'with email', 'only knine'..."
                style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.bgInput, fontSize: 13, color: colors.text, outline: 'none' }}
              />
              <Button variant="ai" size="sm" icon={Wand2} disabled={!refinementQuery.trim()} onClick={handleRefinement}>Filter</Button>
            </form>
          </div>
        </Card>
      )}

      {/* Loading */}
      {loading && searchSteps.length > 0 && (
        <Card style={{ padding: 20, marginBottom: 16 }}>
          {searchSteps.map((s, i) => (
            <div key={i} className="slide-in-up" style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: i === searchSteps.length - 1 ? 1 : 0.5, marginBottom: i < searchSteps.length - 1 ? 10 : 0 }}>
              {i === searchSteps.length - 1 ? <Loader2 size={16} className="spin" style={{ color: colors.accent }} /> : <CheckCircle size={16} style={{ color: colors.success }} />}
              <s.icon size={14} style={{ color: colors.textMuted }} />
              <span style={{ fontSize: 13, color: colors.text }}>{s.text}</span>
            </div>
          ))}
        </Card>
      )}

      {/* Error */}
      {error && (
        <Card style={{ padding: 14, marginBottom: 16, background: colors.bgDanger, borderColor: colors.danger }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertCircle size={18} style={{ color: colors.danger }} />
            <span style={{ color: colors.danger, flex: 1, fontSize: 13 }}>{error}</span>
            <Button variant="ghost" size="sm" onClick={startOver}>Try Again</Button>
          </div>
        </Card>
      )}

      {/* ACTIVE FILTERS BAR */}
      {!loading && searched && results.length >= 0 && getActiveFilterCount() > 0 && (
        <div style={{ 
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          marginBottom: 12, padding: '10px 14px', borderRadius: 10,
          background: colors.bgAccent, border: `1px solid ${colors.accent}40`
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: colors.accent, textTransform: 'uppercase' }}>
            Filters ({getActiveFilterCount()}):
          </span>
          
          {appliedFilters.hasResume === true && (
            <span style={{ 
              display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
              background: colors.bgCard, border: `1px solid ${colors.border}`, fontSize: 11, color: colors.text
            }}>
              With Resume
              <X size={12} style={{ cursor: 'pointer', color: colors.textMuted }} onClick={() => clearFilter('hasResume')} />
            </span>
          )}
          {appliedFilters.cityMatch === true && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
              background: colors.bgCard, border: `1px solid ${colors.border}`, fontSize: 11, color: colors.text
            }}>
              City Match Verified
              <X size={12} style={{ cursor: 'pointer', color: colors.textMuted }} onClick={() => clearFilter('cityMatch')} />
            </span>
          )}

          {appliedFilters.minExperience !== null && (
            <span style={{ 
              display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
              background: colors.bgCard, border: `1px solid ${colors.border}`, fontSize: 11, color: colors.text
            }}>
              {appliedFilters.maxExperience ? `${appliedFilters.minExperience}-${appliedFilters.maxExperience}` : `${appliedFilters.minExperience}+`} years
              <X size={12} style={{ cursor: 'pointer', color: colors.textMuted }} onClick={() => { clearFilter('minExperience'); clearFilter('maxExperience'); }} />
            </span>
          )}
          
          {appliedFilters.locations.map(loc => (
            <span key={loc} style={{ 
              display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
              background: colors.bgCard, border: `1px solid ${colors.border}`, fontSize: 11, color: colors.text
            }}>
              In {loc}
              <X size={12} style={{ cursor: 'pointer', color: colors.textMuted }} onClick={() => setAppliedFilters(f => ({ ...f, locations: f.locations.filter(l => l !== loc) }))} />
            </span>
          ))}
          
          {appliedFilters.minMatchScore !== null && (
            <span style={{ 
              display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
              background: colors.bgCard, border: `1px solid ${colors.border}`, fontSize: 11, color: colors.text
            }}>
              {appliedFilters.minMatchScore}%+ Match
              <X size={12} style={{ cursor: 'pointer', color: colors.textMuted }} onClick={() => clearFilter('minMatchScore')} />
            </span>
          )}
          
          {appliedFilters.hasEmail === true && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
              background: colors.bgCard, border: `1px solid ${colors.border}`, fontSize: 11, color: colors.text
            }}>
              Has Email
              <X size={12} style={{ cursor: 'pointer', color: colors.textMuted }} onClick={() => clearFilter('hasEmail')} />
            </span>
          )}

          {appliedFilters.noEmail === true && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
              background: 'rgba(239, 68, 68, 0.1)', border: `1px solid rgba(239, 68, 68, 0.3)`, fontSize: 11, color: colors.text
            }}>
              No Email
              <X size={12} style={{ cursor: 'pointer', color: colors.textMuted }} onClick={() => clearFilter('noEmail')} />
            </span>
          )}

          {appliedFilters.hasLinkedin === true && (
            <span style={{ 
              display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
              background: colors.bgCard, border: `1px solid ${colors.border}`, fontSize: 11, color: colors.text
            }}>
              Has LinkedIn
              <X size={12} style={{ cursor: 'pointer', color: colors.textMuted }} onClick={() => clearFilter('hasLinkedin')} />
            </span>
          )}
          
          <button 
            onClick={clearAllFilters}
            style={{ 
              marginLeft: 'auto', padding: '4px 8px', borderRadius: 6, border: 'none',
              background: 'transparent', color: colors.accent, fontSize: 11, cursor: 'pointer', fontWeight: 500
            }}
          >
            Clear All
          </button>
        </div>
      )}

      {/* Results Header with Bulk Actions */}
      {!loading && searched && results.length > 0 && results.length < 50 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
          padding: '10px 14px', borderRadius: 10,
          background: colors.bgWarning, border: `1px solid ${colors.warning}40`
        }}>
          <Lightbulb size={16} style={{ color: colors.warning, flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: colors.text, flex: 1 }}>
            Showing {results.length} result{results.length === 1 ? '' : 's'} — broaden your filters or fetch more candidates to see additional matches.
          </span>
          {getActiveFilterCount() > 0 && (
            <Button variant="secondary" size="sm" icon={X} onClick={clearAllFilters}>Clear Filters</Button>
          )}
          <Button variant="ai" size="sm" icon={Search} onClick={() => handleSearch(null, false)}>Fetch More</Button>
        </div>
      )}

      {!loading && searched && results.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button 
              onClick={toggleSelectAll}
              style={{ 
                display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, 
                border: `1px solid ${colors.border}`, background: selectedIds.size > 0 ? colors.bgAccent : colors.bgCard,
                color: colors.textSecondary, fontSize: 12, cursor: 'pointer'
              }}
            >
              {selectedIds.size === results.length ? <CheckSquare size={14} /> : <Square size={14} />}
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
            </button>
            
            {selectedIds.size > 0 && (
              <>
                <Button variant="secondary" size="sm" icon={Plus} onClick={openBulkAddToList}>Add to List</Button>
                <Button variant="secondary" size="sm" icon={Send} onClick={openBulkSubmitToPosition}>Submit</Button>
                <Button variant="secondary" size="sm" icon={Sparkles} onClick={handleBulkEnrich} loading={enriching}>Enrich</Button>
                {results.some(c => selectedIds.has(c.candidateId) && c.source === 'knine' && c.isPreview && !c.enriched) && (
                  <Button variant="ai" size="sm" icon={Coins} onClick={handleBulkKnineEnrich} loading={enriching}>kNine Enrich</Button>
                )}
              </>
            )}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: colors.textMuted }}>
              {getActiveFilterCount() > 0
                ? `${results.length} of ${allResults.length} (filtered)`
                : knineTotal > 0 || atsCount > 0
                  ? `${results.length} of ${formatCount(knineTotal)} kNine${atsCount > 0 ? ` + ${formatCount(atsCount)} ATS` : ''}`
                  : `${results.length} results`
              }
            </span>
            <select 
              value={sortBy} 
              onChange={e => setSortBy(e.target.value)}
              style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.bgInput, color: colors.text, fontSize: 11, cursor: 'pointer' }}
            >
              <option value="score">Match</option>
              <option value="name">Name</option>
              <option value="experience">Experience</option>
              <option value="date">Last Updated</option>
            </select>
            <button 
              onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
              style={{ padding: 5, borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.bgInput, cursor: 'pointer', display: 'flex' }}
            >
              {sortDir === 'desc' ? <SortDesc size={14} style={{ color: colors.textMuted }} /> : <SortAsc size={14} style={{ color: colors.textMuted }} />}
            </button>
            <Button variant="ghost" size="sm" icon={Download} onClick={() => {
              // Export current visible results as CSV
              const rows = sortedResults.map(c => ({
                'Name': c.fullName || '',
                'Title': c.title || '',
                'Company': c.company || '',
                'Location': c.location || [c.city, c.state].filter(Boolean).join(', ') || '',
                'Email': c.email || '',
                'Phone': c.phone || '',
                'LinkedIn': c.linkedinUrl || '',
                'Source': c.source === 'knine' ? 'kNine' : 'ATS',
                'Match': `${c.matchScore || 0}%`,
                'Industry': c.industry || '',
                'Experience': c.yearsExperience ? `${c.yearsExperience} yrs` : ''
              }));
              if (rows.length === 0) return;
              const headers = Object.keys(rows[0]);
              const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${String(r[h]).replace(/"/g, '""')}"`).join(','))].join('\n');
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `kubi_search_${new Date().toISOString().slice(0,10)}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }}>Export</Button>
          </div>
        </div>
      )}

      {/* Results — all fetched candidates ranked by score */}
      {!loading && searched && sortedResults.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sortedResults.map((c) => (
            <CandidateCard
              key={c.candidateId}
              candidate={c}
              selected={selectedIds.has(c.candidateId)}
              onSelect={() => toggleSelect(c.candidateId)}
              onView={() => openProfile(c)}
              onAddToList={() => openSingleAddToList(c)}
              onSubmitToPosition={() => openSingleSubmitToPosition(c)}
              onOpenInATS={() => openInATS(c)}
              onDismiss={c.source === 'knine' ? () => dismissKnineCandidate(c) : null}
            />
          ))}
        </div>
      )}

      {/* Source error notice (partial failure — some sources returned results, others failed) */}
      {!loading && searched && results.length > 0 && sourceErrors.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {sourceErrors.map((se, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
              marginBottom: 6, borderRadius: 8,
              background: 'rgba(245, 158, 11, 0.07)', border: '1px solid rgba(245, 158, 11, 0.2)'
            }}>
              <AlertCircle size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: colors.textSecondary }}>
                <strong style={{ color: '#f59e0b' }}>
                  {se.source === 'knine' ? 'kNine' : se.source === 'ats' ? 'ATS' : se.source} unavailable
                </strong>
                {' — '}
                {se.error?.includes('No ATS') || se.error?.includes('No PCR')
                  ? <>Connect your ATS in <a href="/settings/integrations" style={{ color: colors.primary, fontWeight: 600 }}>Settings → Integrations</a> to search your candidate database.</>
                  : se.error?.includes('not configured') || se.error?.includes('API key')
                    ? 'This search source is not configured. Results may be incomplete.'
                    : 'Results from this source are unavailable and may be incomplete.'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Load More kNine Results */}
      {!loading && results.length > 0 && knineScrollToken && sources.knine &&
       appliedFilters.sourceFilter !== 'ats' && (
        <div style={{ textAlign: 'center', padding: '16px 0 4px' }}>
          <button
            onClick={loadMoreKnine}
            disabled={loadingMoreKnine}
            style={{
              padding: '9px 22px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: colors.bgCard, color: colors.accent,
              border: `1px solid ${colors.accent}40`, cursor: loadingMoreKnine ? 'not-allowed' : 'pointer',
              opacity: loadingMoreKnine ? 0.7 : 1, transition: 'all 0.2s'
            }}
            onMouseEnter={e => { if (!loadingMoreKnine) e.currentTarget.style.background = colors.bgAccent; }}
            onMouseLeave={e => { e.currentTarget.style.background = colors.bgCard; }}
          >
            {loadingMoreKnine
              ? <><Loader2 size={13} className="spin" /> Loading more...</>
              : <><Globe size={13} /> Load More</>}
          </button>
        </div>
      )}

      {/* Empty States */}
      {!loading && searched && results.length === 0 && !error && allResults.length > 0 && (() => {
        // Special-case: a source tab (My Database / kNine) is selected and is
        // the reason results look empty — that's not "filters too narrow" in
        // the usual sense (no criteria match), it's that this source's loaded
        // batch doesn't have matches. Give source-aware guidance instead.
        const sf = appliedFilters.sourceFilter;
        if (sf) {
          const sourceLoaded = allResults.filter(c => c.source === sf).length;
          const sourceLabel = sf === 'knine' ? 'kNine' : 'My Database';
          const hasOtherFilters = getActiveFilterCount() > 0;

          if (sourceLoaded === 0) {
            return (
              <Card style={{ padding: 24, textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: colors.bgAccent, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <Globe size={24} style={{ color: colors.accent }} />
                </div>
                <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: colors.text }}>No {sourceLabel} candidates loaded yet</h3>
                <p style={{ margin: '0 0 16px', color: colors.textSecondary, fontSize: 14 }}>
                  {sf === 'knine' && knineTotal > 0
                    ? `kNine has up to ${formatCount(knineTotal)} matching candidates, but none of the ${allResults.length} loaded results are from kNine. Load more to pull additional kNine candidates.`
                    : `None of the ${allResults.length} loaded results are from ${sourceLabel}.`}
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <Button variant="secondary" icon={X} onClick={() => setAppliedFilters(f => ({ ...f, sourceFilter: null }))}>Show All Sources</Button>
                  {sf === 'knine' && knineScrollToken && (
                    <Button variant="ai" icon={Globe} onClick={loadMoreKnine} disabled={loadingMoreKnine}>
                      {loadingMoreKnine ? 'Loading…' : 'Load More kNine'}
                    </Button>
                  )}
                </div>
              </Card>
            );
          }

          if (hasOtherFilters) {
            return (
              <Card style={{ padding: 24, textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: colors.bgWarning, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <Target size={24} style={{ color: colors.warning }} />
                </div>
                <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: colors.text }}>Your other filters hid all {formatCount(sourceLoaded)} {sourceLabel} candidates</h3>
                <p style={{ margin: '0 0 16px', color: colors.textSecondary, fontSize: 14 }}>
                  {formatCount(sourceLoaded)} {sourceLabel} candidates are loaded, but your active filters (e.g. resume, experience, match score) excluded all of them. Clear those filters to see the {sourceLabel} results, or show all sources.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <Button variant="secondary" icon={X} onClick={() => setAppliedFilters(f => ({ ...defaultFilters, sourceFilter: f.sourceFilter }))}>Clear Other Filters</Button>
                  <Button variant="ghost" icon={X} onClick={() => setAppliedFilters(f => ({ ...f, sourceFilter: null }))}>Show All Sources</Button>
                </div>
              </Card>
            );
          }
        }
        return (
          <Card style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: colors.bgWarning, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Target size={24} style={{ color: colors.warning }} />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: colors.text }}>Filters too narrow</h3>
            <p style={{ margin: '0 0 16px', color: colors.textSecondary, fontSize: 14 }}>
              Your current filters removed all {allResults.length} candidates. Try adjusting your filters or fetch more results.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <Button variant="secondary" icon={X} onClick={clearAllFilters}>Clear All Filters</Button>
              <Button variant="ai" icon={Search} onClick={() => {
                clearAllFilters();
                handleSearch(null, false);
              }}>Fetch More Candidates</Button>
            </div>
          </Card>
        );
      })()}
      {!loading && searched && results.length === 0 && !error && allResults.length === 0 && (
        <div>
          <EmptyState icon={Search} title="No candidates matched" description="Try different terms or broaden your search" action={<Button variant="primary" icon={RefreshCw} onClick={startOver}>Start Over</Button>} />
          {sourceErrors.length > 0 && (
            <div style={{ marginTop: 16 }}>
              {sourceErrors.map((se, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
                  marginBottom: 8, borderRadius: 10,
                  background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)'
                }}>
                  <AlertCircle size={16} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#f59e0b' }}>
                      {se.source === 'knine' ? 'kNine' : se.source === 'ats' ? 'ATS' : se.source} search unavailable
                    </span>
                    <span style={{ fontSize: 12, color: colors.textSecondary, marginLeft: 6 }}>
                      {se.error?.includes('not configured') || se.error?.includes('API key')
                        ? 'kNine search is not configured. Contact your administrator.'
                        : se.error?.includes('No ATS') || se.error?.includes('No PCR')
                          ? 'No ATS connected — go to Settings → Integrations to set up PCRecruiter.'
                          : se.error}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {!loading && !searched && !activeSearch && (
        <EmptyState icon={Users} title="Find Your Next Great Hire" description="Describe who you're looking for in plain English" />
      )}

      {/* Profile Drawer */}
      {selectedCandidate && (
        <ProfileDrawer
          candidate={selectedCandidate}
          loading={profileLoading || enrichingProfile}
          onClose={() => {
            // If enriched and unsaved, warn the user
            // Just close — enriched state is preserved in candidates array, no re-charge on reopen
            setSelectedCandidate(null);
            setAtsUploadResult(null);
          }}
          onAddToList={() => openSingleAddToList(selectedCandidate)}
          onSubmitToPosition={() => openSingleSubmitToPosition(selectedCandidate)}
          onOpenInATS={() => openInATS(selectedCandidate)}
          onViewExistingAts={(id) => loadAndViewATSRecord(id)}
          onUploadToATS={selectedCandidate.enriched && selectedCandidate.atsConnected !== false ? () => uploadToATS(selectedCandidate.enrichedData || selectedCandidate, selectedCandidate.validation) : null}
          onUpdateATSCandidate={updateATSCandidate}
          onDismissEnriched={() => dismissEnrichedResult(selectedCandidate.candidateId || selectedCandidate.id)}
          onDismissKnine={selectedCandidate.source === 'knine' ? () => dismissKnineCandidate(selectedCandidate) : null}
          atsUploadResult={atsUploadResult}
          isEnriched={selectedCandidate.enriched}
          isEnriching={enrichingProfile}
          onEnrich={() => enrichFromDrawer(selectedCandidate)}
          creditsRemaining={creditsRemaining}
          similarCandidates={allResults.filter(c => {
            const cid = c.candidateId || c.id;
            const sid = selectedCandidate.candidateId || selectedCandidate.id;
            if (cid === sid) return false;
            // Same company or similar title
            const sameCompany = c.company && selectedCandidate.company && c.company.toLowerCase() === selectedCandidate.company.toLowerCase();
            const sameTitle = c.title && selectedCandidate.title && c.title.toLowerCase() === selectedCandidate.title.toLowerCase();
            return sameCompany || sameTitle;
          }).slice(0, 5)}
          onViewSimilar={(c) => { openProfile(c); }}
        />
      )}

      {/* Modals */}
      {showAddToList && actionCandidates.length > 0 && (
        <AddToListModal 
          candidates={actionCandidates} 
          rollups={rollups} 
          loading={dataLoading.rollups}
          error={dataError.rollups}
          onClose={() => { setShowAddToList(false); setActionCandidates([]); }} 
          onSuccess={() => { setShowAddToList(false); setActionCandidates([]); setSelectedIds(new Set()); }} 
          onRefresh={fetchRollups} 
        />
      )}
      {showSubmitToPosition && actionCandidates.length > 0 && (
        <SubmitToPositionModal
          candidates={actionCandidates}
          positions={positions}
          loading={dataLoading.positions}
          error={dataError.positions}
          onClose={() => { setShowSubmitToPosition(false); setActionCandidates([]); }}
          onSuccess={() => { setShowSubmitToPosition(false); setActionCandidates([]); setSelectedIds(new Set()); }}
          onRefresh={fetchPositions}
        />
      )}

      {/* Job Selector Modal */}
      {showJobSelector && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onMouseDown={e => { if (e.target === e.currentTarget) setShowJobSelector(false); }}>
          <Card className="kubi-modal-card" style={{ width: '100%', maxWidth: 550, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 20, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Briefcase size={22} style={{ color: '#fff' }} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: colors.text }}>Select a Position</h3>
                  <p style={{ margin: 0, fontSize: 12, color: colors.textMuted }}>Search or choose from recent positions</p>
                </div>
              </div>
              <button onClick={() => setShowJobSelector(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, padding: 8 }}>
                <X size={20} />
              </button>
            </div>

            {/* Search Input */}
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: colors.bgInput, borderRadius: 8, border: `1px solid ${colors.border}`, padding: '8px 12px' }}>
                <Search size={16} style={{ color: colors.textMuted }} />
                <input
                  type="text"
                  value={jobSearchQuery}
                  onChange={(e) => {
                    setJobSearchQuery(e.target.value);
                    if (e.target.value.length > 2) {
                      searchJobs(e.target.value);
                    } else {
                      setJobSearchResults([]);
                    }
                  }}
                  placeholder="Search positions by title, company..."
                  style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 14, color: colors.text, outline: 'none' }}
                />
                {jobSearchLoading && <Loader2 size={16} className="spin" style={{ color: colors.accent }} />}
              </div>
            </div>

            {/* Results */}
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              {/* Search Results */}
              {jobSearchQuery.length > 2 && jobSearchResults.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>
                    Search Results ({jobSearchResults.length})
                  </div>
                  {jobSearchResults.map(job => (
                    <div
                      key={job.id}
                      onClick={() => handleJobSelect(job)}
                      style={{
                        padding: 12, marginBottom: 8, borderRadius: 8, border: `1px solid ${colors.border}`,
                        background: colors.bgCard, cursor: 'pointer', transition: 'all 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = colors.primary}
                      onMouseLeave={e => e.currentTarget.style.borderColor = colors.border}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14, color: colors.text, marginBottom: 4 }}>{job.title}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: colors.textMuted }}>
                        {job.companyName && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Building size={12} /> {job.companyName}</span>}
                        {job.location && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={12} /> {job.location}</span>}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* No search results message */}
              {jobSearchQuery.length > 2 && jobSearchResults.length === 0 && !jobSearchLoading && (
                <div style={{ textAlign: 'center', padding: 24, color: colors.textMuted }}>
                  <Search size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                  <p style={{ margin: 0 }}>No positions found for "{jobSearchQuery}"</p>
                </div>
              )}

              {/* Recent Positions */}
              {(!jobSearchQuery || jobSearchQuery.length <= 2) && recentPositions.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>
                    Recent Positions
                  </div>
                  {recentPositions.map(job => (
                    <div
                      key={job.id}
                      onClick={() => handleJobSelect(job)}
                      style={{
                        padding: 12, marginBottom: 8, borderRadius: 8, border: `1px solid ${colors.border}`,
                        background: colors.bgCard, cursor: 'pointer', transition: 'all 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = colors.primary}
                      onMouseLeave={e => e.currentTarget.style.borderColor = colors.border}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14, color: colors.text, marginBottom: 4 }}>{job.title}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: colors.textMuted }}>
                        {job.companyName && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Building size={12} /> {job.companyName}</span>}
                        {job.location && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={12} /> {job.location}</span>}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Empty state */}
              {(!jobSearchQuery || jobSearchQuery.length <= 2) && recentPositions.length === 0 && (
                <div style={{ textAlign: 'center', padding: 32, color: colors.textMuted }}>
                  <Briefcase size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                  <p style={{ margin: 0, marginBottom: 8 }}>No positions found</p>
                  <p style={{ margin: 0, fontSize: 13 }}>Search above to find positions in your ATS</p>
                </div>
              )}
            </div>

            {/* Footer with Parse JD option */}
            <div style={{ padding: 16, borderTop: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <button
                onClick={() => { setShowJobSelector(false); setShowJDScanner(true); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
                  border: `1px solid ${colors.border}`, background: colors.bgMuted,
                  color: colors.textSecondary, fontSize: 13, fontWeight: 500, cursor: 'pointer'
                }}
              >
                <FileText size={14} /> Paste a Job Description instead
              </button>
              <Button variant="secondary" onClick={() => setShowJobSelector(false)}>Cancel</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Job Description Scanner Modal */}
      {showJDScanner && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onMouseDown={e => { if (e.target === e.currentTarget) setShowJDScanner(false); }}>
          <Card className="kubi-modal-card" style={{ width: '100%', maxWidth: 700, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 20, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `linear-gradient(135deg, ${colors.warning}, #f59e0b)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FileText size={22} style={{ color: '#fff' }} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: colors.text }}>Job Description Scanner</h3>
                  <p style={{ margin: 0, fontSize: 12, color: colors.textMuted }}>Paste a JD to generate the ideal candidate profile</p>
                </div>
              </div>
              <button onClick={() => { setShowJDScanner(false); setJdProfile(null); setJdText(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, padding: 8 }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
              {!jdProfile ? (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: colors.text }}>
                      Paste Job Description
                    </label>
                    <textarea
                      value={jdText}
                      onChange={e => setJdText(e.target.value)}
                      placeholder="Paste the full job description here... Include requirements, responsibilities, qualifications, etc."
                      style={{
                        width: '100%', height: 300, padding: 14, borderRadius: 10,
                        border: `1px solid ${colors.border}`, background: colors.bgInput,
                        color: colors.text, fontSize: 14, lineHeight: 1.6, resize: 'vertical',
                        fontFamily: 'inherit', outline: 'none'
                      }}
                    />
                    <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 6 }}>
                      {jdText.length} characters {jdText.length < 50 && '(minimum 50 required)'}
                    </div>
                  </div>

                  <Button
                    variant="ai"
                    icon={Brain}
                    loading={jdParsing}
                    onClick={parseJobDescription}
                    style={{ width: '100%' }}
                    disabled={jdText.length < 50}
                  >
                    {jdParsing ? 'Analyzing Job Description...' : 'Generate Ideal Candidate Profile'}
                  </Button>
                </>
              ) : (
                <div className="slide-in-up">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                    <CheckCircle size={20} style={{ color: colors.success }} />
                    <span style={{ fontWeight: 600, color: colors.success }}>Profile Generated</span>
                  </div>

                  {/* Job Title & Seniority */}
                  <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                    <div style={{ flex: 1, padding: 14, borderRadius: 10, background: colors.bgMuted }}>
                      <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 4, textTransform: 'uppercase' }}>Job Title</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>{jdProfile.jobTitle || 'Not specified'}</div>
                    </div>
                    <div style={{ padding: 14, borderRadius: 10, background: colors.bgAccent }}>
                      <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 4, textTransform: 'uppercase' }}>Seniority</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: colors.accent, textTransform: 'capitalize' }}>{jdProfile.seniority || 'Mid'}</div>
                    </div>
                  </div>

                  {/* Summary */}
                  {jdProfile.summary && (
                    <div style={{ padding: 14, borderRadius: 10, background: colors.bgMuted, marginBottom: 16 }}>
                      <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 6, textTransform: 'uppercase' }}>Ideal Candidate Summary</div>
                      <div style={{ fontSize: 14, color: colors.text, lineHeight: 1.5 }}>{jdProfile.summary}</div>
                    </div>
                  )}

                  {/* Must Have Skills */}
                  {jdProfile.mustHave?.skills?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: colors.text, marginBottom: 8 }}>Must-Have Skills</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {jdProfile.mustHave.skills.map((skill, i) => (
                          <Badge key={i} variant="primary">{skill}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Nice to Have */}
                  {jdProfile.niceToHave?.skills?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: colors.text, marginBottom: 8 }}>Nice-to-Have</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {jdProfile.niceToHave.skills.map((skill, i) => (
                          <Badge key={i} variant="secondary">{skill}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Location */}
                  {jdProfile.location && (
                    <div style={{ padding: 12, borderRadius: 8, background: colors.bgMuted, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <MapPin size={16} style={{ color: colors.textMuted }} />
                      <span style={{ fontSize: 13, color: colors.text }}>
                        {jdProfile.location.type} {jdProfile.location.locations?.length > 0 && `- ${jdProfile.location.locations.join(', ')}`}
                      </span>
                    </div>
                  )}

                  {/* Suggested Search */}
                  <div style={{ padding: 14, borderRadius: 10, border: `2px solid ${colors.accent}`, background: colors.bgAccent, marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: colors.accent, marginBottom: 6, textTransform: 'uppercase', fontWeight: 600 }}>Suggested Search Query</div>
                    <div style={{ fontSize: 14, color: colors.text, fontWeight: 500 }}>"{jdProfile.suggestedSearch}"</div>
                  </div>

                  {/* Search Tips */}
                  {jdProfile.searchTips?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: colors.text, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Lightbulb size={14} style={{ color: colors.warning }} /> Search Tips
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 20, color: colors.textSecondary, fontSize: 13 }}>
                        {jdProfile.searchTips.map((tip, i) => <li key={i} style={{ marginBottom: 4 }}>{tip}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ padding: 16, borderTop: `1px solid ${colors.border}`, display: 'flex', gap: 12 }}>
              {jdProfile ? (
                <>
                  <Button variant="secondary" onClick={() => { setJdProfile(null); }} style={{ flex: 1 }}>
                    Edit JD
                  </Button>
                  <Button variant="ai" icon={Search} onClick={searchFromJDProfile} style={{ flex: 2 }}>
                    Search for This Candidate
                  </Button>
                </>
              ) : (
                <Button variant="secondary" onClick={() => setShowJDScanner(false)} style={{ flex: 1 }}>
                  Cancel
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* AI Settings Modal */}
      {showAiSettings && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onMouseDown={e => { if (e.target === e.currentTarget) setShowAiSettings(false); }}>
          <Card className="kubi-modal-card" style={{ width: '100%', maxWidth: 520, maxHeight: '80vh', overflow: 'auto' }} onMouseDown={e => e.stopPropagation()}>
            <div style={{ padding: 20, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg, ${colors.accent}, #1565C0)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Brain size={20} style={{ color: '#fff' }} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>AI Learning Settings</h3>
                  <p style={{ margin: 0, fontSize: 12, color: colors.textMuted }}>Configure how Roxi learns your preferences</p>
                </div>
              </div>
              <button onClick={() => setShowAiSettings(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Company Type</label>
                <select
                  value={aiSettings?.companyType || ''}
                  onChange={e => setAiSettings({ ...aiSettings, companyType: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgInput, color: colors.text, fontSize: 14 }}
                >
                  <option value="">Select your company type...</option>
                  <option value="staffing">Staffing Agency</option>
                  <option value="executive_search">Executive Search Firm</option>
                  <option value="corporate">Corporate HR / In-House</option>
                  <option value="rpoBPO">RPO / BPO</option>
                  <option value="consulting">Consulting Firm</option>
                </select>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Primary Industries</label>
                <p style={{ margin: '0 0 8px', fontSize: 12, color: colors.textMuted }}>Industries you commonly recruit for (comma separated)</p>
                <input
                  type="text"
                  value={(aiSettings?.primaryIndustries || []).join(', ')}
                  onChange={e => setAiSettings({ ...aiSettings, primaryIndustries: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                  placeholder="e.g., Technology, Healthcare, Finance"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgInput, color: colors.text, fontSize: 14 }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Target Roles</label>
                <p style={{ margin: '0 0 8px', fontSize: 12, color: colors.textMuted }}>Common roles you search for (comma separated)</p>
                <input
                  type="text"
                  value={(aiSettings?.targetRoles || []).join(', ')}
                  onChange={e => setAiSettings({ ...aiSettings, targetRoles: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                  placeholder="e.g., Software Engineer, Sales Manager, Data Analyst"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgInput, color: colors.text, fontSize: 14 }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Preferred Locations</label>
                <p style={{ margin: '0 0 8px', fontSize: 12, color: colors.textMuted }}>States/regions you commonly recruit in (comma separated)</p>
                <input
                  type="text"
                  value={(aiSettings?.preferredLocations || []).join(', ')}
                  onChange={e => setAiSettings({ ...aiSettings, preferredLocations: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                  placeholder="e.g., Ohio, California, Texas, Remote"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgInput, color: colors.text, fontSize: 14 }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Key Skills</label>
                <p style={{ margin: '0 0 8px', fontSize: 12, color: colors.textMuted }}>Skills you frequently search for (comma separated)</p>
                <input
                  type="text"
                  value={(aiSettings?.skillKeywords || []).join(', ')}
                  onChange={e => setAiSettings({ ...aiSettings, skillKeywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                  placeholder="e.g., Python, Salesforce, Project Management"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgInput, color: colors.text, fontSize: 14 }}
                />
              </div>
              <div style={{ padding: 12, background: colors.bgMuted, borderRadius: 8, marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Lightbulb size={16} style={{ color: colors.accent }} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>AI Learning</span>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: colors.textMuted, lineHeight: 1.5 }}>
                  Roxi AI learns from your searches automatically. Setting these preferences helps Roxi provide better suggestions and understand your recruiting focus areas.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <Button variant="ghost" style={{ flex: 1 }} onClick={() => setShowAiSettings(false)}>Cancel</Button>
                <Button variant="ai" icon={CheckCircle} style={{ flex: 1 }} onClick={() => { updateAiSettings(aiSettings); setShowAiSettings(false); }}>Save Settings</Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* KNine branded modal — replaces native alert/confirm/prompt */}
      <KNineModal modal={knModal} onClose={handleKnModalClose} />
    </div>
  );
}

// =============================================================================
// CANDIDATE CARD - Compact
// =============================================================================
function CandidateCard({ candidate, selected, onSelect, onView, onAddToList, onSubmitToPosition, onOpenInATS, onDismiss }) {
  const colors = useColors();

  const matchScore = candidate.matchScore || 50;
  const matchLevel = candidate.matchLevel || 'Fair';
  const scoreColor = matchScore >= 85 ? colors.success : matchScore >= 70 ? '#3b82f6' : colors.warning;
  const isKnine = candidate.source === 'knine';
  const isPreview = isKnine && candidate.isPreview && !candidate.enriched;
  const isEnrichedKnine = isKnine && candidate.enriched;

  // Count available data points for preview cards
  const previewDataCount = isPreview ? [candidate.hasEmail, candidate.hasPhone, candidate.hasSkills, candidate.hasExperience, candidate.hasEducation, candidate.hasGithub].filter(Boolean).length : 0;

  // Card border/styling based on state
  const cardBorder = selected ? colors.accent : isEnrichedKnine ? '#059669' : isPreview ? (colors.darkMode ? 'rgba(139, 115, 85, 0.5)' : 'rgba(139, 115, 85, 0.35)') : colors.border;
  const cardBg = isEnrichedKnine
    ? 'rgba(16, 185, 129, 0.06)'
    : isPreview
      ? (colors.darkMode ? 'rgba(139, 115, 85, 0.06)' : 'rgba(139, 115, 85, 0.04)')
      : undefined;

  return (
    <Card
      style={{
        padding: 0, overflow: 'hidden', position: 'relative',
        borderColor: cardBorder, background: cardBg,
        borderLeft: isKnine
          ? (isEnrichedKnine ? '3px solid #10b981' : '3px solid #a08060')
          : undefined,
        transition: 'all 0.2s ease'
      }}
    >
      {/* Score bar */}
      <div style={{ height: 3, background: colors.bgMuted }}>
        <div style={{
          height: '100%', width: `${matchScore}%`,
          background: isEnrichedKnine ? colors.success : scoreColor
        }} />
      </div>

      <div style={{ padding: '12px 14px', paddingLeft: isKnine ? 16 : 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <button onClick={onSelect} style={{ padding: 2, background: 'none', border: 'none', cursor: 'pointer', color: selected ? colors.accent : colors.textMuted, marginTop: 2 }}>
          {selected ? <CheckSquare size={16} /> : <Square size={16} />}
        </button>

        <Avatar name={candidate.fullName} size={40} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{titleCase(candidate.fullName)}</span>
            {isKnine ? (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
                background: isEnrichedKnine
                  ? 'rgba(16, 185, 129, 0.12)'
                  : (colors.darkMode ? 'rgba(160, 140, 110, 0.25)' : 'rgba(139, 115, 85, 0.12)'),
                color: isEnrichedKnine ? '#059669' : (colors.darkMode ? '#d4c4a0' : '#6b5530'),
                border: `1px solid ${isEnrichedKnine ? 'rgba(16,185,129,0.25)' : (colors.darkMode ? 'rgba(160,140,110,0.4)' : 'rgba(139,115,85,0.25)')}`
              }}>
                <Globe size={9} /> kNine
              </span>
            ) : (
              <Badge variant="ats" size="sm"><Database size={9} /> ATS</Badge>
            )}
            {isEnrichedKnine && candidate.inATS && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '2px 7px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                background: 'rgba(16, 185, 129, 0.15)', color: '#047857',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }}>
                <CheckCircle size={9} /> In ATS
              </span>
            )}
            {isEnrichedKnine && !candidate.inATS && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '2px 7px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                background: 'rgba(16, 185, 129, 0.12)', color: '#059669',
                border: '1px solid rgba(16, 185, 129, 0.2)'
              }}>
                <CheckCircle size={9} /> Enriched
              </span>
            )}
            {!isKnine && candidate.inATS && <Badge variant="good" size="sm"><CheckCircle size={9} /> In ATS</Badge>}
            <Badge variant={matchLevel === 'Excellent' ? 'excellent' : matchLevel === 'Good' ? 'good' : 'fair'} size="sm">
              {matchLevel === 'Excellent' && <Star size={9} />}{matchLevel}
            </Badge>
          </div>

          <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>
            {titleCase(candidate.title)}{candidate.company && ` at ${titleCase(candidate.company)}`}
          </div>

          <div style={{ display: 'flex', gap: 8, fontSize: 11, color: colors.textMuted, flexWrap: 'wrap', marginBottom: 4 }}>
            {candidate.location && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={11} /> {titleCase(candidate.location)}</span>}
            {typeof candidate.distanceMiles === 'number' && (() => {
              const d = candidate.distanceMiles;
              // Color-coded by proximity to the searched location — green = close,
              // amber = edge of the search radius
              const proximityColor = d <= 15 ? '#059669' : d <= 30 ? '#3b82f6' : '#d97706';
              const proximityBg = d <= 15 ? 'rgba(16,185,129,0.1)' : d <= 30 ? 'rgba(59,130,246,0.1)' : 'rgba(217,119,6,0.1)';
              const proximityBorder = d <= 15 ? 'rgba(16,185,129,0.25)' : d <= 30 ? 'rgba(59,130,246,0.25)' : 'rgba(217,119,6,0.25)';
              return (
                <span title={`~${d} miles from your search location`} style={{
                  display: 'flex', alignItems: 'center', gap: 3,
                  padding: '1px 6px', borderRadius: 6,
                  background: proximityBg, color: proximityColor,
                  border: `1px solid ${proximityBorder}`, fontWeight: 600
                }}>
                  <Navigation size={10} /> {d} mi away
                </span>
              );
            })()}
            {candidate.yearsExperience && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Clock size={11} /> {candidate.yearsExperience} yrs exp</span>}
            {candidate.industry && !isPreview && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Building size={11} /> {titleCase(candidate.industry)}</span>}
            {/* Contact availability icons */}
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {(candidate.linkedinUrl || (isPreview && candidate.linkedinUrl)) && (
                isKnine && candidate.linkedinUrl
                  ? <a href={candidate.linkedinUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="LinkedIn" style={{ color: '#0a66c2', display: 'flex' }}><Linkedin size={12} /></a>
                  : <span title="LinkedIn" style={{ color: '#0a66c2' }}><Linkedin size={12} /></span>
              )}
              {!isPreview && candidate.email && <span title={candidate.email} style={{ color: colors.success }}><Mail size={12} /></span>}
              {!isPreview && candidate.phone && <span title={candidate.phone} style={{ color: colors.success }}><Phone size={12} /></span>}
              {isPreview && candidate.hasEmail && <span title="Email available" style={{ color: colors.success }}><Mail size={12} /></span>}
              {isPreview && candidate.hasPhone && <span title="Phone available" style={{ color: colors.success }}><Phone size={12} /></span>}
              {!isPreview && candidate.hasResume && <span title="Resume" style={{ color: colors.success }}><FileText size={12} /></span>}
            </span>
          </div>

          {/* Skills row */}
          {!isPreview && candidate.skills?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
              {candidate.skills.slice(0, 5).map((s, i) => (
                <span key={i} style={{ padding: '2px 6px', borderRadius: 8, background: colors.bgMuted, fontSize: 10, color: colors.textSecondary }}>{titleCase(s)}</span>
              ))}
              {candidate.skills.length > 5 && <span style={{ padding: '2px 6px', borderRadius: 8, background: colors.bgMuted, fontSize: 10, color: colors.textMuted }}>+{candidate.skills.length - 5}</span>}
            </div>
          )}

          {/* Enriched canonical metadata: role, company size, salary */}
          {!isPreview && (candidate.titleRole || candidate.companySize || candidate.inferredSalary) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
              {candidate.titleRole && (
                <span style={{ padding: '2px 7px', borderRadius: 8, background: colors.bgMuted, fontSize: 10, fontWeight: 500, color: colors.textSecondary, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <Tag size={9} /> {titleCase(candidate.titleRole.replace(/_/g, ' '))}
                </span>
              )}
              {candidate.companySize && (
                <span style={{ padding: '2px 7px', borderRadius: 8, background: colors.bgMuted, fontSize: 10, color: colors.textMuted, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <Users size={9} /> {candidate.companySize}
                </span>
              )}
              {candidate.inferredSalary && (
                <span style={{ padding: '2px 7px', borderRadius: 8, background: colors.bgMuted, fontSize: 10, color: colors.textMuted, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <DollarSign size={9} /> ~{candidate.inferredSalary}
                </span>
              )}
            </div>
          )}

          {/* Enriched education: most recent degree + school */}
          {!isPreview && candidate.education?.length > 0 && (() => {
            const edu = candidate.education[0];
            const label = [edu.degree, edu.school].filter(Boolean).join(' · ');
            return label ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: colors.textMuted, marginBottom: 4 }}>
                <BookOpen size={10} /> {titleCase(label)}
              </div>
            ) : null;
          })()}

          {/* Matched-on row — shows exactly which record fields satisfied the search */}
          {candidate.matchedOn?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
              {candidate.matchedOn.slice(0, 4).map((m, i) => (
                <span key={i} title={`Matched on ${m.label}: ${m.value}`} style={{
                  display: 'flex', alignItems: 'center', gap: 3,
                  padding: '1px 6px', borderRadius: 6, fontSize: 9, fontWeight: 600,
                  color: colors.accent, background: colors.bgAccent,
                  border: `1px solid ${colors.accent}33`
                }}>
                  <Target size={9} /> {m.label}: {titleCase(String(m.value).length > 28 ? String(m.value).slice(0, 28) + '…' : m.value)}
                </span>
              ))}
            </div>
          )}

          {/* Match reasons row */}
          {candidate.matchReasons?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {candidate.matchReasons.slice(0, 4).map((r, i) => (
                <span key={i} style={{
                  padding: '1px 5px', borderRadius: 4, fontSize: 9,
                  color: colors.success, background: 'rgba(16,185,129,0.08)',
                  border: '1px solid rgba(16,185,129,0.15)'
                }}>{r}</span>
              ))}
            </div>
          )}

          {/* kNine preview: seniority, industry, data availability */}
          {isPreview && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
              {candidate.seniorityLevel && (
                <span style={{ padding: '2px 8px', borderRadius: 8, background: colors.bgAccent, fontSize: 10, fontWeight: 600, color: colors.accent, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <Layers size={9} /> {titleCase(candidate.seniorityLevel)}
                </span>
              )}
              {candidate.industry && (
                <span style={{ padding: '2px 8px', borderRadius: 8, background: colors.bgMuted, fontSize: 10, fontWeight: 500, color: colors.textSecondary, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <Building size={9} /> {titleCase(candidate.industry)}
                </span>
              )}
              {/* Data availability — record richness only (email/phone shown as icons above) */}
              {(candidate.hasExperience || candidate.hasEducation || candidate.hasSkills || candidate.hasCertifications) && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', borderRadius: 8, background: 'rgba(16,185,129,0.08)', fontSize: 10, color: colors.success, border: '1px solid rgba(16,185,129,0.15)' }}>
                  <span style={{ fontWeight: 600 }}>Has:</span>
                  {candidate.hasExperience && <span title="Work history" style={{ display: 'flex', alignItems: 'center', gap: 2 }}><Briefcase size={9} /> Work</span>}
                  {candidate.hasEducation && <span title="Education" style={{ display: 'flex', alignItems: 'center', gap: 2 }}><BookOpen size={9} /> Edu</span>}
                  {candidate.hasSkills && <span title="Skills" style={{ display: 'flex', alignItems: 'center', gap: 2 }}><Zap size={9} /> Skills</span>}
                  {candidate.hasCertifications && <span title="Certifications" style={{ display: 'flex', alignItems: 'center', gap: 2 }}><Award size={9} /> Certs</span>}
                </span>
              )}
              {candidate.companyWebsite && (
                <a href={candidate.companyWebsite.startsWith('http') ? candidate.companyWebsite : `https://${candidate.companyWebsite}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ padding: '2px 8px', borderRadius: 8, background: colors.bgMuted, fontSize: 10, color: colors.primary, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <Globe size={9} /> Website
                </a>
              )}
            </div>
          )}
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, minWidth: 70 }}>
          {/* Match strength blocks */}
          <div style={{ display: 'flex', gap: 2, marginBottom: 2 }}>
            {[1,2,3].map(i => (
              <div key={i} style={{
                width: 14, height: 6, borderRadius: 2,
                background: matchScore >= (i * 30) ? scoreColor : colors.bgMuted
              }} />
            ))}
            <span style={{ fontSize: 10, fontWeight: 700, color: scoreColor, marginLeft: 3 }}>
              {matchLevel === 'Excellent' ? 'Strong' : matchLevel === 'Good' ? 'Good' : 'Fair'}
            </span>
          </div>
          <Button variant="primary" size="sm" icon={Eye} onClick={onView}>{isPreview ? 'Preview' : 'View'}</Button>
          {isKnine && onDismiss && (
            <button
              onClick={(e) => { e.stopPropagation(); onDismiss(); }}
              title="Don't show again"
              style={{
                display: 'flex', alignItems: 'center', gap: 3,
                padding: '3px 8px', borderRadius: 6, border: 'none',
                background: 'transparent', color: colors.textMuted,
                fontSize: 10, cursor: 'pointer'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#ef4444'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = colors.textMuted; }}
            >
              <ThumbsDown size={11} /> Skip
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

// =============================================================================
// VALIDATION SCAN PANEL
// Animated reveal of email + phone verification results
// =============================================================================
function ValidationScanPanel({ validation, isEnriching, colors }) {
  // Initialize directly to 'done' if validation data already present (pre-loaded record)
  const [phase, setPhase] = React.useState(() => {
    if (isEnriching) return 'scanning';
    if (validation) return 'done';
    return 'idle';
  });
  // Track whether we came from a scanning phase so we know when to animate the reveal
  const prevEnriching = React.useRef(isEnriching);

  React.useEffect(() => {
    if (isEnriching) { prevEnriching.current = true; setPhase('scanning'); return; }
    if (validation) {
      // Delay reveal only when transitioning from live scan; snap immediately for pre-loaded data
      const delay = prevEnriching.current ? 350 : 0;
      prevEnriching.current = false;
      const t = setTimeout(() => setPhase('done'), delay);
      return () => clearTimeout(t);
    }
    prevEnriching.current = false;
    setPhase('idle');
  }, [isEnriching, validation]);

  if (phase === 'idle' && !validation) return null;

  const emailStatusProps = (e) => {
    if (!e) return null;
    if (e.valid)    return { label: 'Verified',  color: '#10b981', bg: 'rgba(16,185,129,0.13)', border: 'rgba(16,185,129,0.3)',  Icon: ShieldCheck };
    if (e.risky)    return { label: 'Risky',     color: '#f59e0b', bg: 'rgba(245,158,11,0.13)', border: 'rgba(245,158,11,0.3)',  Icon: ShieldAlert };
    if (e.invalid)  return { label: 'Invalid',   color: '#ef4444', bg: 'rgba(239,68,68,0.13)',  border: 'rgba(239,68,68,0.3)',   Icon: ShieldAlert };
    return           { label: 'Unknown',   color: '#64748b', bg: 'rgba(100,116,139,0.1)', border: 'rgba(100,116,139,0.25)', Icon: Shield };
  };

  const phoneStatusProps = (p) => {
    if (!p) return null;
    const color = p.lineType === 'mobile' ? '#10b981' : p.lineType === 'voip' ? '#f59e0b' : '#64748b';
    const label = p.lineType ? (p.lineType.charAt(0).toUpperCase() + p.lineType.slice(1)) : (p.valid ? 'Valid' : 'Invalid');
    return { label, color, bg: `${color}18`, border: `${color}40`, Icon: p.lineType === 'mobile' ? Smartphone : Phone };
  };

  return (
    <div style={{ marginBottom: 10, borderRadius: 10, overflow: 'hidden', border: `1px solid ${phase === 'scanning' ? 'rgba(99,102,241,0.3)' : phase === 'done' && validation?.summary?.fullyVerified ? 'rgba(16,185,129,0.35)' : 'rgba(100,116,139,0.2)'}`, transition: 'border-color 0.4s' }}>
      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: phase === 'scanning' ? 'rgba(99,102,241,0.08)' : phase === 'done' && validation?.summary?.fullyVerified ? 'rgba(16,185,129,0.08)' : 'rgba(100,116,139,0.06)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        {phase === 'scanning' ? (
          <>
            <Loader2 size={13} style={{ color: '#6366f1', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#6366f1' }}>Verifying contact info</span>
            <span className="val-pulse" style={{ fontSize: 11, color: '#6366f1', opacity: 0.6, marginLeft: 2 }}>···</span>
          </>
        ) : validation?.summary?.fullyVerified ? (
          <>
            <ShieldCheck size={13} style={{ color: '#10b981' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#10b981' }}>Contact Verified</span>
          </>
        ) : (
          <>
            <Shield size={13} style={{ color: '#64748b' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted }}>Verification Results</span>
          </>
        )}
        {phase === 'scanning' && (
          <div style={{ flex: 1, height: 2, background: 'rgba(99,102,241,0.15)', borderRadius: 2, overflow: 'hidden', marginLeft: 4 }}>
            <div className="scan-line" style={{ height: '100%', background: 'linear-gradient(90deg, #6366f1, #818cf8)', borderRadius: 2 }} />
          </div>
        )}
      </div>

      {/* Results */}
      {phase === 'done' && validation && (
        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Emails */}
          {(validation.emails || (validation.email ? [validation.email] : [])).map((e, i) => {
            const sp = emailStatusProps(e);
            if (!sp) return null;
            return (
              <div key={i} className="reveal-badge" style={{ animationDelay: `${i * 80}ms`, animationFillMode: 'both', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: sp.bg, border: `1px solid ${sp.border}` }}>
                <sp.Icon size={13} style={{ color: sp.color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: colors.text, flex: 1, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.address}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: sp.color, flexShrink: 0 }}>{sp.label}</span>
                {e.didYouMean && <span style={{ fontSize: 10, color: '#f59e0b' }}>→ {e.didYouMean}?</span>}
              </div>
            );
          })}
          {/* Phones */}
          {(validation.phones || (validation.phone ? [validation.phone] : [])).map((p, i) => {
            const sp = phoneStatusProps(p);
            if (!sp) return null;
            const emailCount = (validation.emails || (validation.email ? [validation.email] : [])).length;
            return (
              <div key={i} className="reveal-badge" style={{ animationDelay: `${(emailCount + i) * 80}ms`, animationFillMode: 'both', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: sp.bg, border: `1px solid ${sp.border}` }}>
                <sp.Icon size={13} style={{ color: sp.color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: colors.text, flex: 1, fontFamily: 'monospace' }}>{p.number}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: sp.color, flexShrink: 0 }}>{sp.label}</span>
                {p.carrier && <span style={{ fontSize: 10, color: colors.textMuted }}>{p.carrier}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// PROFILE DRAWER - Rich data
// =============================================================================
function ProfileDrawer({ candidate, loading, onClose, onAddToList, onSubmitToPosition, onOpenInATS, onViewExistingAts,
  onUploadToATS, onUpdateATSCandidate, onDismissEnriched, onDismissKnine, atsUploadResult, isEnriched, isEnriching, onEnrich, creditsRemaining, similarCandidates, onViewSimilar }) {
  const colors = useColors();
  const [activeTab, setActiveTab] = useState('overview');
  const [expanded, setExpanded] = useState({});
  const [selectedChanges, setSelectedChanges] = useState([]);
  const [resumeModalHtml, setResumeModalHtml] = useState(null);

  // kNine branded modal (local to ProfileDrawer)
  const [knModal, setKnModal] = useState(null);
  const knModalResolveRef = useRef(null);
  const knAlert = (message, opts = {}) => new Promise(resolve => {
    knModalResolveRef.current = resolve;
    setKnModal({ type: 'alert', message, title: opts.title || 'kNine', variant: opts.variant || 'info', ...opts });
  });
  const handleKnModalClose = (result) => {
    const resolve = knModalResolveRef.current;
    knModalResolveRef.current = null;
    setKnModal(null);
    if (resolve) resolve(result);
  };

  const matchScore = candidate.matchScore || 50;
  const matchLevel = candidate.matchLevel || 'Good';
  const scoreColor = matchScore >= 85 ? colors.success : matchScore >= 70 ? '#3b82f6' : colors.warning;
  const isKnine = candidate.source === 'knine';
  const isPreview = candidate.isPreview && !candidate.enriched;
  const attachments = candidate.attachments || [];

  const toggleExpand = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const formatSalary = (val) => {
    if (!val) return null;
    const num = typeof val === 'object' ? val.Value : val;
    return num ? `$${Number(num).toLocaleString()}` : null;
  };

  const formatDate = (val) => {
    if (!val) return null;
    try {
      return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return val; }
  };

  const downloadAttachment = async (att) => {
    // kNine generated resume — download HTML file
    if (att.isKnineResume && att.resumeHtml) {
      const blob = new Blob([att.resumeHtml], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = att.name || 'Resume_kNine.html';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return;
    }

    const attId = att.id || att.AttachmentId || att.tokenId;
    if (!attId) {
      knAlert('Attachment ID not found', { title: 'Error', variant: 'error' });
      return;
    }
    try {
      const token = getToken();
      if (!token) {
        knAlert('Session expired. Please log in again.', { title: 'Session Expired', variant: 'warning' });
        return;
      }
      const url = buildApiUrl(`/api/pcr/candidates/${candidate.candidateId}/attachments/${attId}/download`);
      console.log('[Download] Fetching:', url);
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Download failed' }));
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = att.name || att.Name || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      console.error('Download error:', e);
      knAlert(`Download failed: ${e.message}`, { title: 'Download Error', variant: 'error' });
    }
  };

  const previewAttachment = async (att) => {
    // kNine generated resume — show in modal
    if (att.isKnineResume && att.resumeHtml) {
      setResumeModalHtml(att.resumeHtml);
      return;
    }

    const attId = att.id || att.AttachmentId || att.tokenId;
    if (!attId) {
      knAlert('Attachment ID not found', { title: 'Error', variant: 'error' });
      return;
    }

    const token = getToken();
    if (!token) {
      knAlert('Session expired. Please log in again.', { title: 'Session Expired', variant: 'warning' });
      return;
    }

    try {
      // Always fetch via API and open as blob - this avoids auth issues with new windows
      const url = buildApiUrl(`/api/pcr/candidates/${candidate.candidateId}/attachments/${attId}/preview`);
      console.log('[Preview] Fetching:', url);

      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Preview failed' }));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Open in new tab
      const newWindow = window.open(blobUrl, '_blank');

      // If popup was blocked, open in same window silently
      if (!newWindow) {
        window.location.href = blobUrl;
      }

      // Clean up blob URL after a delay (give time for it to load)
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 120000);

    } catch (e) {
      console.error('Preview error:', e);
      knAlert(`Preview failed: ${e.message}`, { title: 'Preview Error', variant: 'error' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <>
      <div onMouseDown={onClose} className="fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000 }} />
      <div className="slide-in-right kubi-drawer" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 480,
        background: colors.bgCard, zIndex: 1001, display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.15)'
      }}>
        {/* Header */}
        <div style={{ padding: '12px 14px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <ArrowLeft size={18} style={{ color: colors.textMuted }} />
          </button>
          <Avatar name={candidate.fullName} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleCase(candidate.fullName)}</h2>
            <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary }}>{titleCase(candidate.title)}{candidate.company ? ` at ${titleCase(candidate.company)}` : ''}</p>
          </div>
          {isKnine ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
                background: isPreview
                  ? (colors.darkMode ? 'rgba(160, 140, 110, 0.2)' : 'rgba(120, 100, 70, 0.1)')
                  : 'rgba(16, 185, 129, 0.12)',
                color: isPreview ? (colors.darkMode ? '#c4b08a' : '#7a6840') : '#059669',
                border: `1px solid ${isPreview ? (colors.darkMode ? 'rgba(160,140,110,0.3)' : 'rgba(120,100,70,0.2)') : 'rgba(16,185,129,0.25)'}`
              }}>
                <Globe size={10} /> kNine
              </span>
              {isPreview && (
                <span style={{ fontSize: 10, color: colors.darkMode ? '#b0a080' : '#8a7550', fontWeight: 600 }}>Preview</span>
              )}
              {candidate.enriched && (
                <span style={{ fontSize: 10, color: colors.success, fontWeight: 600 }}>Enriched</span>
              )}
            </div>
          ) : (
            <Badge variant="ats"><Database size={10} /> ATS</Badge>
          )}
        </div>

        {/* Tabs - hide attachments for kNine records with no attachments */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}` }}>
          {['overview', ...(isKnine && attachments.length === 0 ? [] : ['attachments'])].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1, padding: '10px', background: 'none', border: 'none',
                borderBottom: activeTab === tab ? `2px solid ${colors.accent}` : '2px solid transparent',
                color: activeTab === tab ? colors.accent : colors.textMuted,
                fontWeight: 600, fontSize: 12, cursor: 'pointer'
              }}
            >
              {tab === 'attachments' ? `Attachments (${attachments.length})` : 'Overview'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
          {(loading || isEnriching) && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Loader2 size={28} className="spin" style={{ color: colors.accent }} />
              {isEnriching && (
                <p style={{ margin: '12px 0 0', fontSize: 13, color: colors.textSecondary }}>
                  Loading contact information...
                </p>
              )}
            </div>
          )}

          {/* kNine Preview - show available data + Enrich CTA */}
          {!loading && !isEnriching && activeTab === 'overview' && isPreview && (
            <div>
              {/* Existing ATS Record Banner */}
              {candidate.existingAtsRecord && (
                <div style={{ marginBottom: 14, padding: 14, background: 'rgba(59,130,246,0.08)', borderRadius: 10, border: '1px solid rgba(59,130,246,0.25)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Database size={16} style={{ color: '#3b82f6' }} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#3b82f6' }}>Already in Your Database</span>
                    </div>
                    <button
                      onClick={() => onViewExistingAts ? onViewExistingAts(candidate.existingAtsRecord.id) : onOpenInATS()}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(59,130,246,0.3)',
                        background: 'rgba(59,130,246,0.1)', color: '#3b82f6', fontSize: 11, fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      <Database size={11} /> View in kNine
                    </button>
                  </div>
                  <div style={{ padding: 10, background: colors.bgCard, borderRadius: 8, border: `1px solid ${colors.border}` }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>{candidate.existingAtsRecord.name}</div>
                    <div style={{ fontSize: 12, color: colors.textSecondary }}>{candidate.existingAtsRecord.title}{candidate.existingAtsRecord.company ? ` at ${candidate.existingAtsRecord.company}` : ''}</div>
                    {/* Show all available ATS contact info */}
                    {candidate.existingAtsRecord.email && (
                      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Mail size={10} /> {candidate.existingAtsRecord.email}
                      </div>
                    )}
                    {candidate.existingAtsRecord.phone && (
                      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Phone size={10} /> {candidate.existingAtsRecord.phone}
                      </div>
                    )}
                    {candidate.existingAtsRecord.location && (
                      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MapPin size={10} /> {candidate.existingAtsRecord.location}
                      </div>
                    )}
                    {candidate.existingAtsRecord.lastActivity && (
                      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Clock size={10} /> Last activity: {new Date(candidate.existingAtsRecord.lastActivity).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  {/* Enrich to add contact info if the ATS record is incomplete */}
                  {candidate.existingAtsRecord.missingContactInfo ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 11, color: '#f59e0b', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <AlertCircle size={11} /> No contact info in your ATS for this person.
                      </div>
                      <button
                        onClick={onEnrich}
                        style={{
                          width: '100%', padding: '8px 14px', borderRadius: 7,
                          background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
                          border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                        }}
                      >
                        <Zap size={13} /> Enrich to Add Contact Info
                      </button>
                    </div>
                  ) : (
                    <p style={{ margin: '8px 0 0', fontSize: 11, color: colors.textMuted }}>
                      Enriching will compare and update their record with the latest data.
                    </p>
                  )}
                </div>
              )}

              {/* What We Know Section */}
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>What We Know</h4>

                {/* Current Position */}
                {(candidate.title || candidate.company) && (
                  <Card style={{ padding: 12, marginBottom: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Current Position</div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <Briefcase size={14} style={{ color: colors.accent, flexShrink: 0, marginTop: 2 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {candidate.title && <div style={{ fontSize: 13, fontWeight: 700, color: colors.text }}>{titleCase(candidate.title)}</div>}
                        {candidate.company && <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 1 }}>at {titleCase(candidate.company)}</div>}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                          {candidate.seniorityLevel && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 600, background: colors.darkMode ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)', color: colors.accent, border: `1px solid ${colors.darkMode ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.15)'}` }}>
                              <Layers size={9} /> {titleCase(candidate.seniorityLevel)}
                            </span>
                          )}
                          {candidate.titleRole && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 600, background: colors.bgMuted, color: colors.textSecondary, border: `1px solid ${colors.border}` }}>
                              {titleCase(candidate.titleRole.replace(/_/g, ' '))}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Company Details */}
                {(candidate.company || candidate.companySize || candidate.companyIndustry || candidate.companyWebsite) && (
                  <Card style={{ padding: 12, marginBottom: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Company</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {candidate.company && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Building size={13} style={{ color: colors.textMuted, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>{titleCase(candidate.company)}</span>
                        </div>
                      )}
                      {candidate.companyIndustry && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Tag size={13} style={{ color: colors.textMuted, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: colors.textSecondary }}>{titleCase(candidate.companyIndustry)}</span>
                        </div>
                      )}
                      {candidate.companySize && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Users size={13} style={{ color: colors.textMuted, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: colors.textSecondary }}>{candidate.companySize} employees</span>
                        </div>
                      )}
                      {candidate.companyWebsite && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Globe size={13} style={{ color: colors.textMuted, flexShrink: 0 }} />
                          <a href={candidate.companyWebsite.startsWith('http') ? candidate.companyWebsite : `https://${candidate.companyWebsite}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: colors.primary, textDecoration: 'none' }}>
                            {candidate.companyWebsite.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                          </a>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* Location */}
                {(candidate.location || candidate.city || candidate.state) && (
                  <Card style={{ padding: 12, marginBottom: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Location</div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <MapPin size={13} style={{ color: colors.textMuted, flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>
                          {[candidate.city, candidate.state].filter(Boolean).map(s => titleCase(s)).join(', ') || titleCase(candidate.location)}
                        </div>
                        {candidate.country && candidate.country.toLowerCase() !== 'united states' && (
                          <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 1 }}>{titleCase(candidate.country)}</div>
                        )}
                        {candidate.locationMetro && candidate.city && candidate.locationMetro.toLowerCase() !== candidate.city.toLowerCase() && (
                          <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 1 }}>{titleCase(candidate.locationMetro)} metro</div>
                        )}
                        {typeof candidate.distanceMiles === 'number' && (() => {
                          const d = candidate.distanceMiles;
                          const proximityColor = d <= 15 ? '#059669' : d <= 30 ? '#3b82f6' : '#d97706';
                          const proximityBg = d <= 15 ? 'rgba(16,185,129,0.1)' : d <= 30 ? 'rgba(59,130,246,0.1)' : 'rgba(217,119,6,0.1)';
                          const proximityBorder = d <= 15 ? 'rgba(16,185,129,0.25)' : d <= 30 ? 'rgba(59,130,246,0.25)' : 'rgba(217,119,6,0.25)';
                          return (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 5,
                              padding: '2px 7px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                              background: proximityBg, color: proximityColor,
                              border: `1px solid ${proximityBorder}`
                            }}>
                              <Navigation size={10} /> ~{d} miles from search location
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </Card>
                )}

                {/* Industry & Compensation */}
                {(candidate.industry || candidate.inferredSalary) && (
                  <Card style={{ padding: 12, marginBottom: 8 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {candidate.industry && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Building size={13} style={{ color: colors.textMuted, flexShrink: 0 }} />
                          <div>
                            <span style={{ fontSize: 10, color: colors.textMuted, marginRight: 6 }}>INDUSTRY</span>
                            <span style={{ fontSize: 12, color: colors.text }}>{titleCase(candidate.industry)}</span>
                          </div>
                        </div>
                      )}
                      {candidate.inferredSalary && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <DollarSign size={13} style={{ color: colors.textMuted, flexShrink: 0 }} />
                          <div>
                            <span style={{ fontSize: 10, color: colors.textMuted, marginRight: 6 }}>EST. SALARY</span>
                            <span style={{ fontSize: 12, color: colors.text }}>{candidate.inferredSalary}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* LinkedIn */}
                {candidate.linkedinUrl && (
                  <Card style={{ padding: 10, marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Linkedin size={14} style={{ color: '#0a66c2', flexShrink: 0 }} />
                      <a
                        href={candidate.linkedinUrl.startsWith('http') ? candidate.linkedinUrl : `https://${candidate.linkedinUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ fontSize: 12, color: '#0a66c2', textDecoration: 'none', fontWeight: 600 }}
                      >
                        View LinkedIn Profile
                      </a>
                    </div>
                  </Card>
                )}
              </div>

              {/* Match Info */}
              {candidate.matchReasons?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>Match Reasons</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {candidate.matchReasons.map((r, i) => (
                      <span key={i} style={{
                        padding: '3px 8px', borderRadius: 6, fontSize: 11,
                        color: colors.success, background: 'rgba(16,185,129,0.08)',
                        border: '1px solid rgba(16,185,129,0.15)'
                      }}>{r}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Enrich CTA — combines available data + action */}
              <Card style={{
                padding: 16,
                background: colors.darkMode ? 'rgba(30, 136, 229, 0.06)' : 'rgba(30, 136, 229, 0.04)',
                border: `1px solid ${colors.darkMode ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.15)'}`
              }}>
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                  <Sparkles size={24} style={{ color: colors.accent, marginBottom: 4 }} />
                  <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: colors.text }}>Unlock Full Profile</h3>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, justifyContent: 'center', marginBottom: 14 }}>
                  {[
                    { has: candidate.hasEmail !== false, icon: Mail, label: 'Email' },
                    { has: candidate.hasPhone !== false, icon: Phone, label: 'Phone' },
                    { has: candidate.hasExperience, icon: Briefcase, label: 'Work History' },
                    { has: candidate.hasEducation, icon: BookOpen, label: 'Education' },
                    { has: candidate.hasSkills, icon: Zap, label: 'Skills' },
                    { has: true, icon: FileText, label: 'AI Resume' }
                  ].filter(d => d.has).map((d, i) => (
                    <span key={i} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                      background: colors.darkMode ? 'rgba(99,102,241,0.15)' : 'rgba(59,130,246,0.08)',
                      color: colors.accent, border: `1px solid ${colors.darkMode ? 'rgba(99,102,241,0.25)' : 'rgba(59,130,246,0.15)'}`
                    }}>
                      <d.icon size={10} /> {d.label}
                    </span>
                  ))}
                </div>
                <button
                  onClick={onEnrich}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
                    padding: '11px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: colors.accent, color: '#fff', fontSize: 14, fontWeight: 700,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                >
                  <Coins size={16} /> Enrich Profile · 1 Credit
                </button>
                {creditsRemaining !== null && creditsRemaining !== undefined && (
                  <p style={{ margin: '6px 0 0', fontSize: 11, color: colors.textMuted, textAlign: 'center' }}>
                    {creditsRemaining} credit{creditsRemaining !== 1 ? 's' : ''} remaining
                  </p>
                )}
              </Card>
            </div>
          )}

          {!loading && !isEnriching && activeTab === 'overview' && !isPreview && (
            <div>
              {/* ATS Upload Saving Indicator */}
              {atsUploadResult && atsUploadResult.action === 'saving' && (
                <div style={{ marginBottom: 14, padding: 14, borderRadius: 10, textAlign: 'center', background: colors.bgMuted, border: `1px solid ${colors.border}` }}>
                  <Loader2 size={24} className="spin" style={{ color: colors.accent, marginBottom: 6 }} />
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: colors.text }}>Saving to ATS...</p>
                  <p style={{ margin: '4px 0 0', fontSize: 11, color: colors.textMuted }}>Creating record with work history and resume</p>
                </div>
              )}

              {/* ATS Upload Success Banner */}
              {atsUploadResult && (atsUploadResult.action === 'created' || atsUploadResult.action === 'updated') && (
                <div style={{
                  marginBottom: 14, padding: 16, borderRadius: 12, textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(5,150,105,0.08))',
                  border: '1px solid rgba(16,185,129,0.3)'
                }}>
                  <CheckCircle size={32} style={{ color: colors.success, marginBottom: 8 }} />
                  <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: colors.success }}>
                    {atsUploadResult.action === 'created' ? 'Record Saved to ATS' : 'Record Updated in ATS'}
                  </h3>
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: colors.text, fontWeight: 600 }}>
                    {atsUploadResult.candidateName || titleCase(candidate.fullName)}
                  </p>
                  {(atsUploadResult.candidateTitle || candidate.title) && (
                    <p style={{ margin: '0 0 2px', fontSize: 12, color: colors.textSecondary }}>
                      {titleCase(atsUploadResult.candidateTitle || candidate.title)}
                      {(atsUploadResult.candidateCompany || candidate.company) && ` at ${titleCase(atsUploadResult.candidateCompany || candidate.company)}`}
                    </p>
                  )}
                  <p style={{ margin: '8px 0 0', fontSize: 11, color: colors.textMuted }}>
                    Full details, work history, and resume included.
                  </p>
                </div>
              )}


              {atsUploadResult && atsUploadResult.action === 'error' && (
                <div style={{ marginBottom: 14, padding: 12, background: 'rgba(239,68,68,0.1)', borderRadius: 10, border: '1px solid rgba(239,68,68,0.3)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AlertCircle size={18} style={{ color: '#ef4444' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#ef4444' }}>Upload failed: {atsUploadResult.message}</span>
                  </div>
                </div>
              )}

              {/* Duplicate Found - Change Preview */}
              {atsUploadResult && atsUploadResult.action === 'duplicate_found' && (
                <div style={{ marginBottom: 14, padding: 14, background: colors.bgWarning, borderRadius: 10, border: `1px solid ${colors.warning}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <AlertCircle size={18} style={{ color: colors.warning }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: colors.warning }}>Duplicate Found in ATS</span>
                  </div>
                  <p style={{ margin: '0 0 10px', fontSize: 12, color: colors.textSecondary }}>
                    <strong style={{ color: colors.text }}>{atsUploadResult.duplicateCandidate?.name}</strong> already exists.
                    Select which fields to update:
                  </p>
                  {atsUploadResult.changes?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {atsUploadResult.changes.map((change, i) => (
                        <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: 8, background: colors.bgCard, borderRadius: 8, cursor: 'pointer', border: `1px solid ${colors.border}` }}>
                          <input
                            type="checkbox"
                            checked={selectedChanges.some(c => c.field === change.field)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedChanges(prev => [...prev, change]);
                              } else {
                                setSelectedChanges(prev => prev.filter(c => c.field !== change.field));
                              }
                            }}
                            style={{ marginTop: 2, accentColor: colors.accent }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>{change.label}</div>
                            <div style={{ fontSize: 11, color: colors.textMuted }}>
                              {change.currentValue ? (
                                <><span style={{ textDecoration: 'line-through' }}>{change.currentValue}</span> → <span style={{ color: colors.success }}>{change.newValue}</span></>
                              ) : (
                                <span style={{ color: colors.success }}>+ {change.newValue}</span>
                              )}
                            </div>
                          </div>
                        </label>
                      ))}
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <Button
                          variant="primary"
                          size="sm"
                          icon={CheckCircle}
                          onClick={() => {
                            if (selectedChanges.length === 0) { knAlert('Select at least one field to update.', { title: 'Update Fields', variant: 'warning' }); return; }
                            onUpdateATSCandidate(atsUploadResult.duplicateCandidate.id, candidate.enrichedData || candidate, selectedChanges);
                          }}
                          style={{ flex: 1 }}
                        >
                          Update ({selectedChanges.length})
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDismissEnriched?.()}
                          style={{ flex: 1 }}
                        >
                          Skip
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: 10, background: colors.bgCard, borderRadius: 8 }}>
                      <p style={{ margin: 0, fontSize: 12, color: colors.textMuted }}>No new changes to apply — the record is up to date.</p>
                      <Button variant="ghost" size="sm" onClick={() => onDismissEnriched?.()} style={{ marginTop: 8 }}>Dismiss</Button>
                    </div>
                  )}
                </div>
              )}

              {/* Match Score with Breakdown */}
              <div style={{ marginBottom: 14, padding: 14, background: colors.bgMuted, borderRadius: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: scoreColor }}>{matchScore}%</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>{matchLevel} Match</div>
                    {candidate.matchReasons?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 3 }}>
                        {candidate.matchReasons.map((r, i) => (
                          <span key={i} style={{
                            padding: '1px 6px', borderRadius: 4, fontSize: 10,
                            color: colors.success, background: 'rgba(16,185,129,0.08)',
                            border: '1px solid rgba(16,185,129,0.15)'
                          }}>{r}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {/* Score breakdown bars */}
                {candidate.matchBreakdown && (() => {
                  const bars = [
                    { label: 'Title', value: candidate.matchBreakdown.title || 0, max: 30, color: '#3b82f6' },
                    { label: 'Location', value: candidate.matchBreakdown.location || 0, max: 20, color: '#10b981' },
                    { label: 'Skills', value: candidate.matchBreakdown.skills || 0, max: 20, color: '#8b5cf6' },
                    { label: 'Experience', value: candidate.matchBreakdown.experience || 0, max: 10, color: '#f59e0b' },
                    { label: 'Industry', value: candidate.matchBreakdown.industry || 0, max: 10, color: '#06b6d4' },
                  ].filter(b => b.value !== 0);
                  if (bars.length === 0) return null;
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {bars.map((bar, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 10, color: colors.textMuted, minWidth: 55, textAlign: 'right' }}>{bar.label}</span>
                          <div style={{ flex: 1, height: 6, borderRadius: 3, background: colors.darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}>
                            <div style={{
                              height: '100%', borderRadius: 3,
                              width: `${Math.min(100, Math.max(0, (bar.value / bar.max) * 100))}%`,
                              background: bar.value < 0 ? '#ef4444' : bar.color,
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                          <span style={{ fontSize: 10, color: colors.textMuted, minWidth: 20 }}>{bar.value > 0 ? `+${bar.value}` : bar.value}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Pipeline & Activity — ATS records only */}
              {!isKnine && (candidate.status || candidate.pipelineStage || candidate.lastActivity || candidate.lastModified || candidate.lastNote) && (
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>Pipeline & Activity</h4>
                  <Card style={{ padding: 10 }}>
                    {(candidate.status || candidate.pipelineStage) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                        <Target size={14} style={{ color: colors.accent, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: colors.textMuted, minWidth: 70 }}>Stage</span>
                        <span style={{
                          fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                          background: colors.bgAccent, color: colors.accent
                        }}>{candidate.pipelineStage || candidate.status}</span>
                      </div>
                    )}
                    {(candidate.lastActivity || candidate.lastModified) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                        <Clock size={14} style={{ color: colors.textMuted, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: colors.textMuted, minWidth: 70 }}>Last active</span>
                        <span style={{ fontSize: 13, color: colors.text }}>{new Date(candidate.lastActivity || candidate.lastModified).toLocaleDateString()}</span>
                      </div>
                    )}
                    {candidate.lastNote && (
                      <div style={{ padding: '8px 0 4px', borderTop: `1px solid ${colors.border}`, marginTop: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                          <FileText size={12} style={{ color: colors.textMuted }} />
                          <span style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600 }}>Latest Note</span>
                        </div>
                        <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary, lineHeight: 1.4,
                          overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                          {candidate.lastNote}
                        </p>
                      </div>
                    )}
                  </Card>
                </div>
              )}

              {/* Contact Section */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <h4 style={{ margin: 0, fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>Contact</h4>
                </div>
                <Card style={{ padding: 10 }}>
                  {/* Animated validation scan panel — shown when enriching or validation data present */}
                  {(isEnriching || candidate.validation) && (
                    <ValidationScanPanel
                      validation={candidate.validation}
                      isEnriching={isEnriching}
                      colors={colors}
                    />
                  )}
                  {/* Show a notice when enriched but PDL returned no contact data */}
                  {candidate.enriched && !candidate.email && !candidate.mobilePhone && !candidate.phone && !candidate.workEmail && (
                    <div style={{ fontSize: 11, color: colors.textMuted, padding: '6px 0', fontStyle: 'italic' }}>
                      Contact info not returned by kNine for this person. Check your kNine subscription for contact data access.
                    </div>
                  )}
                  {/* All emails — only verified one gets badge */}
                  {(() => {
                    const val = candidate.validation;
                    const verifiedAddr = val?.verifiedEmail?.address || val?.email?.address;
                    const checkedMap = {};
                    (val?.checkedEmails || val?.emails || []).forEach(e => { if (e?.address) checkedMap[e.address] = e; });

                    // Collect all unique emails preserving priority order
                    const seen = new Set();
                    const allEmails = [];
                    const pushEmail = (addr, label) => {
                      if (addr && !seen.has(addr)) { seen.add(addr); allEmails.push({ addr, label }); }
                    };
                    pushEmail(candidate.email, 'Email');
                    pushEmail(candidate.workEmail, 'Work');
                    if (Array.isArray(candidate.personalEmails)) {
                      candidate.personalEmails.forEach((e, i) => pushEmail(e, i === 0 ? 'Personal' : `Personal ${i + 1}`));
                    }
                    pushEmail(candidate.email2, 'Email 2');

                    return allEmails.map(({ addr, label }) => {
                      const checked = checkedMap[addr];
                      const isVerified = verifiedAddr && addr === verifiedAddr;
                      const badge = isVerified
                        ? { label: 'Verified', color: '#10b981', bg: 'rgba(16,185,129,0.12)', Icon: ShieldCheck }
                        : checked?.risky
                        ? { label: 'Risky', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', Icon: ShieldAlert }
                        : checked?.invalid
                        ? { label: 'Invalid', color: '#ef4444', bg: 'rgba(239,68,68,0.12)', Icon: ShieldAlert }
                        : null;
                      return (
                        <div key={addr} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ flex: 1 }}>
                            <InfoRow icon={Mail} label={label} value={addr} link={`mailto:${addr}`} copyable />
                          </div>
                          {badge && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 6, background: badge.bg, color: badge.color, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>
                              <badge.Icon size={10} /> {badge.label}
                            </span>
                          )}
                        </div>
                      );
                    });
                  })()}
                  {/* All phones — verified one gets line-type badge */}
                  {(() => {
                    const val = candidate.validation;
                    const verifiedNum = val?.verifiedPhone?.number || val?.phone?.number;
                    const checkedPhoneMap = {};
                    (val?.checkedPhones || val?.phones || []).forEach(p => { if (p?.number) checkedPhoneMap[p.number] = p; });

                    const seen = new Set();
                    const allPhones = [];
                    const pushPhone = (num, label) => {
                      if (num && !seen.has(num)) { seen.add(num); allPhones.push({ num, label }); }
                    };
                    pushPhone(candidate.mobilePhone, 'Mobile');
                    pushPhone(candidate.phone, 'Phone');
                    pushPhone(candidate.workPhone, 'Work');
                    if (Array.isArray(candidate.phoneNumbers)) {
                      candidate.phoneNumbers.forEach((p, i) => pushPhone(p, `Phone ${i + 2}`));
                    }

                    return allPhones.map(({ num, label }) => {
                      const checked = checkedPhoneMap[num];
                      const isVerified = verifiedNum && num === verifiedNum;
                      const lineType = isVerified ? candidate.validation?.verifiedPhone?.lineType : (checked?.lineType || null);
                      const phoneColor = lineType === 'mobile' ? '#10b981' : lineType === 'voip' ? '#f59e0b' : colors.textMuted;
                      const PhoneIcon = lineType === 'mobile' ? Smartphone : Phone;
                      return (
                        <div key={num} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ flex: 1 }}>
                            <InfoRow icon={PhoneIcon} label={lineType === 'mobile' ? 'Mobile' : lineType === 'landline' ? 'Landline' : label} value={num} link={`tel:${num}`} copyable color={lineType === 'mobile' && isVerified ? '#10b981' : undefined} />
                          </div>
                          {lineType && isVerified && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 6, background: `${phoneColor}15`, color: phoneColor, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, textTransform: 'capitalize' }}>
                              {lineType}
                            </span>
                          )}
                        </div>
                      );
                    });
                  })()}
                  <InfoRow icon={MapPin} label="Location" value={titleCase(candidate.location || [candidate.city, candidate.state].filter(Boolean).join(', '))} />
                  {candidate.address && <InfoRow icon={MapPin} label="Address" value={titleCase(candidate.address)} />}
                  <InfoRow icon={Linkedin} label="LinkedIn" value={candidate.linkedinUrl ? 'View Profile' : null} link={candidate.linkedinUrl && (candidate.linkedinUrl.startsWith('http') ? candidate.linkedinUrl : `https://${candidate.linkedinUrl}`)} color="#0a66c2" />
                  {candidate.githubUrl && <InfoRow icon={Globe} label="GitHub" value="View Profile" link={candidate.githubUrl.startsWith('http') ? candidate.githubUrl : `https://${candidate.githubUrl}`} />}
                  {candidate.website && <InfoRow icon={Globe} label="Website" value="Visit" link={candidate.website.startsWith('http') ? candidate.website : `https://${candidate.website}`} />}
                  {candidate.facebookUrl && <InfoRow icon={Globe} label="Facebook" value="View" link={candidate.facebookUrl.startsWith('http') ? candidate.facebookUrl : `https://${candidate.facebookUrl}`} />}
                  {candidate.twitterUrl && <InfoRow icon={Globe} label="Twitter" value="View" link={candidate.twitterUrl.startsWith('http') ? candidate.twitterUrl : `https://${candidate.twitterUrl}`} />}
                </Card>
              </div>

              {/* Professional Section */}
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>Professional</h4>
                <Card style={{ padding: 10 }}>
                  <InfoRow icon={Briefcase} label="Title" value={titleCase(candidate.title || candidate.currentOccupation)} />
                  <InfoRow icon={Building} label="Company" value={titleCase(candidate.company || candidate.currentEmployer)} />
                  {candidate.companyIndustry && candidate.companyIndustry !== candidate.industry && <InfoRow icon={Tag} label="Co. Industry" value={titleCase(candidate.companyIndustry)} />}
                  {candidate.companySize && <InfoRow icon={Users} label="Co. Size" value={candidate.companySize} />}
                  {candidate.companyWebsite && <InfoRow icon={Globe} label="Co. Website" value={candidate.companyWebsite.replace(/^https?:\/\/(www\.)?/, '')} link={candidate.companyWebsite.startsWith('http') ? candidate.companyWebsite : `https://${candidate.companyWebsite}`} />}
                  <InfoRow icon={Clock} label="Experience" value={candidate.yearsExperience ? `${candidate.yearsExperience} years` : null} />
                  <InfoRow icon={Tag} label="Industry" value={titleCase(candidate.industry)} />
                  {candidate.titleLevels?.length > 0 && <InfoRow icon={Layers} label="Level" value={candidate.titleLevels.map(l => titleCase(l)).join(', ')} />}
                  <InfoRow icon={Award} label="Specialty" value={titleCase(candidate.specialty)} />
                  {candidate.inferredSalary && <InfoRow icon={DollarSign} label="Est. Salary" value={candidate.inferredSalary} />}
                  <InfoRow icon={DollarSign} label="Current" value={formatSalary(candidate.currentSalary)} />
                  <InfoRow icon={DollarSign} label="Desired" value={formatSalary(candidate.desiredSalary)} />
                  <InfoRow icon={Shield} label="Work Auth" value={candidate.workAuthorization} />
                  <InfoRow icon={MapPin} label="Relocate" value={candidate.willingToRelocate} />
                </Card>
              </div>

              {/* Summary / Headline (from enrichment) */}
              {(candidate.summary || candidate.headline) && (
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>About</h4>
                  <Card style={{ padding: 10 }}>
                    {candidate.headline && <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: colors.text }}>{candidate.headline}</p>}
                    {candidate.summary && <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>{candidate.summary}</p>}
                  </Card>
                </div>
              )}

              {/* Skills */}
              {candidate.skills?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>Skills</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {candidate.skills.map((s, i) => (
                      <span key={i} style={{ padding: '4px 8px', borderRadius: 12, background: colors.bgMuted, fontSize: 11, color: colors.text }}>{titleCase(s)}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Keywords */}
              {candidate.keywords?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>Keywords</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {candidate.keywords.map((k, i) => (
                      <span key={i} style={{ padding: '4px 8px', borderRadius: 12, background: colors.bgAccent, fontSize: 11, color: colors.accent }}>{titleCase(k)}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Languages (from enrichment) */}
              {candidate.languages?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>Languages</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {candidate.languages.map((l, i) => (
                      <span key={i} style={{ padding: '4px 8px', borderRadius: 12, background: colors.bgMuted, fontSize: 11, color: colors.text }}>{titleCase(typeof l === 'string' ? l : l.name || '')}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience History (from enrichment) */}
              {Array.isArray(candidate.experience) && candidate.experience.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>Experience</h4>
                  <Card style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {candidate.experience.slice(0, 8).map((exp, i) => (
                      <div key={i} style={{ borderBottom: i < Math.min(candidate.experience.length, 8) - 1 ? `1px solid ${colors.border}` : 'none', paddingBottom: 6 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>{titleCase(exp.title || exp.name || 'Unknown Role')}</div>
                        <div style={{ fontSize: 12, color: colors.textSecondary }}>
                          {titleCase(exp.company || exp.organization || '')}
                          {exp.isCurrent && <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 4, background: colors.bgSuccess, color: colors.success, fontSize: 10 }}>Current</span>}
                        </div>
                        {exp.location && <div style={{ fontSize: 11, color: colors.textMuted }}>{titleCase(exp.location)}</div>}
                        {(exp.startDate || exp.start_date) && (
                          <div style={{ fontSize: 11, color: colors.textMuted }}>{exp.startDate || exp.start_date}{(exp.endDate || exp.end_date) ? ` — ${exp.endDate || exp.end_date}` : exp.isCurrent ? ' — Present' : ''}</div>
                        )}
                      </div>
                    ))}
                    {candidate.experience.length > 8 && <div style={{ fontSize: 11, color: colors.textMuted, textAlign: 'center' }}>+{candidate.experience.length - 8} more positions</div>}
                  </Card>
                </div>
              )}

              {/* Education & Certs */}
              {((Array.isArray(candidate.education) ? candidate.education.length > 0 : candidate.education) || (Array.isArray(candidate.certifications) ? candidate.certifications.length > 0 : candidate.certifications)) && (
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>Education & Certifications</h4>
                  <Card style={{ padding: 10 }}>
                    {Array.isArray(candidate.education) ? candidate.education.map((edu, i) => (
                      <div key={i} style={{ padding: '4px 0', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <BookOpen size={14} style={{ color: colors.textMuted, flexShrink: 0, marginTop: 2 }} />
                        <div>
                          <div style={{ fontSize: 13, color: colors.text }}>{titleCase(edu.school || edu.name || 'Unknown')}</div>
                          {(edu.degree || edu.major) && <div style={{ fontSize: 12, color: colors.textMuted }}>{[titleCase(edu.degree), titleCase(edu.major)].filter(Boolean).join(', ')}</div>}
                          {(edu.startDate || edu.endDate) && <div style={{ fontSize: 11, color: colors.textMuted }}>{[edu.startDate, edu.endDate].filter(Boolean).join(' — ')}</div>}
                        </div>
                      </div>
                    )) : candidate.education && typeof candidate.education === 'string' ? (
                      <InfoRow icon={BookOpen} label="Education" value={titleCase(candidate.education)} />
                    ) : null}
                    {Array.isArray(candidate.certifications) && candidate.certifications.length > 0 ? (
                      <InfoRow icon={Award} label="Certs" value={candidate.certifications.map(c => titleCase(typeof c === 'string' ? c : (c.name || c.title || ''))).filter(Boolean).join(', ')} />
                    ) : candidate.certifications && typeof candidate.certifications === 'string' ? (
                      <InfoRow icon={Award} label="Certs" value={titleCase(candidate.certifications)} />
                    ) : null}
                  </Card>
                </div>
              )}

              {/* Status & Dates */}
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>Record Info</h4>
                <Card style={{ padding: 10 }}>
                  {!isKnine && <InfoRow icon={Tag} label="Status" value={candidate.status} />}
                  <InfoRow icon={Database} label="Source" value={isKnine ? 'kNine' : (candidate.candidateSource || candidate.source)} />
                  {!isKnine && <InfoRow icon={Calendar} label="Created" value={formatDate(candidate.dateEntered)} />}
                  {!isKnine && <InfoRow icon={Calendar} label="Updated" value={formatDate(candidate.lastModified || candidate.lastActivity)} />}
                  {!isKnine && <InfoRow icon={FileText} label="Resume" value={candidate.hasResume ? (candidate.resumeDate ? `Yes (${formatDate(candidate.resumeDate)})` : 'Yes') : 'No'} color={candidate.hasResume ? colors.success : colors.textMuted} />}
                  {isKnine && candidate.numSources && <InfoRow icon={Database} label="Data Sources" value={`${candidate.numSources} sources`} />}
                  {isKnine && candidate.enrichedAt && <InfoRow icon={Calendar} label="Enriched" value={formatDate(candidate.enrichedAt)} />}
                </Card>
              </div>

              {/* Notes */}
              {candidate.notes && (
                <div style={{ marginBottom: 14 }}>
                  <button onClick={() => toggleExpand('notes')} style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 8 }}>
                    <h4 style={{ margin: 0, fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>Notes</h4>
                    {expanded.notes ? <ChevronUp size={14} style={{ color: colors.textMuted }} /> : <ChevronDown size={14} style={{ color: colors.textMuted }} />}
                  </button>
                  {expanded.notes && (
                    <Card style={{ padding: 10 }}>
                      <p style={{ margin: 0, fontSize: 12, color: colors.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{candidate.notes}</p>
                    </Card>
                  )}
                </div>
              )}

              {/* Current Rollups */}
              {candidate.rollups?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>On Lists</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {candidate.rollups.map((r, i) => (
                      <Badge key={i} variant="default" size="sm"><Layers size={10} /> {r.name}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Submissions */}
              {candidate.submissions?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>Submitted To</h4>
                  {candidate.submissions.map((s, i) => (
                    <Card key={i} style={{ padding: 10, marginBottom: 6 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>{s.positionTitle}</div>
                      <div style={{ fontSize: 11, color: colors.textSecondary }}>{s.company} • {s.status}</div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Similar Candidates */}
              {similarCandidates?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase' }}>
                    Similar Candidates ({similarCandidates.length})
                  </h4>
                  {similarCandidates.map((sc, i) => (
                    <button key={i} onClick={() => onViewSimilar?.(sc)} style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 10px',
                      marginBottom: 4, borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCard,
                      cursor: 'pointer', textAlign: 'left'
                    }}>
                      <Avatar name={sc.fullName} size={28} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleCase(sc.fullName)}</div>
                        <div style={{ fontSize: 11, color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {titleCase(sc.title)}{sc.company ? ` at ${titleCase(sc.company)}` : ''}
                        </div>
                      </div>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                        color: (sc.matchScore || 0) >= 85 ? colors.success : (sc.matchScore || 0) >= 70 ? '#f59e0b' : colors.textMuted,
                        background: (sc.matchScore || 0) >= 85 ? 'rgba(16,185,129,0.1)' : (sc.matchScore || 0) >= 70 ? 'rgba(245,158,11,0.1)' : colors.bgMuted
                      }}>{sc.matchScore || 0}%</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!loading && activeTab === 'attachments' && (
            <div>
              {candidate.loadingAttachments && <div style={{ textAlign: 'center', padding: 20 }}><Loader2 size={20} className="spin" style={{ color: colors.accent }} /></div>}
              {!candidate.loadingAttachments && attachments.length === 0 && (
                <div style={{ textAlign: 'center', padding: 32, color: colors.textMuted }}>
                  <Paperclip size={28} style={{ marginBottom: 10, opacity: 0.5 }} />
                  <p style={{ margin: 0, fontSize: 13 }}>No attachments</p>
                </div>
              )}
              {attachments.length > 0 && [...attachments]
                .sort((a, b) => (b.isCurrent ? 1 : 0) - (a.isCurrent ? 1 : 0))
                .map((att, i) => {
                const fileName = att.name || att.Name || att.FileName || 'Document';
                const isResume = att.isResume || fileName.toLowerCase().includes('resume') || fileName.toLowerCase().includes('cv');
                const isCurrent = att.isCurrent;
                return (
                  <Card key={i} style={{ padding: 10, marginBottom: 8, borderColor: isCurrent ? colors.primary : isResume ? colors.success : colors.border }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 6, background: isCurrent ? colors.bgAccent || colors.bgMuted : isResume ? colors.bgSuccess : colors.bgMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FileText size={16} style={{ color: isCurrent ? colors.primary : isResume ? colors.success : colors.textMuted }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{fileName}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                          {isCurrent && <span style={{ fontSize: 10, color: colors.primary, fontWeight: 600 }}>Current Resume</span>}
                          {!isCurrent && isResume && <span style={{ fontSize: 10, color: colors.success }}>Resume</span>}
                          {att.attachmentType && !isResume && <span style={{ fontSize: 10, color: colors.textMuted }}>{att.attachmentType}</span>}
                          {att.date && <span style={{ fontSize: 10, color: colors.textMuted }}>{formatDate(att.date)}</span>}
                        </div>
                      </div>
                      <button onClick={() => previewAttachment(att)} style={{ padding: 6, background: 'none', border: 'none', cursor: 'pointer' }} title="Preview">
                        <Eye size={16} style={{ color: colors.textMuted }} />
                      </button>
                      <button onClick={() => downloadAttachment(att)} style={{ padding: 6, background: 'none', border: 'none', cursor: 'pointer' }} title="Download">
                        <Download size={16} style={{ color: colors.primary }} />
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 14px', borderTop: `1px solid ${colors.border}`, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* kNine Preview footer: Dismiss only — Enrich CTA is inline above */}
          {isKnine && isPreview && onDismissKnine && (
            <button onClick={onDismissKnine} title="Not a fit — hide for 6 months" style={{
              display: 'flex', alignItems: 'center', gap: 6, margin: '0 auto',
              padding: '8px 16px', borderRadius: 8, border: `1px solid ${colors.border}`,
              background: 'transparent', color: colors.textMuted, fontSize: 12, cursor: 'pointer'
            }}>
              <ThumbsDown size={13} /> Not a fit
            </button>
          )}
          {/* kNine Enriched with ATS: Add to ATS + CSV + Dismiss */}
          {isKnine && isEnriched && onUploadToATS && !atsUploadResult?.action?.match(/created|updated|duplicate_found/) && (
            <>
              <Button variant="ai" icon={Upload} onClick={onUploadToATS} style={{ flex: 1 }}>Add to ATS</Button>
              <Button variant="success" size="sm" icon={Download} onClick={() => downloadEnrichedCSV(candidate)}>CSV</Button>
              <Button variant="ghost" icon={X} onClick={onDismissEnriched}>Dismiss</Button>
            </>
          )}
          {/* kNine Enriched without ATS: CSV + LinkedIn + Close */}
          {isKnine && isEnriched && !onUploadToATS && (
            <>
              <div style={{ width: '100%', marginBottom: 6, padding: '8px 10px', borderRadius: 8, background: colors.bgSuccess, border: `1px solid ${colors.success}30`, display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle size={14} style={{ color: colors.success, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: colors.success, fontWeight: 500 }}>Enriched data saved to your records</span>
              </div>
              <Button variant="success" icon={Download} onClick={() => downloadEnrichedCSV(candidate)} style={{ flex: 1 }}>Download CSV</Button>
              <Button variant="ghost" icon={ExternalLink} onClick={onOpenInATS}>LinkedIn</Button>
              <Button variant="ghost" icon={X} onClick={onDismissEnriched}>Close</Button>
            </>
          )}
          {/* ATS record (non-kNine): Standard actions */}
          {!isKnine && (
            <>
              <Button variant="primary" icon={Plus} onClick={onAddToList} style={{ flex: 1 }}>Add to List</Button>
              <Button variant="secondary" icon={Send} onClick={onSubmitToPosition} style={{ flex: 1 }}>Submit</Button>
              <Button variant="ghost" icon={ExternalLink} onClick={onOpenInATS}>ATS</Button>
            </>
          )}
          {/* kNine Enriched with ATS upload done: standard actions + CSV */}
          {isKnine && isEnriched && atsUploadResult?.action?.match(/created|updated/) && (
            <>
              <Button variant="primary" icon={Plus} onClick={onAddToList} style={{ flex: 1 }}>Add to List</Button>
              <Button variant="secondary" icon={Send} onClick={onSubmitToPosition} style={{ flex: 1 }}>Submit</Button>
              <Button variant="ghost" size="sm" icon={Download} onClick={() => downloadEnrichedCSV(candidate)} title="Download CSV">CSV</Button>
              <Button variant="ghost" icon={ExternalLink} onClick={onOpenInATS}>LI</Button>
            </>
          )}
        </div>
      </div>

      {/* Resume Modal — renders AI-generated HTML resume inline */}
      {resumeModalHtml && (
        <>
          <div onClick={() => setResumeModalHtml(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000
          }} />
          <div className="kubi-modal-card" style={{
            position: 'fixed', top: '3%', left: '50%', transform: 'translateX(-50%)',
            width: '95%', maxWidth: 820, height: '94vh',
            background: '#fff', borderRadius: 12, zIndex: 2001,
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', borderBottom: '1px solid #e2e8f0',
              background: '#f8fafc', borderRadius: '12px 12px 0 0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={16} style={{ color: '#4a5568' }} />
                <span style={{ fontWeight: 600, fontSize: 14, color: '#1a202c' }}>
                  {titleCase(candidate.fullName || '')} — Resume
                </span>
                <span style={{ fontSize: 11, color: '#a0aec0', fontStyle: 'italic' }}>Generated by kNine</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  onClick={() => {
                    const blob = new Blob([resumeModalHtml], { type: 'text/html' });
                    const url = window.URL.createObjectURL(blob);
                    window.open(url, '_blank');
                    setTimeout(() => window.URL.revokeObjectURL(url), 120000);
                  }}
                  style={{ padding: '4px 10px', background: 'none', border: `1px solid ${colors.border}`, borderRadius: 6, cursor: 'pointer', fontSize: 12, color: colors.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}
                  title="Open in new tab"
                >
                  <ExternalLink size={13} /> New Tab
                </button>
                <button
                  onClick={() => {
                    const blob = new Blob([resumeModalHtml], { type: 'text/html' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${titleCase(candidate.fullName || 'Resume').replace(/\s+/g, '_')}_Resume_kNine.html`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  }}
                  style={{ padding: '4px 10px', background: 'none', border: `1px solid ${colors.border}`, borderRadius: 6, cursor: 'pointer', fontSize: 12, color: colors.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}
                  title="Download"
                >
                  <Download size={13} /> Download
                </button>
                <button
                  onClick={() => setResumeModalHtml(null)}
                  style={{ padding: 4, background: 'none', border: 'none', cursor: 'pointer' }}
                  title="Close"
                >
                  <X size={18} style={{ color: '#718096' }} />
                </button>
              </div>
            </div>
            {/* Resume content iframe */}
            <iframe
              srcDoc={resumeModalHtml}
              title="Resume Preview"
              style={{
                flex: 1, border: 'none', borderRadius: '0 0 12px 12px',
                background: '#fff', width: '100%'
              }}
              sandbox="allow-same-origin"
            />
          </div>
        </>
      )}

      {/* KNine branded modal — replaces native alert/confirm/prompt */}
      <KNineModal modal={knModal} onClose={handleKnModalClose} />
    </>
  );
}

// =============================================================================
// ADD TO LIST MODAL - Supports bulk with confirmation
// =============================================================================
function AddToListModal({ candidates, rollups, loading, error, onClose, onSuccess, onRefresh }) {
  const colors = useColors();
  const [selected, setSelected] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = rollups.filter(r => r.name.toLowerCase().includes(search.toLowerCase()));
  const isBulk = candidates.length > 1;
  const selectedRollup = rollups.find(r => r.code === selected);

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true); setSubmitError(null); setSubmitSuccess(null);
    
    try {
      if (isBulk) {
        // Use bulk endpoint
        const res = await fetch(buildApiUrl('/api/pcr/bulk/add-to-rollup'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
          body: JSON.stringify({ candidateIds: candidates.map(c => c.atsRecordId || c.candidateId), rollupCode: selected })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to add to list');
        if (data.succeeded === 0) throw new Error(`Could not add candidates to the list. ${data.results?.failed?.[0]?.error || 'PCRecruiter rejected the request.'}`);
        console.log(`[Bulk Add] ${data.succeeded} of ${data.total} added`);
        setSubmitSuccess(`Successfully added ${data.succeeded} of ${data.total} candidates to "${selectedRollup?.name || selected}"`);
      } else {
        // Single candidate
        const res = await fetch(buildApiUrl(`/api/pcr/rollups/${selected}/candidates`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
          body: JSON.stringify({ candidateId: candidates[0].atsRecordId || candidates[0].candidateId })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to add to list');
        if (data.added === 0) throw new Error(`Could not add to list. ${data.results?.failed?.[0]?.error || 'PCRecruiter rejected the request.'}`);
        setSubmitSuccess(`Successfully added ${candidates[0].fullName} to "${selectedRollup?.name || selected}"`);
      }
      // Auto close after success
      setTimeout(() => onSuccess(), 1500);
    } catch (e) { 
      console.error('[Add to List] Error:', e);
      setSubmitError(e.message); 
    } 
    finally { setSubmitting(false); }
  };

  return (
    <>
      <div onMouseDown={onClose} className="fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100 }} />
      <div className="slide-in-up" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1101, width: 400, maxWidth: '90vw' }}>
        <Card style={{ padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.text }}>Add to List</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} style={{ color: colors.textMuted }} /></button>
          </div>
          
          <p style={{ margin: '0 0 14px', color: colors.textSecondary, fontSize: 13 }}>
            {isBulk 
              ? `Add ${candidates.length} candidates to a list`
              : <>Add <strong style={{ color: colors.text }}>{candidates[0]?.fullName}</strong> to a list</>
            }
          </p>
          
          {/* Success Message */}
          {submitSuccess && (
            <div style={{ marginBottom: 12, padding: 12, background: colors.bgSuccess, borderRadius: 8, border: `1px solid ${colors.success}`, display: 'flex', alignItems: 'center', gap: 10 }}>
              <CheckCircle size={18} style={{ color: colors.success, flexShrink: 0 }} />
              <span style={{ color: colors.success, fontSize: 13, fontWeight: 500 }}>{submitSuccess}</span>
            </div>
          )}
          
          {/* Error Message */}
          {(submitError || error) && !submitSuccess && (
            <div style={{ marginBottom: 12, padding: 10, background: colors.bgDanger, borderRadius: 6, color: colors.danger, fontSize: 12 }}>{submitError || error}</div>
          )}
          
          {!submitSuccess && (
            <>
              {loading ? (
                <div style={{ padding: 20, textAlign: 'center' }}><Loader2 size={22} className="spin" style={{ color: colors.accent }} /></div>
              ) : rollups.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', background: colors.bgMuted, borderRadius: 8 }}>
                  <p style={{ margin: '0 0 10px', color: colors.textMuted, fontSize: 13 }}>No lists found</p>
                  <Button variant="ghost" size="sm" icon={RefreshCw} onClick={onRefresh}>Refresh</Button>
                </div>
              ) : (
                <>
                  <div style={{ position: 'relative', marginBottom: 10 }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: colors.textMuted }} />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search lists..." style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.bgInput, color: colors.text, fontSize: 12, outline: 'none' }} />
                  </div>
                  <div style={{ maxHeight: 180, overflow: 'auto', marginBottom: 14 }}>
                    {filtered.length === 0 ? (
                      <div style={{ padding: 14, textAlign: 'center', color: colors.textMuted, fontSize: 12 }}>No matches</div>
                    ) : (
                      filtered.map((r, idx) => (
                        <div 
                          key={r.code || idx} 
                          onClick={() => setSelected(r.code)} 
                          style={{ 
                            padding: '10px', borderRadius: 6, cursor: 'pointer', marginBottom: 4,
                            background: selected === r.code ? colors.bgAccent : 'transparent', 
                            border: `1px solid ${selected === r.code ? colors.accent : 'transparent'}`
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{r.name}</div>
                          <div style={{ fontSize: 11, color: colors.textMuted }}>{r.memberCount} members</div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
              
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
                <Button variant="primary" size="sm" icon={Plus} loading={submitting} disabled={!selected || loading} onClick={handleSubmit}>
                  {isBulk ? `Add ${candidates.length}` : 'Add'}
                </Button>
              </div>
            </>
          )}
          
          {submitSuccess && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="primary" size="sm" icon={CheckCircle} onClick={onClose}>Done</Button>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

// =============================================================================
// SUBMIT TO POSITION MODAL - Supports bulk with confirmation
// =============================================================================
function SubmitToPositionModal({ candidates, positions, loading, error, onClose, onSuccess, onRefresh }) {
  const colors = useColors();
  const [selected, setSelected] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = positions.filter(p => 
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    p.companyName.toLowerCase().includes(search.toLowerCase())
  );
  const isBulk = candidates.length > 1;
  const selectedPosition = positions.find(p => p.id === selected);

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true); setSubmitError(null); setSubmitSuccess(null);
    
    try {
      if (isBulk) {
        const res = await fetch(buildApiUrl('/api/pcr/bulk/submit-to-position'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
          body: JSON.stringify({ candidateIds: candidates.map(c => c.atsRecordId || c.candidateId), positionId: selected, notes })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to submit');
        if (data.succeeded === 0) throw new Error(`Could not submit candidates. ${data.results?.failed?.[0]?.error || 'PCRecruiter rejected the request.'}`);
        console.log(`[Bulk Submit] ${data.succeeded} of ${data.total} submitted`);
        setSubmitSuccess(`Successfully submitted ${data.succeeded} of ${data.total} candidates to "${selectedPosition?.title || 'position'}"`);
      } else {
        const cand = candidates[0];
        const res = await fetch(buildApiUrl(`/api/pcr/positions/${selected}/submit`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
          body: JSON.stringify({
            candidateId: cand.atsRecordId || cand.candidateId,
            candidateNames: { [cand.atsRecordId || cand.candidateId]: cand.fullName || `${cand.firstName || ''} ${cand.lastName || ''}`.trim() },
            notes
          })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to submit');
        if (data.submitted === 0) throw new Error(`Could not submit to position. ${data.results?.failed?.[0]?.error || 'PCRecruiter rejected the request.'}`);
        setSubmitSuccess(`Successfully submitted ${candidates[0].fullName} to "${selectedPosition?.title || 'position'}"`);
      }
      // Auto close after success
      setTimeout(() => onSuccess(), 1500);
    } catch (e) { 
      console.error('[Submit to Position] Error:', e);
      setSubmitError(e.message); 
    } 
    finally { setSubmitting(false); }
  };

  return (
    <>
      <div onMouseDown={onClose} className="fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100 }} />
      <div className="slide-in-up" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1101, width: 420, maxWidth: '90vw' }}>
        <Card style={{ padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.text }}>Submit to Position</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} style={{ color: colors.textMuted }} /></button>
          </div>
          
          <p style={{ margin: '0 0 14px', color: colors.textSecondary, fontSize: 13 }}>
            {isBulk 
              ? `Submit ${candidates.length} candidates to a position`
              : <>Submit <strong style={{ color: colors.text }}>{candidates[0]?.fullName}</strong></>
            }
          </p>
          
          {/* Success Message */}
          {submitSuccess && (
            <div style={{ marginBottom: 12, padding: 12, background: colors.bgSuccess, borderRadius: 8, border: `1px solid ${colors.success}`, display: 'flex', alignItems: 'center', gap: 10 }}>
              <CheckCircle size={18} style={{ color: colors.success, flexShrink: 0 }} />
              <span style={{ color: colors.success, fontSize: 13, fontWeight: 500 }}>{submitSuccess}</span>
            </div>
          )}
          
          {/* Error Message */}
          {(submitError || error) && !submitSuccess && (
            <div style={{ marginBottom: 12, padding: 10, background: colors.bgDanger, borderRadius: 6, color: colors.danger, fontSize: 12 }}>{submitError || error}</div>
          )}
          
          {!submitSuccess && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 600, color: colors.textMuted, marginBottom: 5, textTransform: 'uppercase' }}>Position</label>
                {loading ? (
                  <div style={{ padding: 20, textAlign: 'center' }}><Loader2 size={22} className="spin" style={{ color: colors.accent }} /></div>
                ) : positions.length === 0 ? (
                  <div style={{ padding: 16, textAlign: 'center', background: colors.bgMuted, borderRadius: 8 }}>
                    <p style={{ margin: '0 0 10px', color: colors.textMuted, fontSize: 13 }}>No open positions found</p>
                    <p style={{ margin: '0 0 10px', color: colors.textMuted, fontSize: 11 }}>Check your PCRecruiter for active positions</p>
                    <Button variant="ghost" size="sm" icon={RefreshCw} onClick={onRefresh}>Refresh</Button>
                  </div>
                ) : (
                  <>
                    <div style={{ position: 'relative', marginBottom: 8 }}>
                      <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: colors.textMuted }} />
                      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search positions..." style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.bgInput, color: colors.text, fontSize: 12, outline: 'none' }} />
                    </div>
                    <div style={{ maxHeight: 160, overflow: 'auto' }}>
                      {filtered.length === 0 ? (
                        <div style={{ padding: 14, textAlign: 'center', color: colors.textMuted, fontSize: 12 }}>No matches</div>
                      ) : (
                        filtered.map((p, idx) => (
                          <div 
                            key={p.id || idx} 
                            onClick={() => setSelected(p.id)} 
                            style={{ 
                              padding: '10px', borderRadius: 6, cursor: 'pointer', marginBottom: 4,
                              background: selected === p.id ? colors.bgAccent : 'transparent', 
                              border: `1px solid ${selected === p.id ? colors.accent : 'transparent'}`
                            }}
                          >
                            <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{p.title}</div>
                            <div style={{ fontSize: 11, color: colors.textSecondary }}>{p.companyName}{p.location && ` • ${p.location}`}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
              
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 600, color: colors.textMuted, marginBottom: 5, textTransform: 'uppercase' }}>Notes (Optional)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any notes about this submission..." rows={2} style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.bgInput, color: colors.text, fontSize: 12, resize: 'vertical', outline: 'none' }} />
              </div>
              
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
                <Button variant="primary" size="sm" icon={Send} loading={submitting} disabled={!selected || loading} onClick={handleSubmit}>
                  {isBulk ? `Submit ${candidates.length}` : 'Submit'}
                </Button>
              </div>
            </>
          )}
          
          {submitSuccess && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="primary" size="sm" icon={CheckCircle} onClick={onClose}>Done</Button>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
